/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
// Header provided by AppLayout
import DataTable, { ActionMenu } from './DataTable';
import { Icons } from './ActionIcons';
import AppModal from './AppModal';
import AlertConfirmModal from './AlertConfirmModal';
import AppToastContainer, { AppToastItem } from './AppToastContainer';
import ModalTabs from './ModalTabs';
import StepWizard from './StepWizard';

type JsonRow = Record<string, unknown>;

type AnalyzerClientRow = {
  _id: string;
  clientId: string;
  clientName?: string;
  source: 'profilegen' | 'manual';
  deletionStatus?: 'idle' | 'queued' | 'processing' | 'error';
  deletionProcessedItems?: number;
  deletionTotalItems?: number;
  deletionStage?: string;
  deletionErrorMessage?: string;
  createdByName?: string;
  createdAt?: string;
};

type UploadRow = {
  _id: string;
  analyzerClientId?: string;
  clientId?: string;
  fileName: string;
  uploadedByName?: string;
  logType?: 'STUSERTRACE (json)' | 'STUSERTRACE (csv)' | 'STUSOBTRACE' | 'STAUTHTRACE' | 'ST01' | 'STUSERTRACE';
  status: 'uploaded' | 'processing' | 'processed' | 'error' | 'cancelled';
  totalRecords: number;
  estimatedRecords?: number;
  processedRecords: number;
  cleanupStatus?: 'idle' | 'queued' | 'processing' | 'completed' | 'error' | 'cancelled';
  cleanupProcessedRecords?: number;
  cleanupTotalRecords?: number;
  cleanupStage?: string;
  cleanupActionIndex?: number;
  cleanupActionCount?: number;
  cleanupActionLabel?: string;
  cleanupActionProcessed?: number;
  cleanupActionTotal?: number;
  cleanupErrorMessage?: string;
  deletionStatus?: 'idle' | 'queued' | 'processing' | 'completed' | 'error';
  deletionProcessedItems?: number;
  deletionTotalItems?: number;
  deletionStage?: string;
  deletionErrorMessage?: string;
  createdAt?: string;
};

type ProfileGenClientOption = {
  _id: string;
  clientId: string;
  clientName?: string;
};

type UploadActivityItem = {
  action: string;
  message: string;
  level: 'info' | 'success' | 'warning' | 'error';
  actor?: {
    userId?: string;
    username?: string;
    fullName?: string;
  };
  metadata?: Record<string, unknown>;
  createdAt?: string;
};

type ConsolidationActivityItem = UploadActivityItem;

type LogConsolidationPlanTier = 'heavy' | 'medium' | 'light' | 'simple';
const LOG_CONSOLIDATION_V2_TIER_ORDER: LogConsolidationPlanTier[] = ['heavy', 'medium', 'light', 'simple'];
const LOG_CONSOLIDATION_V2_BATCH_BY_TIER: Record<LogConsolidationPlanTier, number> = {
  heavy: 1,
  medium: 2,
  light: 5,
  simple: 10,
};

type LogConsolidationTierThresholds = {
  heavyMinPercent: number;
  mediumMinPercent: number;
  lightMinPercent: number;
};

type LogConsolidationPlanTierSummary = {
  tier: LogConsolidationPlanTier;
  usersPerJob: number;
  totalUsers: number;
  totalLogCount: number;
  totalJobs: number;
};

type LogConsolidationPlanGroup = {
  jobIndex: number;
  tier: LogConsolidationPlanTier;
  userCount: number;
  totalLogCount: number;
  usernames: string[];
  users: Array<{ username: string; logCount: number }>;
};

type LogConsolidationPlanUser = {
  username: string;
  workloadTier: LogConsolidationPlanTier;
  logCount: number;
  logSharePercent: number;
};

type LogConsolidationPlanObjectHierarchy = {
  object: string;
  logCount: number;
};

type LogConsolidationPlanCodeHierarchy = {
  code: string;
  logCount: number;
  objects: LogConsolidationPlanObjectHierarchy[];
};

type LogConsolidationPlanUserHierarchy = {
  username: string;
  totalLogCount: number;
  totalCodes: number;
  totalObjects: number;
  codes: LogConsolidationPlanCodeHierarchy[];
};

type LogConsolidationPlanUserHierarchyState = {
  loading: boolean;
  error?: string;
  data?: LogConsolidationPlanUserHierarchy;
};

type LogConsolidationPlanHierarchyRow = {
  key: string;
  rowType: 'user' | 'code' | 'object' | 'status';
  username: string;
  label: string;
  workloadTier?: LogConsolidationPlanTier;
  logCount?: number;
  logSharePercent?: number;
  level: number;
  expandable: boolean;
  code?: string;
  object?: string;
};

type LogConsolidationObjectFieldDistinctRow = {
  field: string;
  distinctValuesCount: number;
  isGlobalAuthorization?: boolean;
};

type LogConsolidationRule = {
  ruleType: 'global_authorization' | 'custom_pre_group_wildcard' | 'global_authorization_threshold_post_group';
  username: string;
  code: string;
  object: string;
  field: string;
  marker: '*';
  thresholdDistinctValues?: number;
  fieldScope?: 'all' | 'common' | 'org_level';
};

type LogConsolidationManualRuleDraft = {
  ruleType: 'custom_pre_group_wildcard' | 'global_authorization_threshold_post_group';
  username: string;
  code: string;
  object: string;
  field: string;
  thresholdDistinctValuesCommon: number;
  thresholdDistinctValuesOrgLevel: number;
};

type LogConsolidationObjectFieldDistinctContext = {
  username: string;
  code: string;
  object: string;
};

type LogConsolidationTopDistinctFieldRow = {
  username: string;
  code: string;
  object: string;
  field: string;
  distinctValuesCount: number;
  isGlobalAuthorization?: boolean;
};

type LoadFileGenerationProfileSummaryRow = {
  profileName: string;
  totalRows: number;
  totalObjects: number;
  totalFields: number;
};

type LoadFileGenerationPreviewRow = {
  profileName: string;
  user: string;
  code: string;
  object: string;
  field: string;
  valueFrom: string;
  valueTo: string;
};

type LoadFileConsolidatedViewRow = {
  profileName: string;
  object: string;
  field: string;
  valueFrom: string;
  valueTo: string;
  numProfile?: string;
  originUsers?: string[];
  originCodes?: string[];
  originPairs?: Array<{ user: string; code: string }>;
  originUsersTotal?: number;
  originCodesTotal?: number;
  originPairsTotal?: number;
  originUsersTruncated?: boolean;
  originCodesTruncated?: boolean;
  originPairsTruncated?: boolean;
};

type LoadFileGenerationOriginRow = {
  user: string;
  code: string;
  object: string;
  field: string;
  valueFrom: string;
  valueTo: string;
  numProfile: string;
};

type LoadFileOrgLevelViewRow = {
  profileName: string;
  field: string;
  valueFrom: string;
  valueTo: string;
  variable?: string;
};

type LoadFileGenerationExecutionRow = {
  _id: string;
  consolidationId: string;
  sourceOption: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'unknown';
  progress: number;
  progressMessage?: string;
  errorMessage?: string;
  totalGeneratedRows?: number;
  profilesCount?: number;
  previewLimit?: number;
  previewRowsCount?: number;
  previewTruncated?: boolean;
  createdByName?: string;
  createdAt?: string;
  startedAt?: string;
  finishedAt?: string;
  updatedAt?: string;
};

type LogConsolidationPlanPreview = {
  optimizationProfile: 'v2';
  totalUsers: number;
  totalLogCount?: number;
  totalJobs?: number;
  totalJobsIfHeavySplitByCode?: number;
  totalJobsIfHeavyUserOnly?: number;
  heavyUsersCount?: number;
  heavyUserCodePairsCount?: number;
  recommendedUsersPerJob?: number;
  minUsersPerJob?: number;
  maxUsersPerJob?: number;
  defaultUsersPerJob?: number;
  summaryByTier?: LogConsolidationPlanTierSummary[];
  groups?: LogConsolidationPlanGroup[];
  users?: LogConsolidationPlanUser[];
  topDistinctFields?: LogConsolidationTopDistinctFieldRow[];
  consolidationRules?: LogConsolidationRule[];
  tierThresholds?: LogConsolidationTierThresholds & {
    heavyMinCount?: number;
    mediumMinCount?: number;
    lightMinCount?: number;
  };
};

type LogConsolidationPlanUserBase = {
  username: string;
  logCount: number;
  logSharePercent: number;
};

const DEFAULT_LOG_CONSOLIDATION_V2_THRESHOLDS: LogConsolidationTierThresholds = {
  heavyMinPercent: 2,
  mediumMinPercent: 0.33,
  lightMinPercent: 0.07,
};

const _buildLogConsolidationV2PreviewLocally = (
  usersBase: LogConsolidationPlanUserBase[],
  totalLogCountRaw: number,
  thresholds: LogConsolidationTierThresholds
): LogConsolidationPlanPreview => {
  const totalLogCount = Math.max(0, Number(totalLogCountRaw || 0));
  const toCount = (pct: number) => {
    if (totalLogCount <= 0 || pct <= 0) return 0;
    return Math.max(1, Math.ceil((totalLogCount * pct) / 100));
  };

  const tierThresholds = {
    heavyMinPercent: thresholds.heavyMinPercent,
    mediumMinPercent: thresholds.mediumMinPercent,
    lightMinPercent: thresholds.lightMinPercent,
    heavyMinCount: toCount(thresholds.heavyMinPercent),
    mediumMinCount: toCount(thresholds.mediumMinPercent),
    lightMinCount: toCount(thresholds.lightMinPercent),
  };

  const classify = (logCount: number): LogConsolidationPlanTier => {
    if (logCount >= tierThresholds.heavyMinCount) return 'heavy';
    if (logCount >= tierThresholds.mediumMinCount) return 'medium';
    if (logCount >= tierThresholds.lightMinCount) return 'light';
    return 'simple';
  };

  const users = usersBase
    .map((item) => ({
      username: item.username,
      logCount: item.logCount,
      logSharePercent: item.logSharePercent,
      workloadTier: classify(item.logCount),
    }))
    .sort((a, b) => b.logCount - a.logCount || a.username.localeCompare(b.username));

  const usersByTier = new Map<LogConsolidationPlanTier, LogConsolidationPlanUser[]>();
  for (const tier of LOG_CONSOLIDATION_V2_TIER_ORDER) usersByTier.set(tier, []);
  for (const user of users) usersByTier.get(user.workloadTier)?.push(user);

  const groups: LogConsolidationPlanGroup[] = [];
  const summaryByTier: LogConsolidationPlanTierSummary[] = LOG_CONSOLIDATION_V2_TIER_ORDER.map((tier) => {
    const tierUsers = (usersByTier.get(tier) || []).slice();
    const usersPerJob = LOG_CONSOLIDATION_V2_BATCH_BY_TIER[tier];
    let tierJobs = 0;

    for (let i = 0; i < tierUsers.length; i += usersPerJob) {
      const chunk = tierUsers.slice(i, i + usersPerJob);
      if (chunk.length === 0) continue;
      tierJobs += 1;
      groups.push({
        jobIndex: groups.length + 1,
        tier,
        userCount: chunk.length,
        totalLogCount: chunk.reduce((sum, item) => sum + item.logCount, 0),
        usernames: chunk.map((item) => item.username),
        users: chunk.map((item) => ({ username: item.username, logCount: item.logCount })),
      });
    }

    return {
      tier,
      usersPerJob,
      totalUsers: tierUsers.length,
      totalLogCount: tierUsers.reduce((sum, item) => sum + item.logCount, 0),
      totalJobs: tierJobs,
    };
  });

  return {
    optimizationProfile: 'v2',
    totalUsers: users.length,
    totalLogCount,
    totalJobs: groups.length,
    tierThresholds,
    summaryByTier,
    groups,
    users,
  };
};

type ProfileConsolidationRow = {
  _id: string;
  analyzerClientId?: string;
  clientId?: string;
  uploadIds?: string[];
  uploadFileNames?: string[];
  uploadCount?: number;
  status?: 'initial' | 'generated' | 'structure_adjusted';
  structureAdjustStatus?: 'idle' | 'queued' | 'processing' | 'completed' | 'error' | 'cancelled';
  structureAdjustStage?: string;
  structureAdjustActionIndex?: number;
  structureAdjustActionCount?: number;
  structureAdjustActionLabel?: string;
  structureAdjustActionProcessed?: number;
  structureAdjustActionTotal?: number;
  structureAdjustProcessedRecords?: number;
  structureAdjustTotalRecords?: number;
  userConsolidationMode?: 'user' | 'user_code';
  userConsolidationProcessedUsers?: number;
  userConsolidationTotalUsers?: number;
  userConsolidationFailedUsers?: number;
  structureAdjustErrorMessage?: string;
  structureAdjustStartedAt?: string;
  deletionStatus?: 'idle' | 'queued' | 'processing' | 'completed' | 'error';
  deletionProcessedItems?: number;
  deletionTotalItems?: number;
  deletionStage?: string;
  deletionErrorMessage?: string;
  structuredRowsTotal?: number;
  structureAdjustedAt?: string;
  createdByName?: string;
  createdAt?: string;
};

type ProfileAdjustedRecordRow = {
  _id: string;
  sourceGroupId?: string;
  sourceRowRef?: string;
  sourceRowRefs?: string[];
  user?: string;
  code?: string;
  object?: string;
  field?: string;
  valueFrom?: string;
  valueTo?: string;
  sourceRowNumber?: number;
  createdAt?: string;
};

type ProfileAdjustedGroupOriginRow = {
  _id: string;
  user?: string;
  code?: string;
  object?: string;
  groupedFieldValues?: Array<{ field?: string; values?: string[] }>;
  sourceRows?: Array<{ uploadId?: string; sourceRowNumber?: number; sourceRowRef?: string; fieldValues?: Array<{ field?: string; values?: string[] }> }>;
};

type DialogState = {
  show: boolean;
  mode: 'alert' | 'confirm';
  title: string;
  message: string;
  showPersistGroupOriginsToggle?: boolean;
  persistGroupOriginsToggleId?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger' | 'warning' | 'secondary';
};

type CleanupConditionDraft = {
  id: string;
  column: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'empty' | 'not_empty';
  value: string;
};

type CleanupRuleDraft = {
  id: string;
  conditions: CleanupConditionDraft[];
};

const resolveApiBase = () => {
  const baseEnv = process.env.REACT_APP_API_URL || '';
  const hostname = window.location.hostname;
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocalHost) return '';
  if (!baseEnv) return '';
  try {
    const parsed = new URL(baseEnv);
    return parsed.origin;
  } catch {
    return baseEnv;
  }
};

const extractFileNameFromContentDisposition = (headerValue: unknown, fallbackName: string) => {
  const rawHeader = String(headerValue || '').trim();
  if (!rawHeader) return fallbackName;

  const utf8Match = rawHeader.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(String(utf8Match[1] || '').trim().replace(/^"|"$/g, '')) || fallbackName;
    } catch {
      return String(utf8Match[1] || '').trim().replace(/^"|"$/g, '') || fallbackName;
    }
  }

  const defaultMatch = rawHeader.match(/filename=([^;]+)/i);
  if (!defaultMatch?.[1]) return fallbackName;

  const cleanName = String(defaultMatch[1] || '').trim().replace(/^"|"$/g, '');
  return cleanName || fallbackName;
};

const triggerBlobDownload = (blobData: BlobPart, fileName: string, mimeType?: string) => {
  const blob = new Blob([blobData], {
    type: String(mimeType || 'application/octet-stream').trim() || 'application/octet-stream',
  });

  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1500);
};

const orderedFieldKeys = ['FIELD1', 'FIELD2', 'FIELD3', 'FIELD4', 'FIELD5', 'FIELD6', 'FIELD7', 'FIELD8', 'FIELD9', 'FIELD0'] as const;

const LOG_TYPE_OPTIONS = [
  { value: 'STUSERTRACE (json)', label: 'STUSERTRACE (json)', supported: true },
  { value: 'STUSERTRACE (csv)', label: 'STUSERTRACE (csv)', supported: false },
  { value: 'STUSOBTRACE', label: 'STUSOBTRACE', supported: false },
  { value: 'STAUTHTRACE', label: 'STAUTHTRACE', supported: false },
  { value: 'ST01', label: 'ST01', supported: false }
] as const;

type UploadModelType = (typeof LOG_TYPE_OPTIONS)[number]['value'];
const CLEANUP_OPERATOR_OPTIONS: Array<{ value: CleanupConditionDraft['operator']; label: string; requiresValue: boolean }> = [
  { value: 'equals', label: 'É igual a', requiresValue: true },
  { value: 'not_equals', label: 'É diferente de', requiresValue: true },
  { value: 'contains', label: 'Contém', requiresValue: true },
  { value: 'not_contains', label: 'Não contém', requiresValue: true },
  { value: 'empty', label: 'É vazio', requiresValue: false },
  { value: 'not_empty', label: 'Não é vazio', requiresValue: false }
];

const RECOMMENDED_CLEANUP_COLUMNS = [
  'ABAPLINE',
  'ABAPPROG',
  'ADDINFO',
  'CDS',
  'FIRSTCALL',
  'FORUSER',
  'HASH',
  'MANDT',
  'REASON',
  'REASON2',
  'REASON3',
  'REASON4',
  'REASON5'
] as const;

const RECOMMENDED_CLEANUP_RULES: Array<{
  conditions: Array<{
    column: string;
    operator: CleanupConditionDraft['operator'];
    value: string;
  }>;
}> = [
  {
    conditions: [
      {
        column: 'OBJECT',
        operator: 'equals',
        value: 'S_TCODE'
      }
    ]
  },
  {
    conditions: [
      {
        column: 'RC',
        operator: 'not_equals',
        value: '0'
      }
    ]
  },
  {
    conditions: [
      {
        column: 'NAME',
        operator: 'empty',
        value: ''
      }
    ]
  }
];

const createCleanupCondition = (): CleanupConditionDraft => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  column: '',
  operator: 'equals',
  value: ''
});

const createCleanupRule = (): CleanupRuleDraft => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  conditions: [createCleanupCondition()]
});

const ANALYZER_STEP_LABELS = [
  'Carga e higienização de logs',
  'Análise e consolidação de autorizações',
  'Geração de arquivos de carga'
] as const;

const AnalisadorLogsSAP: React.FC = () => {
  const { analyzerClientId } = useParams<{ analyzerClientId?: string }>();
  const navigate = useNavigate();
  const isClientDetail = Boolean(analyzerClientId);

  
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [toasts, setToasts] = useState<AppToastItem[]>([]);

  const [analyzerClients, setAnalyzerClients] = useState<AnalyzerClientRow[]>([]);
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsPageSize, setClientsPageSize] = useState(20);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [clientsLoading, setClientsLoading] = useState(false);

  const [profileGenClients, setProfileGenClients] = useState<ProfileGenClientOption[]>([]);
  const [newClientMode, setNewClientMode] = useState<'profilegen' | 'manual'>('profilegen');
  const [selectedProfileGenClientId, setSelectedProfileGenClientId] = useState('');
  const [manualClientId, setManualClientId] = useState('');
  const [manualClientName, setManualClientName] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);

  const [currentClient, setCurrentClient] = useState<AnalyzerClientRow | null>(null);
  const [loadingCurrentClient, setLoadingCurrentClient] = useState(false);
  const [analyzerStep, setAnalyzerStep] = useState<1 | 2 | 3>(1);

  const [fileName, setFileName] = useState('');
  const [mappingFileName, setMappingFileName] = useState('');
  const [selectedLogType, setSelectedLogType] = useState<UploadModelType>('STUSERTRACE (json)');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [logFile, setLogFile] = useState<File | null>(null);
  const [mappingFile, setMappingFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'uploaded' | 'processing' | 'processed' | 'error' | 'cancelled' | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadTransferProgress, setUploadTransferProgress] = useState<{ loaded: number; total: number | null; percent: number | null } | null>(null);
  const [, setProcessingProcessedRecords] = useState(0);
  const [, setProcessingTotalRecords] = useState(0);

  const [rows, setRows] = useState<JsonRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsPageSize, setRecordsPageSize] = useState(100);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsSortColumn, setRecordsSortColumn] = useState<string | null>(null);
  const [recordsSortDirection, setRecordsSortDirection] = useState<'asc' | 'desc'>('asc');
  const [recordsColumnFilters, setRecordsColumnFilters] = useState<Record<string, string>>({});

  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const [uploadsPage, setUploadsPage] = useState(1);
  const [uploadsPageSize, setUploadsPageSize] = useState(10);
  const [uploadsTotal, setUploadsTotal] = useState(0);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [uploadsColumnFilters, setUploadsColumnFilters] = useState<Record<string, string>>({});
  const [profileConsolidations, setProfileConsolidations] = useState<ProfileConsolidationRow[]>([]);
  const [profileConsolidationsPage, setProfileConsolidationsPage] = useState(1);
  const [profileConsolidationsPageSize, setProfileConsolidationsPageSize] = useState(10);
  const [profileConsolidationsTotal, setProfileConsolidationsTotal] = useState(0);
  const [profileConsolidationsLoading, setProfileConsolidationsLoading] = useState(false);
  const [showConsolidationModal, setShowConsolidationModal] = useState(false);
  const [showUsorgModal, setShowUsorgModal] = useState(false);
  const [usorgFile, setUsorgFile] = useState<File | null>(null);
  const [usorgUploading, setUsorgUploading] = useState(false);
  const [usorgRowsTotal, setUsorgRowsTotal] = useState(0);
  const [usorgSummaryLoading, setUsorgSummaryLoading] = useState(false);
  const [consolidationOptionsLoading, setConsolidationOptionsLoading] = useState(false);
  const [consolidationOptions, setConsolidationOptions] = useState<UploadRow[]>([]);
  const [selectedConsolidationUploadIds, setSelectedConsolidationUploadIds] = useState<string[]>([]);
  const [creatingConsolidation, setCreatingConsolidation] = useState(false);
  const [showAdjustedStructureModal, setShowAdjustedStructureModal] = useState(false);
  const [adjustedStructureTarget, setAdjustedStructureTarget] = useState<ProfileConsolidationRow | null>(null);
  const [adjustedStructureRows, setAdjustedStructureRows] = useState<ProfileAdjustedRecordRow[]>([]);
  const [adjustedStructureLoading, setAdjustedStructureLoading] = useState(false);
  const [adjustedStructurePage, setAdjustedStructurePage] = useState(1);
  const [adjustedStructurePageSize, setAdjustedStructurePageSize] = useState(100);
  const [adjustedStructureTotal, setAdjustedStructureTotal] = useState(0);
  const [adjustedStructureColumnFilters, setAdjustedStructureColumnFilters] = useState<Record<string, string>>({});
  const [showAdjustedGroupOriginModal, setShowAdjustedGroupOriginModal] = useState(false);
  const [adjustedGroupOriginLoading, setAdjustedGroupOriginLoading] = useState(false);
  const [adjustedGroupOriginItem, setAdjustedGroupOriginItem] = useState<ProfileAdjustedGroupOriginRow | null>(null);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupUpload, setCleanupUpload] = useState<UploadRow | null>(null);
  const [availableCleanupColumns, setAvailableCleanupColumns] = useState<string[]>([]);
  const [selectedCleanupColumns, setSelectedCleanupColumns] = useState<string[]>([]);
  const [availableSelection, setAvailableSelection] = useState<string[]>([]);
  const [removeSelection, setRemoveSelection] = useState<string[]>([]);
  const [cleanupColumnsLoading, setCleanupColumnsLoading] = useState(false);
  const [cleanupRemoveEmptyFieldValuePairs, setCleanupRemoveEmptyFieldValuePairs] = useState(false);
  const [cleanupRemoveDuplicates, setCleanupRemoveDuplicates] = useState(false);
  const [cleanupRules, setCleanupRules] = useState<CleanupRuleDraft[]>([]);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityUpload, setActivityUpload] = useState<UploadRow | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityItems, setActivityItems] = useState<UploadActivityItem[]>([]);
  const [showConsolidationActivityModal, setShowConsolidationActivityModal] = useState(false);
  const [consolidationActivityTarget, setConsolidationActivityTarget] = useState<ProfileConsolidationRow | null>(null);
  const [consolidationActivityLoading, setConsolidationActivityLoading] = useState(false);
  const [consolidationActivityItems, setConsolidationActivityItems] = useState<ConsolidationActivityItem[]>([]);
  const [dialogState, setDialogState] = useState<DialogState>({
    show: false,
    mode: 'confirm',
    title: '',
    message: ''
  });
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogAction, setDialogAction] = useState<null | (() => Promise<void> | void)>(null);
  const [persistGroupOriginsChoice, setPersistGroupOriginsChoice] = useState(true);
  const [showLogConsolidationPlanModal, setShowLogConsolidationPlanModal] = useState(false);
  const [logConsolidationPlanLoading, setLogConsolidationPlanLoading] = useState(false);
  const [logConsolidationPlanLoadProgress, setLogConsolidationPlanLoadProgress] = useState(0);
  const [logConsolidationPlanLoadMessage, setLogConsolidationPlanLoadMessage] = useState('');
  const [logConsolidationPlanSubmitting, setLogConsolidationPlanSubmitting] = useState(false);
  const [logConsolidationPlanTarget, setLogConsolidationPlanTarget] = useState<ProfileConsolidationRow | null>(null);
  const [logConsolidationPlanActionLabel, setLogConsolidationPlanActionLabel] = useState('Consolidação de logs');
  const [logConsolidationPlanActiveTab, setLogConsolidationPlanActiveTab] = useState<'general' | 'rules' | 'analysis'>('general');
  const [logConsolidationPlanPreview, setLogConsolidationPlanPreview] = useState<LogConsolidationPlanPreview | null>(null);
  const [logConsolidationPlanUsersBase, setLogConsolidationPlanUsersBase] = useState<LogConsolidationPlanUserBase[]>([]);
  const [logConsolidationSplitHeavyByCode, setLogConsolidationSplitHeavyByCode] = useState(false);
  const [logConsolidationTierThresholds, setLogConsolidationTierThresholds] = useState<LogConsolidationTierThresholds>(DEFAULT_LOG_CONSOLIDATION_V2_THRESHOLDS);
  const [logConsolidationUsersFilters, setLogConsolidationUsersFilters] = useState<Record<string, string>>({});
  const [logConsolidationSummaryPage, setLogConsolidationSummaryPage] = useState(1);
  const [logConsolidationSummaryPageSize, setLogConsolidationSummaryPageSize] = useState(5);
  const [logConsolidationRulesPage, setLogConsolidationRulesPage] = useState(1);
  const [logConsolidationRulesPageSize, setLogConsolidationRulesPageSize] = useState(5);
  const [logConsolidationTopDistinctFieldsPage, setLogConsolidationTopDistinctFieldsPage] = useState(1);
  const [logConsolidationTopDistinctFieldsPageSize, setLogConsolidationTopDistinctFieldsPageSize] = useState(5);
  const [logConsolidationTopDistinctFieldsLimit, setLogConsolidationTopDistinctFieldsLimit] = useState(10);
  const [logConsolidationTopDistinctFieldsLoading, setLogConsolidationTopDistinctFieldsLoading] = useState(false);
  const [logConsolidationTopDistinctFieldsLoaded, setLogConsolidationTopDistinctFieldsLoaded] = useState(false);
  const [logConsolidationTopDistinctFieldsLoadProgress, setLogConsolidationTopDistinctFieldsLoadProgress] = useState(0);
  const [logConsolidationTopDistinctFieldsLoadMessage, setLogConsolidationTopDistinctFieldsLoadMessage] = useState('');
  const [logConsolidationTopDistinctFieldsJobId, setLogConsolidationTopDistinctFieldsJobId] = useState('');
  const [logConsolidationManualRuleDraft, setLogConsolidationManualRuleDraft] = useState<LogConsolidationManualRuleDraft | null>(null);
  const [logConsolidationManualRuleSubmitting, setLogConsolidationManualRuleSubmitting] = useState(false);
  const [showLoadFileGenerationModal, setShowLoadFileGenerationModal] = useState(false);
  const [selectedLoadGenerationConsolidationId, setSelectedLoadGenerationConsolidationId] = useState('');
  const [loadGenerationSourceOption, setLoadGenerationSourceOption] = useState<'profilegen' | 'upload'>('profilegen');
  const [loadGenerationRunning, setLoadGenerationRunning] = useState(false);
  const [loadGenerationProgress, setLoadGenerationProgress] = useState(0);
  const [loadGenerationProgressMessage, setLoadGenerationProgressMessage] = useState('');
  const [loadGenerationJobId, setLoadGenerationJobId] = useState('');
  const [loadGenerationProfiles, setLoadGenerationProfiles] = useState<LoadFileGenerationProfileSummaryRow[]>([]);
  const [loadGenerationPreviewRows, setLoadGenerationPreviewRows] = useState<LoadFileGenerationPreviewRow[]>([]);
  const [loadGenerationPreviewLimit, setLoadGenerationPreviewLimit] = useState(1000);
  const [loadGenerationPreviewTruncated, setLoadGenerationPreviewTruncated] = useState(false);
  const [loadGenerationTotalGeneratedRows, setLoadGenerationTotalGeneratedRows] = useState(0);
  const [showLoadGenerationResultModal, setShowLoadGenerationResultModal] = useState(false);
  const [loadGenerationResultRows, setLoadGenerationResultRows] = useState<LoadFileConsolidatedViewRow[]>([]);
  const [loadGenerationResultTitle, setLoadGenerationResultTitle] = useState('');
  const [loadGenerationResultLoading, setLoadGenerationResultLoading] = useState(false);
  const [loadGenerationResultConsolidationId, setLoadGenerationResultConsolidationId] = useState('');
  const [loadGenerationResultJobId, setLoadGenerationResultJobId] = useState('');
  const [loadGenerationResultActiveTab, setLoadGenerationResultActiveTab] = useState<'authorization' | 'orgLevels'>('authorization');
  const [loadGenerationResultPage, setLoadGenerationResultPage] = useState(1);
  const [loadGenerationResultPageSize, setLoadGenerationResultPageSize] = useState(25);
  const [loadGenerationResultSortColumn, setLoadGenerationResultSortColumn] = useState<string | null>(null);
  const [loadGenerationResultSortDirection, setLoadGenerationResultSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loadGenerationResultFilters, setLoadGenerationResultFilters] = useState<Record<string, string>>({});
  const [loadGenerationOrgLevelRows, setLoadGenerationOrgLevelRows] = useState<LoadFileOrgLevelViewRow[]>([]);
  const [loadGenerationOrgLevelLoading, setLoadGenerationOrgLevelLoading] = useState(false);
  const [loadGenerationTotalOrgLevelRows, setLoadGenerationTotalOrgLevelRows] = useState(0);
  const [loadGenerationOrgLevelPage, setLoadGenerationOrgLevelPage] = useState(1);
  const [loadGenerationOrgLevelPageSize, setLoadGenerationOrgLevelPageSize] = useState(25);
  const [loadGenerationOrgLevelSortColumn, setLoadGenerationOrgLevelSortColumn] = useState<string | null>(null);
  const [loadGenerationOrgLevelSortDirection, setLoadGenerationOrgLevelSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loadGenerationOrgLevelFilters, setLoadGenerationOrgLevelFilters] = useState<Record<string, string>>({});
  const [showLoadGenerationRowOriginsModal, setShowLoadGenerationRowOriginsModal] = useState(false);
  const [selectedLoadGenerationOriginRow, setSelectedLoadGenerationOriginRow] = useState<LoadFileConsolidatedViewRow | null>(null);
  const [loadGenerationOriginRows, setLoadGenerationOriginRows] = useState<LoadFileGenerationOriginRow[]>([]);
  const [loadGenerationOriginLoading, setLoadGenerationOriginLoading] = useState(false);
  const [loadGenerationOriginUsersCount, setLoadGenerationOriginUsersCount] = useState(0);
  const [loadGenerationOriginCodesCount, setLoadGenerationOriginCodesCount] = useState(0);
  const [loadGenerationOriginTotalPairs, setLoadGenerationOriginTotalPairs] = useState(0);
  const [loadGenerationOriginTotalItems, setLoadGenerationOriginTotalItems] = useState(0);
  const [loadGenerationOriginTotalVersions, setLoadGenerationOriginTotalVersions] = useState(0);
  const [loadGenerationOriginPairsPage, setLoadGenerationOriginPairsPage] = useState(1);
  const [loadGenerationOriginPairsPageSize, setLoadGenerationOriginPairsPageSize] = useState(25);
  const [loadGenerationOriginSortColumn, setLoadGenerationOriginSortColumn] = useState<string | null>(null);
  const [loadGenerationOriginSortDirection, setLoadGenerationOriginSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loadGenerationOriginFilters, setLoadGenerationOriginFilters] = useState<Record<string, string>>({});
  const [loadGenerationExecutions, setLoadGenerationExecutions] = useState<LoadFileGenerationExecutionRow[]>([]);
  const [loadGenerationExecutionsPage, setLoadGenerationExecutionsPage] = useState(1);
  const [loadGenerationExecutionsPageSize, setLoadGenerationExecutionsPageSize] = useState(10);
  const [loadGenerationExecutionsTotal, setLoadGenerationExecutionsTotal] = useState(0);
  const [loadGenerationExecutionsLoading, setLoadGenerationExecutionsLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadModalRow, setDownloadModalRow] = useState<LoadFileGenerationExecutionRow | null>(null);
  const [downloadModalLoading, setDownloadModalLoading] = useState(false);
  const [downloadModalAuthCount, setDownloadModalAuthCount] = useState<number | null>(null);
  const [downloadModalOrgLevelCount, setDownloadModalOrgLevelCount] = useState<number | null>(null);
  const [downloadAuthPartition, setDownloadAuthPartition] = useState(false);
  const [downloadAuthParts, setDownloadAuthParts] = useState(2);
  const [downloadOrgPartition, setDownloadOrgPartition] = useState(false);
  const [downloadOrgParts, setDownloadOrgParts] = useState(2);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [logConsolidationJobsPage, setLogConsolidationJobsPage] = useState(1);
  const [logConsolidationJobsPageSize, setLogConsolidationJobsPageSize] = useState(5);
  const [logConsolidationUsersPage, setLogConsolidationUsersPage] = useState(1);
  const [logConsolidationUsersPageSize, setLogConsolidationUsersPageSize] = useState(5);
  const [logConsolidationExpandedUsers, setLogConsolidationExpandedUsers] = useState<Record<string, boolean>>({});
  const [logConsolidationExpandedCodes, setLogConsolidationExpandedCodes] = useState<Record<string, boolean>>({});
  const [logConsolidationUserHierarchyByUsername, setLogConsolidationUserHierarchyByUsername] = useState<Record<string, LogConsolidationPlanUserHierarchyState>>({});
  const [showLogConsolidationObjectFieldsModal, setShowLogConsolidationObjectFieldsModal] = useState(false);
  const [logConsolidationObjectFieldsLoading, setLogConsolidationObjectFieldsLoading] = useState(false);
  const [logConsolidationObjectFieldsError, setLogConsolidationObjectFieldsError] = useState<string | null>(null);
  const [logConsolidationObjectFieldsRows, setLogConsolidationObjectFieldsRows] = useState<LogConsolidationObjectFieldDistinctRow[]>([]);
  const [logConsolidationObjectFieldsContext, setLogConsolidationObjectFieldsContext] = useState<LogConsolidationObjectFieldDistinctContext | null>(null);
  const [logConsolidationObjectFieldsPage, setLogConsolidationObjectFieldsPage] = useState(1);
  const [logConsolidationObjectFieldsPageSize, setLogConsolidationObjectFieldsPageSize] = useState(10);
  const [logConsolidationObjectFieldsLoadProgress, setLogConsolidationObjectFieldsLoadProgress] = useState(0);
  const [logConsolidationObjectFieldsLoadMessage, setLogConsolidationObjectFieldsLoadMessage] = useState('');
  const persistGroupOriginsChoiceRef = useRef(true);
  const logConsolidationPlanProgressTimerRef = useRef<number | null>(null);
  const logConsolidationObjectFieldsProgressTimerRef = useRef<number | null>(null);
  const logConsolidationTopDistinctFieldsPollTimerRef = useRef<number | null>(null);
  const loadGenerationPollTimerRef = useRef<number | null>(null);
  const loadGenerationListPollTimerRef = useRef<number | null>(null);

  const pollTimerRef = useRef<number | null>(null);
  const clientsPollTimerRef = useRef<number | null>(null);
  const uploadsPollTimerRef = useRef<number | null>(null);
  const consolidationsPollTimerRef = useRef<number | null>(null);
  const cleanupStatusRef = useRef<Record<string, string>>({});
  const uploadStatusRef = useRef<Record<string, string>>({});
  const activeAnalyzerClientIdRef = useRef<string | undefined>(analyzerClientId);
  const analyzerClientsRef = useRef<AnalyzerClientRow[]>([]);
  const recordsCacheRef = useRef<Map<string, { items: JsonRow[]; total: number; page: number; pageSize: number }>>(new Map());
  const profileConsolidationsRef = useRef<ProfileConsolidationRow[]>([]);

  const loadGenerationEligibleConsolidations = useMemo(
    () => profileConsolidations.filter((item) => String(item.status || '').toLowerCase() === 'structure_adjusted'),
    [profileConsolidations]
  );

  const selectedLoadGenerationConsolidation = useMemo(
    () => loadGenerationEligibleConsolidations.find((item) => item._id === selectedLoadGenerationConsolidationId) || null,
    [loadGenerationEligibleConsolidations, selectedLoadGenerationConsolidationId]
  );

  // theme handled globally in AppLayout

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  };

  const pushToast = (payload: Omit<AppToastItem, 'id'>) => {
    setToasts((prev) => {
      const last = prev.length > 0 ? prev[prev.length - 1] : null;
      const isDuplicateLast = Boolean(
        last
        && last.variant === payload.variant
        && String(last.title || '') === String(payload.title || '')
        && String(last.message || '') === String(payload.message || '')
      );

      if (isDuplicateLast) return prev;

      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      return [...prev, { id, ...payload }];
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!error) return;
    pushToast({ variant: 'danger', title: 'Erro', message: error, durationMs: 7000 });
    setError(null);
  }, [error]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!info) return;
    pushToast({ variant: 'info', title: 'Informação', message: info, durationMs: 5000 });
    setInfo(null);
  }, [info]);

  const guardedNavigate = (to: string) => {
    navigate(to);
  };

  const guardedSetAnalyzerStep = (step: 1 | 2 | 3) => {
    setAnalyzerStep(step);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
      if (clientsPollTimerRef.current) window.clearInterval(clientsPollTimerRef.current);
      if (uploadsPollTimerRef.current) window.clearInterval(uploadsPollTimerRef.current);
      if (consolidationsPollTimerRef.current) window.clearInterval(consolidationsPollTimerRef.current);
      if (loadGenerationPollTimerRef.current) window.clearInterval(loadGenerationPollTimerRef.current);
      if (loadGenerationListPollTimerRef.current) window.clearInterval(loadGenerationListPollTimerRef.current);
    };
  }, []);

  const formatClientDeletionStage = (stage?: string) => {
    const value = String(stage || '').trim();
    if (!value) return '';
    if (value === 'preparing') return 'preparando';
    if (value === 'deleting_uploads') return 'removendo uploads';
    if (value === 'deleting_consolidations') return 'removendo consolidações';
    if (value === 'deleting_adjusted_records') return 'removendo registros ajustados';
    if (value === 'deleting_adjusted_origins') return 'removendo origens ajustadas';
    if (value === 'deleting_usorg_rows') return 'removendo USORG';
    if (value === 'deleting_client_doc') return 'removendo cliente';
    if (value === 'error') return 'erro';
    return value;
  };

  const formatCell = (value: unknown) => {
    if (value === null || typeof value === 'undefined') return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getProgressPercents = (processed: number, total: number) => {
    const safeTotal = total > 0 ? total : 1;
    const realPercent = Math.min(100, Math.max(0, (processed / safeTotal) * 100));

    return {
      safeTotal,
      realPercent: Number(realPercent.toFixed(4)),
      visualPercent: Number(realPercent.toFixed(4))
    };
  };

  const formatBytes = (value: number) => {
    const bytes = Number.isFinite(value) && value > 0 ? value : 0;
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let size = bytes / 1024;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(size >= 100 ? 0 : size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
  };

  const formatCleanupStage = (stage?: string) => {
    const value = String(stage || '').trim();
    if (!value) return '';
    if (value === 'removing_empty_field_value_pairs') return 'removendo pares FIELD/VALUE vazios';
    if (value === 'removing_columns') return 'removendo colunas';
    if (value === 'analyzing_duplicates') return 'analisando duplicadas';
    if (value === 'removing_duplicates') return 'removendo duplicadas';
    if (value === 'removing_by_rules') return 'aplicando regras';
    if (value === 'finished') return 'finalizado';
    if (value === 'cancelled') return 'interrompido';
    if (value === 'error') return 'erro';
    return value;
  };

  const _formatStructureAdjustStage = (stage?: string) => {
    const value = String(stage || '').trim();
    if (!value) return '';
    if (value === 'cleaning_previous_adjusted_data') return 'limpando ajuste anterior';
    if (value === 'consolidating_logs') return 'consolidando logs';
    if (value === 'removing_duplicate_rows') return 'removendo duplicadas';
    if (value === 'grouping_compatible_objects') return 'agrupando objetos compatíveis';
    if (value === 'regrouping_compatible_objects') return 'reavaliando agrupamentos';
    if (value === 'saving_group_origins') return 'persistindo origens dos agrupamentos';
    if (value === 'breaking_fields_values') return 'quebrando campos/valores';
    if (value === 'saving_adjusted_rows') return 'persistindo estrutura';
    if (value === 'stale_processing') return 'interrompido por reinício do backend';
    if (value === 'cleanup_partial_data') return 'limpando dados parciais';
    if (value === 'cleanup_error') return 'erro na limpeza';
    if (value === 'completed') return 'concluído';
    if (value === 'cancelling') return 'interrompendo';
    if (value === 'cancelled') return 'interrompido';
    if (value === 'error') return 'erro';
    return value;
  };

  const renderProgressFill = (percent: number) => {
    const width = Math.max(0, Math.min(100, percent));
    return (
      <div
        style={{
          height: 10,
          width: '100%',
          borderRadius: 999,
          backgroundColor: 'var(--bs-tertiary-bg, rgba(0, 0, 0, 0.12))',
          border: '1px solid var(--bs-border-color, rgba(0, 0, 0, 0.2))',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${width}%`,
            borderRadius: 999,
            backgroundColor: 'var(--bs-primary, #0d6efd)',
            transition: 'width 0.3s ease'
          }}
        />
      </div>
    );
  };

  const buildColumns = (dataRows: JsonRow[]) => {
    const set = new Set<string>();
    dataRows.forEach((row) => Object.keys(row).forEach((key) => set.add(key)));

    const allColumns = Array.from(set);
    const pairColumns = orderedFieldKeys
      .flatMap((fieldKey) => [fieldKey, fieldKey.replace('FIELD', 'VALUE')])
      .filter((column) => set.has(column));

    const remaining = allColumns.filter((column) => !pairColumns.includes(column));
    return [...remaining, ...pairColumns];
  };

  const upsertUploadRow = (row: UploadRow) => {
    setUploads((prev) => {
      const existingIndex = prev.findIndex((item) => item._id === row._id);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = { ...next[existingIndex], ...row };
        return next;
      }
      return [row, ...prev];
    });
  };

  const closeDialog = () => {
    setDialogState({ show: false, mode: 'confirm', title: '', message: '' });
    setDialogAction(null);
    setDialogBusy(false);
  };

  const closeLogConsolidationPlanModal = (force = false) => {
    if (logConsolidationPlanSubmitting && !force) return;
    if (logConsolidationPlanProgressTimerRef.current) {
      window.clearInterval(logConsolidationPlanProgressTimerRef.current);
      logConsolidationPlanProgressTimerRef.current = null;
    }
    if (logConsolidationObjectFieldsProgressTimerRef.current) {
      window.clearInterval(logConsolidationObjectFieldsProgressTimerRef.current);
      logConsolidationObjectFieldsProgressTimerRef.current = null;
    }
    if (logConsolidationTopDistinctFieldsPollTimerRef.current) {
      window.clearInterval(logConsolidationTopDistinctFieldsPollTimerRef.current);
      logConsolidationTopDistinctFieldsPollTimerRef.current = null;
    }
    setShowLogConsolidationPlanModal(false);
    setLogConsolidationPlanLoading(false);
    setLogConsolidationPlanLoadProgress(0);
    setLogConsolidationPlanLoadMessage('');
    setLogConsolidationPlanTarget(null);
    setLogConsolidationPlanPreview(null);
    setLogConsolidationPlanUsersBase([]);
    setLogConsolidationSplitHeavyByCode(false);
    setLogConsolidationUsersFilters({});
    setLogConsolidationRulesPage(1);
    setLogConsolidationRulesPageSize(5);
    setLogConsolidationTopDistinctFieldsPage(1);
    setLogConsolidationTopDistinctFieldsPageSize(5);
    setLogConsolidationTopDistinctFieldsLimit(10);
    setLogConsolidationTopDistinctFieldsLoading(false);
    setLogConsolidationTopDistinctFieldsLoaded(false);
    setLogConsolidationTopDistinctFieldsLoadProgress(0);
    setLogConsolidationTopDistinctFieldsLoadMessage('');
    setLogConsolidationTopDistinctFieldsJobId('');
    setLogConsolidationManualRuleDraft(null);
    setLogConsolidationManualRuleSubmitting(false);
    setLogConsolidationPlanActiveTab('general');
    setLogConsolidationExpandedUsers({});
    setLogConsolidationExpandedCodes({});
    setLogConsolidationUserHierarchyByUsername({});
    setShowLogConsolidationObjectFieldsModal(false);
    setLogConsolidationObjectFieldsLoading(false);
    setLogConsolidationObjectFieldsError(null);
    setLogConsolidationObjectFieldsRows([]);
    setLogConsolidationObjectFieldsContext(null);
    setLogConsolidationObjectFieldsPage(1);
    setLogConsolidationObjectFieldsPageSize(10);
    setLogConsolidationObjectFieldsLoadProgress(0);
    setLogConsolidationObjectFieldsLoadMessage('');
  };

  const openLoadFileGenerationModal = () => {
    if (currentClient?.source !== 'profilegen') {
      setError('A opção 1 de geração de arquivos de carga está disponível apenas para clientes ProfileGen.');
      return;
    }

    if (loadGenerationEligibleConsolidations.length <= 0) {
      setError('Nenhuma consolidação com estrutura ajustada foi encontrada para geração de carga.');
      return;
    }

    if (loadGenerationPollTimerRef.current) {
      window.clearInterval(loadGenerationPollTimerRef.current);
      loadGenerationPollTimerRef.current = null;
    }

    const defaultConsolidationId = String(loadGenerationEligibleConsolidations[0]?._id || '').trim();
    setSelectedLoadGenerationConsolidationId(defaultConsolidationId);
    setLoadGenerationSourceOption('profilegen');
    setLoadGenerationRunning(false);
    setLoadGenerationProgress(0);
    setLoadGenerationProgressMessage('');
    setLoadGenerationJobId('');
    setLoadGenerationProfiles([]);
    setLoadGenerationPreviewRows([]);
    setLoadGenerationPreviewLimit(1000);
    setLoadGenerationPreviewTruncated(false);
    setLoadGenerationTotalGeneratedRows(0);
    setShowLoadFileGenerationModal(true);
  };

  const closeLoadFileGenerationModal = (force = false) => {
    if (loadGenerationRunning && !force) return;
    if (loadGenerationPollTimerRef.current) {
      window.clearInterval(loadGenerationPollTimerRef.current);
      loadGenerationPollTimerRef.current = null;
    }
    setShowLoadFileGenerationModal(false);
    setLoadGenerationRunning(false);
    setLoadGenerationProgress(0);
    setLoadGenerationProgressMessage('');
    setLoadGenerationJobId('');
    setLoadGenerationProfiles([]);
    setLoadGenerationPreviewRows([]);
    setLoadGenerationPreviewTruncated(false);
    setLoadGenerationTotalGeneratedRows(0);
  };

  const closeLoadGenerationResultModal = () => {
    setShowLoadGenerationResultModal(false);
    setLoadGenerationResultRows([]);
    setLoadGenerationOrgLevelRows([]);
    setLoadGenerationResultTitle('');
    setLoadGenerationResultLoading(false);
    setLoadGenerationOrgLevelLoading(false);
    setLoadGenerationResultConsolidationId('');
    setLoadGenerationResultJobId('');
    setLoadGenerationResultActiveTab('authorization');
    setLoadGenerationResultPage(1);
    setLoadGenerationResultSortColumn(null);
    setLoadGenerationResultSortDirection('asc');
    setLoadGenerationResultFilters({});
    setLoadGenerationOrgLevelPage(1);
    setLoadGenerationOrgLevelPageSize(25);
    setLoadGenerationOrgLevelSortColumn(null);
    setLoadGenerationOrgLevelSortDirection('asc');
    setLoadGenerationOrgLevelFilters({});
    setLoadGenerationTotalOrgLevelRows(0);
    setShowLoadGenerationRowOriginsModal(false);
    setSelectedLoadGenerationOriginRow(null);
    setLoadGenerationOriginRows([]);
    setLoadGenerationOriginLoading(false);
    setLoadGenerationOriginUsersCount(0);
    setLoadGenerationOriginCodesCount(0);
    setLoadGenerationOriginTotalPairs(0);
    setLoadGenerationOriginTotalItems(0);
    setLoadGenerationOriginTotalItems(0);
    setLoadGenerationOriginPairsPage(1);
    setLoadGenerationOriginSortColumn(null);
    setLoadGenerationOriginSortDirection('asc');
    setLoadGenerationOriginFilters({});
  };

  const openLoadGenerationRowOriginsModal = (row: LoadFileConsolidatedViewRow) => {
    setSelectedLoadGenerationOriginRow(row);
    setLoadGenerationOriginRows([]);
    setLoadGenerationOriginUsersCount(0);
    setLoadGenerationOriginCodesCount(0);
    setLoadGenerationOriginTotalPairs(0);
    setLoadGenerationOriginTotalItems(0);
    setLoadGenerationOriginTotalVersions(0);
    setLoadGenerationOriginPairsPage(1);
    setLoadGenerationOriginSortColumn(null);
    setLoadGenerationOriginSortDirection('asc');
    setLoadGenerationOriginFilters({});
    setShowLoadGenerationRowOriginsModal(true);
    loadLoadGenerationRowOrigins(row, 1, loadGenerationOriginPairsPageSize, null, 'asc', {}).catch(() => undefined);
  };

  const startLogConsolidationPlanProgress = (message: string, initialProgress = 8) => {
    if (logConsolidationPlanProgressTimerRef.current) {
      window.clearInterval(logConsolidationPlanProgressTimerRef.current);
    }
    setLogConsolidationPlanLoadMessage(message);
    setLogConsolidationPlanLoadProgress(initialProgress);
    logConsolidationPlanProgressTimerRef.current = window.setInterval(() => {
      setLogConsolidationPlanLoadProgress((prev) => {
        if (prev >= 88) return prev;
        const next = prev + (prev < 40 ? 9 : prev < 70 ? 5 : 2);
        return Math.min(88, next);
      });
    }, 220);
  };

  const updateLogConsolidationPlanProgress = (progress: number, message?: string) => {
    setLogConsolidationPlanLoadProgress(Math.max(0, Math.min(100, progress)));
    if (message) setLogConsolidationPlanLoadMessage(message);
  };

  const finishLogConsolidationPlanProgress = (message = 'Prévia carregada.') => {
    if (logConsolidationPlanProgressTimerRef.current) {
      window.clearInterval(logConsolidationPlanProgressTimerRef.current);
      logConsolidationPlanProgressTimerRef.current = null;
    }
    setLogConsolidationPlanLoadProgress(100);
    setLogConsolidationPlanLoadMessage(message);
  };

  const stopLogConsolidationPlanProgress = () => {
    if (logConsolidationPlanProgressTimerRef.current) {
      window.clearInterval(logConsolidationPlanProgressTimerRef.current);
      logConsolidationPlanProgressTimerRef.current = null;
    }
  };

  const startLogConsolidationObjectFieldsProgress = (message: string, initialProgress = 10) => {
    if (logConsolidationObjectFieldsProgressTimerRef.current) {
      window.clearInterval(logConsolidationObjectFieldsProgressTimerRef.current);
    }
    setLogConsolidationObjectFieldsLoadMessage(message);
    setLogConsolidationObjectFieldsLoadProgress(initialProgress);
    logConsolidationObjectFieldsProgressTimerRef.current = window.setInterval(() => {
      setLogConsolidationObjectFieldsLoadProgress((prev) => {
        if (prev >= 90) return prev;
        const next = prev + (prev < 45 ? 8 : prev < 75 ? 4 : 2);
        return Math.min(90, next);
      });
    }, 180);
  };

  const updateLogConsolidationObjectFieldsProgress = (progress: number, message?: string) => {
    setLogConsolidationObjectFieldsLoadProgress(Math.max(0, Math.min(100, progress)));
    if (message) setLogConsolidationObjectFieldsLoadMessage(message);
  };

  const finishLogConsolidationObjectFieldsProgress = (message = 'Campos carregados.') => {
    if (logConsolidationObjectFieldsProgressTimerRef.current) {
      window.clearInterval(logConsolidationObjectFieldsProgressTimerRef.current);
      logConsolidationObjectFieldsProgressTimerRef.current = null;
    }
    setLogConsolidationObjectFieldsLoadProgress(100);
    setLogConsolidationObjectFieldsLoadMessage(message);
  };

  const stopLogConsolidationObjectFieldsProgress = () => {
    if (logConsolidationObjectFieldsProgressTimerRef.current) {
      window.clearInterval(logConsolidationObjectFieldsProgressTimerRef.current);
      logConsolidationObjectFieldsProgressTimerRef.current = null;
    }
  };

  const startLogConsolidationTopDistinctFieldsProgress = (message: string, initialProgress = 10) => {
    setLogConsolidationTopDistinctFieldsLoadMessage(message);
    setLogConsolidationTopDistinctFieldsLoadProgress(Math.max(0, Math.min(100, initialProgress)));
  };

  const updateLogConsolidationTopDistinctFieldsProgress = (progress: number, message?: string) => {
    setLogConsolidationTopDistinctFieldsLoadProgress(Math.max(0, Math.min(100, progress)));
    if (message) setLogConsolidationTopDistinctFieldsLoadMessage(message);
  };

  const finishLogConsolidationTopDistinctFieldsProgress = (message = 'Mapeamento concluído.') => {
    setLogConsolidationTopDistinctFieldsLoadProgress(100);
    setLogConsolidationTopDistinctFieldsLoadMessage(message);
  };

  const stopLogConsolidationTopDistinctFieldsProgress = () => {
    if (logConsolidationTopDistinctFieldsPollTimerRef.current) {
      window.clearInterval(logConsolidationTopDistinctFieldsPollTimerRef.current);
      logConsolidationTopDistinctFieldsPollTimerRef.current = null;
    }
  };

  const mapTopDistinctFieldsWithRules = (
    rowsRaw: any[],
    ruleKeySet: Set<string>
  ): LogConsolidationTopDistinctFieldRow[] => (
    (Array.isArray(rowsRaw) ? rowsRaw : [])
      .map((item: any) => {
        const username = String(item?.username || '').trim();
        const code = String(item?.code || '').trim();
        const object = String(item?.object || '').trim();
        const field = String(item?.field || '').trim();
        const key = `${username.toUpperCase()}|||${code.toUpperCase()}|||${object.toUpperCase()}|||${field.toUpperCase()}`;
        return {
          username,
          code,
          object,
          field,
          distinctValuesCount: Math.max(0, Number(item?.distinctValuesCount || 0)),
          isGlobalAuthorization: ruleKeySet.has(key),
        };
      })
      .filter((item: LogConsolidationTopDistinctFieldRow) => Boolean(item.username) && Boolean(item.code) && Boolean(item.field))
  );

  const parseLogConsolidationRules = (rulesRaw: any[]): LogConsolidationRule[] => {
    const parsed: LogConsolidationRule[] = [];
    const source = Array.isArray(rulesRaw) ? rulesRaw : [];

    for (const rule of source) {
      const rawType = String(rule?.ruleType || '').trim().toLowerCase();
      const username = String(rule?.username || '').trim();
      const code = String(rule?.code || '').trim();
      const object = String(rule?.object || '').trim();
      const field = String(rule?.field || '').trim();
      const thresholdDistinctValues = Math.max(0, Number(rule?.thresholdDistinctValues || 0));
      const rawFieldScope = String(rule?.fieldScope || '').trim().toLowerCase();
      const fieldScope: LogConsolidationRule['fieldScope'] = rawFieldScope === 'common'
        ? 'common'
        : rawFieldScope === 'org_level'
        ? 'org_level'
        : 'all';

      if (rawType === 'global_authorization') {
        if (!username || !code || !field) continue;
        parsed.push({
          ruleType: 'global_authorization',
          username,
          code,
          object,
          field,
          marker: '*',
        });
        continue;
      }

      if (rawType === 'custom_pre_group_wildcard') {
        if (!username || !code || !object || !field) continue;
        parsed.push({
          ruleType: 'custom_pre_group_wildcard',
          username,
          code,
          object,
          field,
          marker: '*',
        });
        continue;
      }

      if (rawType === 'global_authorization_threshold_post_group') {
        if (thresholdDistinctValues <= 0) continue;
        parsed.push({
          ruleType: 'global_authorization_threshold_post_group',
          username: username || '*',
          code: code || '*',
          object: object || '*',
          field: field || '*',
          marker: '*',
          thresholdDistinctValues,
          fieldScope,
        });
      }
    }

    return parsed;
  };

  const applyLogConsolidationPlanPreview = (planData: any) => {
    const consolidationRules = parseLogConsolidationRules(planData?.consolidationRules);

    const ruleKeySet = new Set(
      consolidationRules
        .filter((rule) => rule.ruleType === 'global_authorization')
        .map((rule) => `${rule.username.toUpperCase()}|||${rule.code.toUpperCase()}|||${rule.object.toUpperCase()}|||${rule.field.toUpperCase()}`)
    );
    const incomingTopDistinctFields = Array.isArray(planData?.topDistinctFields)
      ? mapTopDistinctFieldsWithRules(planData.topDistinctFields, ruleKeySet)
      : null;

    const usersBase = Array.isArray(planData?.users)
      ? planData.users.map((user: any) => ({
        username: String(user?.username || '').trim(),
        logCount: Math.max(0, Number(user?.logCount || 0)),
        logSharePercent: Math.max(0, Number(user?.logSharePercent || 0)),
      })).filter((user: LogConsolidationPlanUserBase) => Boolean(user.username))
      : [];

    setLogConsolidationPlanUsersBase(usersBase);
    setLogConsolidationPlanPreview((prev) => {
      const previousTopDistinctFields = mapTopDistinctFieldsWithRules(prev?.topDistinctFields || [], ruleKeySet);
      return {
        optimizationProfile: 'v2',
        totalUsers: Number(planData?.totalUsers || 0),
        totalLogCount: Number(planData?.totalLogCount || 0),
        totalJobs: Number(planData?.totalJobs || 0),
        totalJobsIfHeavySplitByCode: Number(planData?.totalJobsIfHeavySplitByCode || 0),
        totalJobsIfHeavyUserOnly: Number(planData?.totalJobsIfHeavyUserOnly || 0),
        heavyUsersCount: Number(planData?.heavyUsersCount || 0),
        heavyUserCodePairsCount: Number(planData?.heavyUserCodePairsCount || 0),
        tierThresholds: planData?.tierThresholds,
        summaryByTier: Array.isArray(planData?.summaryByTier) ? planData.summaryByTier : [],
        groups: Array.isArray(planData?.groups) ? planData.groups : [],
        users: Array.isArray(planData?.users) ? planData.users : [],
        topDistinctFields: incomingTopDistinctFields || previousTopDistinctFields,
        consolidationRules,
      };
    });
  };

  const _formatConsolidationPlanPercent = (valueRaw: number, totalRaw: number) => {
    const value = Math.max(0, Number(valueRaw || 0));
    const total = Math.max(0, Number(totalRaw || 0));
    const percent = total > 0 ? (value / total) * 100 : 0;
    return `${percent.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`;
  };

  const toggleLogConsolidationCodeHierarchy = (usernameRaw: string, codeRaw: string) => {
    const username = String(usernameRaw || '').trim();
    const code = String(codeRaw || '').trim();
    if (!username || !code) return;
    const key = `${username}|||${code}`;
    setLogConsolidationExpandedCodes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const logConsolidationHierarchyRows = useMemo<LogConsolidationPlanHierarchyRow[]>(() => {
    const previewUsers = logConsolidationPlanPreview?.users;
    const users = Array.isArray(previewUsers) ? previewUsers : [];
    const rows: LogConsolidationPlanHierarchyRow[] = [];
    const totalLogCount = Math.max(0, Number(logConsolidationPlanPreview?.totalLogCount || 0));

    for (const user of users) {
      const username = String(user?.username || '').trim();
      if (!username) continue;

      rows.push({
        key: `user|||${username}`,
        rowType: 'user',
        username,
        label: username,
        workloadTier: user.workloadTier,
        logCount: Math.max(0, Number(user.logCount || 0)),
        logSharePercent: Math.max(0, Number(user.logSharePercent || 0)),
        level: 0,
        expandable: true,
      });

      if (!logConsolidationExpandedUsers[username]) continue;

      const hierarchyState = logConsolidationUserHierarchyByUsername[username];
      if (hierarchyState?.loading) {
        rows.push({
          key: `status|||${username}|||loading`,
          rowType: 'status',
          username,
          label: 'Carregando codes e objects...',
          level: 1,
          expandable: false,
        });
        continue;
      }

      if (hierarchyState?.error) {
        rows.push({
          key: `status|||${username}|||error`,
          rowType: 'status',
          username,
          label: `Não foi possível carregar a hierarquia (${hierarchyState.error}).`,
          level: 1,
          expandable: false,
        });
        continue;
      }

      const hierarchy = hierarchyState?.data;
      const hierarchyCodes = hierarchy?.codes;
      const codes = Array.isArray(hierarchyCodes) ? hierarchyCodes : [];
      if (codes.length === 0) {
        rows.push({
          key: `status|||${username}|||empty`,
          rowType: 'status',
          username,
          label: 'Nenhum code encontrado para este usuário.',
          level: 1,
          expandable: false,
        });
        continue;
      }

      for (const codeRow of codes) {
        const code = String(codeRow?.code || '').trim();
        const codeKey = `${username}|||${code}`;
        const codeLogCount = Math.max(0, Number(codeRow?.logCount || 0));
        rows.push({
          key: `code|||${codeKey}`,
          rowType: 'code',
          username,
          code,
          label: code,
          logCount: codeLogCount,
          logSharePercent: totalLogCount > 0 ? (codeLogCount / totalLogCount) * 100 : 0,
          level: 1,
          expandable: Array.isArray(codeRow?.objects) && codeRow.objects.length > 0,
        });

        if (!logConsolidationExpandedCodes[codeKey]) continue;

        const objects = Array.isArray(codeRow?.objects) ? codeRow.objects : [];
        if (objects.length === 0) {
          rows.push({
            key: `status|||${codeKey}|||empty`,
            rowType: 'status',
            username,
            code,
            label: 'Sem objects para este code.',
            level: 2,
            expandable: false,
          });
          continue;
        }

        for (const objectRow of objects) {
          const object = String(objectRow?.object || '').trim() || '(sem OBJECT)';
          const objectLogCount = Math.max(0, Number(objectRow?.logCount || 0));
          rows.push({
            key: `object|||${codeKey}|||${object}`,
            rowType: 'object',
            username,
            code,
            object,
            label: object,
            logCount: objectLogCount,
            logSharePercent: totalLogCount > 0 ? (objectLogCount / totalLogCount) * 100 : 0,
            level: 2,
            expandable: false,
          });
        }
      }
    }

    return rows;
  }, [logConsolidationExpandedCodes, logConsolidationExpandedUsers, logConsolidationPlanPreview?.totalLogCount, logConsolidationPlanPreview?.users, logConsolidationUserHierarchyByUsername]);

  const toggleLogConsolidationUserHierarchy = async (usernameRaw: string) => {
    const username = String(usernameRaw || '').trim();
    if (!username) return;

    const currentlyExpanded = Boolean(logConsolidationExpandedUsers[username]);
    if (currentlyExpanded) {
      setLogConsolidationExpandedUsers((prev) => ({ ...prev, [username]: false }));
      return;
    }

    setLogConsolidationExpandedUsers((prev) => ({ ...prev, [username]: true }));

    const existing = logConsolidationUserHierarchyByUsername[username];
    if (existing?.loading || existing?.data) return;
    if (!analyzerClientId || !logConsolidationPlanTarget?._id) return;

    setLogConsolidationUserHierarchyByUsername((prev) => ({
      ...prev,
      [username]: {
        loading: true,
        error: undefined,
        data: prev[username]?.data,
      }
    }));

    try {
      const base = resolveApiBase();
      const res = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${logConsolidationPlanTarget._id}/log-consolidation-plan/user-hierarchy`,
        {
          withCredentials: true,
          params: { username },
        }
      );

      const codes = Array.isArray(res.data?.codes)
        ? res.data.codes.map((codeRow: any) => ({
          code: String(codeRow?.code || '').trim(),
          logCount: Math.max(0, Number(codeRow?.logCount || 0)),
          objects: Array.isArray(codeRow?.objects)
            ? codeRow.objects.map((objRow: any) => ({
              object: String(objRow?.object || '').trim() || '(sem OBJECT)',
              logCount: Math.max(0, Number(objRow?.logCount || 0)),
            }))
            : [],
        })).filter((codeRow: LogConsolidationPlanCodeHierarchy) => Boolean(codeRow.code))
        : [];

      const hierarchy: LogConsolidationPlanUserHierarchy = {
        username,
        totalLogCount: Math.max(0, Number(res.data?.totalLogCount || 0)),
        totalCodes: Math.max(0, Number(res.data?.totalCodes || codes.length)),
        totalObjects: Math.max(0, Number(res.data?.totalObjects || codes.reduce((sum: number, codeRow: LogConsolidationPlanCodeHierarchy) => sum + codeRow.objects.length, 0))),
        codes,
      };

      setLogConsolidationUserHierarchyByUsername((prev) => ({
        ...prev,
        [username]: {
          loading: false,
          error: undefined,
          data: hierarchy,
        }
      }));
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_hierarquia_usuario');
      setLogConsolidationUserHierarchyByUsername((prev) => ({
        ...prev,
        [username]: {
          loading: false,
          error: message,
          data: prev[username]?.data,
        }
      }));
    }
  };

  const closeLogConsolidationObjectFieldsModal = () => {
    if (logConsolidationObjectFieldsLoading) return;
    stopLogConsolidationObjectFieldsProgress();
    setShowLogConsolidationObjectFieldsModal(false);
    setLogConsolidationObjectFieldsError(null);
    setLogConsolidationObjectFieldsRows([]);
    setLogConsolidationObjectFieldsContext(null);
    setLogConsolidationObjectFieldsPage(1);
    setLogConsolidationObjectFieldsPageSize(10);
    setLogConsolidationObjectFieldsLoadProgress(0);
    setLogConsolidationObjectFieldsLoadMessage('');
  };

  const openLogConsolidationObjectFieldsModal = async (row: LogConsolidationPlanHierarchyRow) => {
    if (row.rowType !== 'object') return;

    const username = String(row.username || '').trim();
    const code = String(row.code || '').trim();
    const objectLabel = String(row.object || '').trim() || '(sem OBJECT)';
    if (!username || !code || !analyzerClientId || !logConsolidationPlanTarget?._id) return;

    setShowLogConsolidationObjectFieldsModal(true);
    setLogConsolidationObjectFieldsLoading(true);
    setLogConsolidationObjectFieldsError(null);
    setLogConsolidationObjectFieldsRows([]);
    setLogConsolidationObjectFieldsPage(1);
    startLogConsolidationObjectFieldsProgress('Lendo campos e valores distintos deste object...');
    setLogConsolidationObjectFieldsContext({
      username,
      code,
      object: objectLabel,
    });

    try {
      const base = resolveApiBase();
      const objectParam = objectLabel === '(sem OBJECT)' ? '__EMPTY_OBJECT__' : objectLabel;
      const res = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${logConsolidationPlanTarget._id}/log-consolidation-plan/object-field-distincts`,
        {
          withCredentials: true,
          params: {
            username,
            code,
            object: objectParam,
          },
        }
      );

      const rows = Array.isArray(res.data?.fields)
        ? (() => {
          const globalAuthSet = new Set(
            (Array.isArray(res.data?.globalAuthorizationFields) ? res.data.globalAuthorizationFields : [])
              .map((field: any) => String(field || '').trim().toUpperCase())
              .filter((field: string) => field.length > 0)
          );

          return res.data.fields.map((item: any) => {
            const field = String(item?.field || '').trim();
            return {
              field,
              distinctValuesCount: Math.max(0, Number(item?.distinctValuesCount || 0)),
              isGlobalAuthorization: globalAuthSet.has(field.toUpperCase()),
            };
          }).filter((item: LogConsolidationObjectFieldDistinctRow) => Boolean(item.field));
        })()
        : [];

      updateLogConsolidationObjectFieldsProgress(82, 'Organizando dados dos campos...');
      setLogConsolidationObjectFieldsRows(rows);
      if (Array.isArray(res.data?.consolidationRules) && logConsolidationPlanPreview) {
        setLogConsolidationPlanPreview((prev) => prev ? {
          ...prev,
          consolidationRules: parseLogConsolidationRules(res.data.consolidationRules)
        } : prev);
      }
      finishLogConsolidationObjectFieldsProgress();
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_campos_object');
      setLogConsolidationObjectFieldsError(message);
      stopLogConsolidationObjectFieldsProgress();
    } finally {
      setLogConsolidationObjectFieldsLoading(false);
    }
  };

  const setFieldGlobalAuthorizationRule = async (
    fieldRaw: string,
    enabled: boolean,
    scopeOverride?: { username: string; code: string; object: string }
  ) => {
    const field = String(fieldRaw || '').trim();
    if (!field || !analyzerClientId || !logConsolidationPlanTarget?._id) return;

    const username = String(scopeOverride?.username || logConsolidationObjectFieldsContext?.username || '').trim();
    const code = String(scopeOverride?.code || logConsolidationObjectFieldsContext?.code || '').trim();
    const object = String(scopeOverride?.object || logConsolidationObjectFieldsContext?.object || '').trim();
    if (!username || !code) return;

    try {
      const base = resolveApiBase();
      const res = await axios.patch(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${logConsolidationPlanTarget._id}/log-consolidation-rules/global-authorization`,
        { username, code, object, field, enabled },
        { withCredentials: true }
      );

      const nextRules = parseLogConsolidationRules(res.data?.consolidationRules);

      const nextRuleKeySet = new Set(
        nextRules
          .filter((rule) => rule.ruleType === 'global_authorization')
          .map((rule) => `${rule.username.toUpperCase()}|||${rule.code.toUpperCase()}|||${rule.object.toUpperCase()}|||${rule.field.toUpperCase()}`)
      );

      setLogConsolidationPlanPreview((prev) => prev ? {
        ...prev,
        consolidationRules: nextRules,
        topDistinctFields: Array.isArray(prev.topDistinctFields)
          ? prev.topDistinctFields.map((item) => {
            const key = `${String(item.username || '').trim().toUpperCase()}|||${String(item.code || '').trim().toUpperCase()}|||${String(item.object || '').trim().toUpperCase()}|||${String(item.field || '').trim().toUpperCase()}`;
            return {
              ...item,
              isGlobalAuthorization: nextRuleKeySet.has(key),
            };
          })
          : [],
      } : prev);

      const isSameOpenScope = Boolean(
        logConsolidationObjectFieldsContext
        && String(logConsolidationObjectFieldsContext.username || '').trim().toUpperCase() === username.toUpperCase()
        && String(logConsolidationObjectFieldsContext.code || '').trim().toUpperCase() === code.toUpperCase()
        && String(logConsolidationObjectFieldsContext.object || '').trim().toUpperCase() === object.toUpperCase()
      );

      if (isSameOpenScope) {
        const globalAuthSet = new Set(
          (Array.isArray(res.data?.globalAuthorizationFields) ? res.data.globalAuthorizationFields : [])
            .map((item: any) => String(item || '').trim().toUpperCase())
            .filter((item: string) => item.length > 0)
        );

        setLogConsolidationObjectFieldsRows((prev) => prev.map((item) => ({
          ...item,
          isGlobalAuthorization: globalAuthSet.has(String(item.field || '').trim().toUpperCase()),
        })));
      }
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_salvar_regra_autorizacao_global');
      const actionText = enabled ? 'salvar' : 'remover';
      setError(`Não foi possível ${actionText} a regra de autorização global (${message}).`);
    }
  };

  const markFieldAsGlobalAuthorization = async (fieldRaw: string) => {
    await setFieldGlobalAuthorizationRule(fieldRaw, true);
  };

  const clearFieldGlobalAuthorization = async (fieldRaw: string) => {
    await setFieldGlobalAuthorizationRule(fieldRaw, false);
  };

  const loadLogConsolidationTopDistinctFields = async (limitOverride?: number) => {
    if (!analyzerClientId || !logConsolidationPlanTarget?._id) return;

    const requestedLimit = Math.max(
      1,
      Math.min(
        100,
        Number.isFinite(Number(limitOverride))
          ? Number(limitOverride)
          : Number(logConsolidationTopDistinctFieldsLimit || 10)
      )
    );

    if (logConsolidationTopDistinctFieldsPollTimerRef.current) {
      window.clearInterval(logConsolidationTopDistinctFieldsPollTimerRef.current);
      logConsolidationTopDistinctFieldsPollTimerRef.current = null;
    }

    setLogConsolidationTopDistinctFieldsLoading(true);
    setLogConsolidationTopDistinctFieldsLoaded(false);
    setLogConsolidationTopDistinctFieldsPage(1);
    setLogConsolidationTopDistinctFieldsJobId('');
    startLogConsolidationTopDistinctFieldsProgress(`Enfileirando job para mapear Top ${requestedLimit} campos críticos...`, 2);

    try {
      const base = resolveApiBase();
      const startRes = await axios.post(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${logConsolidationPlanTarget._id}/log-consolidation-plan/top-distinct-fields/jobs`,
        {
          limit: requestedLimit,
        },
        {
          withCredentials: true,
        }
      );

      const jobId = String(startRes.data?.jobId || '').trim();
      if (!jobId) {
        throw new Error('top_distinct_fields_job_not_started');
      }

      setLogConsolidationTopDistinctFieldsJobId(jobId);
      updateLogConsolidationTopDistinctFieldsProgress(3, 'Job enfileirado. Aguardando worker...');

      let shouldContinuePolling = true;

      const pollJobStatus = async () => {
        try {
          const statusRes = await axios.get(
            `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${logConsolidationPlanTarget._id}/log-consolidation-plan/top-distinct-fields/jobs/${jobId}`,
            {
              withCredentials: true,
            }
          );

          const status = String(statusRes.data?.status || '').trim().toLowerCase();
          const progress = Math.max(0, Math.min(100, Number(statusRes.data?.progress || 0)));
          const progressMessage = String(statusRes.data?.progressMessage || '').trim();

          updateLogConsolidationTopDistinctFieldsProgress(progress, progressMessage || `Status: ${status || 'processing'}`);

          if (['completed', 'failed', 'cancelled'].includes(status)) {
            shouldContinuePolling = false;
            if (logConsolidationTopDistinctFieldsPollTimerRef.current) {
              window.clearInterval(logConsolidationTopDistinctFieldsPollTimerRef.current);
              logConsolidationTopDistinctFieldsPollTimerRef.current = null;
            }

            if (status === 'completed') {
              setLogConsolidationPlanPreview((prev) => {
                if (!prev) return prev;
                const ruleKeySet = new Set(
                  (Array.isArray(prev.consolidationRules) ? prev.consolidationRules : [])
                    .filter((rule) => String(rule?.ruleType || '').trim().toLowerCase() === 'global_authorization')
                    .map((rule) => `${String(rule.username || '').trim().toUpperCase()}|||${String(rule.code || '').trim().toUpperCase()}|||${String(rule.object || '').trim().toUpperCase()}|||${String(rule.field || '').trim().toUpperCase()}`)
                );

                return {
                  ...prev,
                  topDistinctFields: mapTopDistinctFieldsWithRules(
                    Array.isArray(statusRes.data?.topDistinctFields) ? statusRes.data.topDistinctFields : [],
                    ruleKeySet
                  ),
                };
              });

              setLogConsolidationTopDistinctFieldsLoaded(true);
              finishLogConsolidationTopDistinctFieldsProgress(`Top ${requestedLimit} campos críticos mapeado(s).`);
              setLogConsolidationTopDistinctFieldsLoading(false);
              return;
            }

            const backendMessage = String(statusRes.data?.errorMessage || '').trim();
            const fallback = status === 'cancelled'
              ? 'mapeamento_cancelado'
              : 'erro_top_campos_criticos';
            setError(`Não foi possível mapear os campos críticos (${backendMessage || fallback}).`);
            setLogConsolidationTopDistinctFieldsLoading(false);
          }
        } catch (pollError: any) {
          shouldContinuePolling = false;
          if (logConsolidationTopDistinctFieldsPollTimerRef.current) {
            window.clearInterval(logConsolidationTopDistinctFieldsPollTimerRef.current);
            logConsolidationTopDistinctFieldsPollTimerRef.current = null;
          }
          const backendMessage = pollError?.response?.data?.message;
          const message = typeof backendMessage === 'string' && backendMessage.length > 0
            ? backendMessage
            : (typeof pollError?.message === 'string' ? pollError.message : 'erro_consultar_job_top_campos_criticos');
          setError(`Não foi possível acompanhar o mapeamento dos campos críticos (${message}).`);
          setLogConsolidationTopDistinctFieldsLoading(false);
        }
      };

      await pollJobStatus();
      if (shouldContinuePolling) {
        logConsolidationTopDistinctFieldsPollTimerRef.current = window.setInterval(() => {
          pollJobStatus().catch(() => undefined);
        }, 1200);
      }
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_top_campos_criticos');
      setError(`Não foi possível mapear os campos críticos (${message}).`);
      stopLogConsolidationTopDistinctFieldsProgress();
      setLogConsolidationTopDistinctFieldsLoading(false);
    } finally {
      // Loading is finalized by job terminal status or immediate failure.
    }
  };

  const runLoadFileGeneration = async () => {
    if (!analyzerClientId) return;

    if (currentClient?.source !== 'profilegen') {
      setError('A opção 1 de geração de arquivos de carga está disponível apenas para clientes ProfileGen.');
      return;
    }

    const consolidationId = String(selectedLoadGenerationConsolidationId || '').trim();
    if (!consolidationId) {
      setError('Selecione uma consolidação com estrutura ajustada para gerar os arquivos de carga.');
      return;
    }

    if (loadGenerationSourceOption !== 'profilegen') {
      setError('A opção selecionada ainda não está disponível nesta versão.');
      return;
    }

    setLoadGenerationRunning(true);

    try {
      const base = resolveApiBase();
      const startRes = await axios.post(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${consolidationId}/load-files/jobs`,
        {
          sourceOption: 'profilegen',
          previewLimit: loadGenerationPreviewLimit,
        },
        {
          withCredentials: true,
        }
      );

      const jobId = String(startRes.data?.finalConsolidationId || '').trim();
      if (!jobId) {
        throw new Error('load_files_job_not_started');
      }
      setLoadGenerationJobId(jobId);
      closeLoadFileGenerationModal(true);
      await loadLoadGenerationExecutions(1, loadGenerationExecutionsPageSize);
      setInfo('Geração de arquivos de carga iniciada. O progresso será exibido na tabela do passo 3.');
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_gerar_arquivos_carga');
      setError(`Não foi possível iniciar a geração de arquivos de carga (${message}).`);
    }
    setLoadGenerationRunning(false);
  };

  const removeConsolidationRule = async (rule: LogConsolidationRule) => {
    if (!analyzerClientId || !logConsolidationPlanTarget?._id) return;

    if (rule.ruleType === 'global_authorization') {
      await setFieldGlobalAuthorizationRule(rule.field, false, {
        username: rule.username,
        code: rule.code,
        object: rule.object,
      });
      return;
    }

    try {
      const base = resolveApiBase();
      const res = await axios.delete(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${logConsolidationPlanTarget._id}/log-consolidation-rules`,
        {
          withCredentials: true,
          data: {
            ruleType: rule.ruleType,
            username: rule.username,
            code: rule.code,
            object: rule.object,
            field: rule.field,
            thresholdDistinctValues: rule.thresholdDistinctValues,
            fieldScope: rule.fieldScope,
          }
        }
      );

      const nextRules = parseLogConsolidationRules(res.data?.consolidationRules);
      const nextRuleKeySet = new Set(
        nextRules
          .filter((item) => item.ruleType === 'global_authorization')
          .map((item) => `${item.username.toUpperCase()}|||${item.code.toUpperCase()}|||${item.object.toUpperCase()}|||${item.field.toUpperCase()}`)
      );

      setLogConsolidationPlanPreview((prev) => prev ? {
        ...prev,
        consolidationRules: nextRules,
        topDistinctFields: Array.isArray(prev.topDistinctFields)
          ? prev.topDistinctFields.map((item) => {
            const key = `${String(item.username || '').trim().toUpperCase()}|||${String(item.code || '').trim().toUpperCase()}|||${String(item.object || '').trim().toUpperCase()}|||${String(item.field || '').trim().toUpperCase()}`;
            return {
              ...item,
              isGlobalAuthorization: nextRuleKeySet.has(key),
            };
          })
          : [],
      } : prev);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_remover_regra_manual');
      setError(`Não foi possível remover a regra de consolidação (${message}).`);
    }
  };

  const addManualConsolidationRuleDraft = () => {
    setLogConsolidationManualRuleDraft({
      ruleType: 'custom_pre_group_wildcard',
      username: '*',
      code: '*',
      object: '*',
      field: '*',
      thresholdDistinctValuesCommon: 40,
      thresholdDistinctValuesOrgLevel: 40,
    });
  };

  const updateManualConsolidationRuleDraft = (field: keyof LogConsolidationManualRuleDraft, value: string | number) => {
    setLogConsolidationManualRuleDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const submitManualConsolidationRuleDraft = async () => {
    if (!analyzerClientId || !logConsolidationPlanTarget?._id || !logConsolidationManualRuleDraft) return;

    const draft = logConsolidationManualRuleDraft;
    const normalizeScopeInput = (valueRaw: string) => {
      const value = String(valueRaw || '').trim();
      if (!value) return '*';
      return value;
    };

    const payload = draft.ruleType === 'custom_pre_group_wildcard'
      ? {
        ruleType: 'custom_pre_group_wildcard',
        username: normalizeScopeInput(draft.username),
        code: normalizeScopeInput(draft.code),
        object: normalizeScopeInput(draft.object),
        field: normalizeScopeInput(draft.field),
      }
      : {
        ruleType: 'global_authorization_threshold_post_group',
        thresholdDistinctValuesCommon: Math.max(0, Number(draft.thresholdDistinctValuesCommon || 0)),
        thresholdDistinctValuesOrgLevel: Math.max(0, Number(draft.thresholdDistinctValuesOrgLevel || 0)),
      };

    try {
      setLogConsolidationManualRuleSubmitting(true);
      const base = resolveApiBase();
      const res = await axios.post(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${logConsolidationPlanTarget._id}/log-consolidation-rules`,
        payload,
        { withCredentials: true }
      );

      const nextRules = parseLogConsolidationRules(res.data?.consolidationRules);
      const nextRuleKeySet = new Set(
        nextRules
          .filter((item) => item.ruleType === 'global_authorization')
          .map((item) => `${item.username.toUpperCase()}|||${item.code.toUpperCase()}|||${item.object.toUpperCase()}|||${item.field.toUpperCase()}`)
      );

      setLogConsolidationPlanPreview((prev) => prev ? {
        ...prev,
        consolidationRules: nextRules,
        topDistinctFields: Array.isArray(prev.topDistinctFields)
          ? prev.topDistinctFields.map((item) => {
            const key = `${String(item.username || '').trim().toUpperCase()}|||${String(item.code || '').trim().toUpperCase()}|||${String(item.object || '').trim().toUpperCase()}|||${String(item.field || '').trim().toUpperCase()}`;
            return {
              ...item,
              isGlobalAuthorization: nextRuleKeySet.has(key),
            };
          })
          : [],
      } : prev);

      setLogConsolidationManualRuleDraft(null);
      setLogConsolidationRulesPage(1);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_criar_regra_manual');
      setError(`Não foi possível salvar a regra manual (${message}).`);
    } finally {
      setLogConsolidationManualRuleSubmitting(false);
    }
  };

  const setTopDistinctFieldRule = async (row: LogConsolidationTopDistinctFieldRow, enabled: boolean) => {
    await setFieldGlobalAuthorizationRule(row.field, enabled, {
      username: row.username,
      code: row.code,
      object: row.object,
    });
  };

  const formatLogConsolidationTier = (tier?: LogConsolidationPlanTier) => {
    if (tier === 'heavy') return 'Pesados';
    if (tier === 'medium') return 'Médios';
    if (tier === 'light') return 'Leves';
    return 'Simples';
  };

  const startLogConsolidationRequest = async (
    row: ProfileConsolidationRow,
    options?: {
      actionLabel?: string;
      usersPerJob?: number;
      tierThresholds?: LogConsolidationTierThresholds;
      splitHeavyUsersByCode?: boolean;
    }
  ) => {
    if (!analyzerClientId) return;

    const base = resolveApiBase();
    const actionLabel = options?.actionLabel || 'Consolidação de logs';
    const endpoint = 'log-consolidation-2';

    setError(null);
    setInfo(null);

    try {
      await axios.post(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row._id}/${endpoint}`,
        {
          usersPerJob: options?.usersPerJob,
          persistGroupOrigins: persistGroupOriginsChoiceRef.current,
          tierThresholds: options?.tierThresholds,
          splitHeavyUsersByCode: options?.splitHeavyUsersByCode,
        },
        { withCredentials: true }
      );
      await loadProfileConsolidations(profileConsolidationsPage, profileConsolidationsPageSize);
      setInfo(`${actionLabel} iniciada com agrupamento automático por faixa de usuários. O progresso por usuários será exibido na tabela.`);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_consolidacao_logs');
      setError(`Não foi possível iniciar ${actionLabel.toLowerCase()} (${message}).`);
      throw e;
    }
  };

  const openV2LogConsolidationPlanModal = async (row: ProfileConsolidationRow, actionLabel: string) => {
    if (!analyzerClientId) return;

    setError(null);
    setInfo(null);
    setPersistGroupOriginsChoice(true);
    persistGroupOriginsChoiceRef.current = true;
    setLogConsolidationSplitHeavyByCode(false);
    setLogConsolidationTierThresholds(DEFAULT_LOG_CONSOLIDATION_V2_THRESHOLDS);
    setLogConsolidationUsersFilters({});
    setLogConsolidationExpandedUsers({});
    setLogConsolidationExpandedCodes({});
    setLogConsolidationUserHierarchyByUsername({});
    setLogConsolidationSummaryPage(1);
    setLogConsolidationSummaryPageSize(5);
    setLogConsolidationRulesPage(1);
    setLogConsolidationRulesPageSize(5);
    setLogConsolidationTopDistinctFieldsPage(1);
    setLogConsolidationTopDistinctFieldsPageSize(5);
    setLogConsolidationTopDistinctFieldsLimit(10);
    setLogConsolidationTopDistinctFieldsLoading(false);
    setLogConsolidationTopDistinctFieldsLoaded(false);
    setLogConsolidationTopDistinctFieldsLoadProgress(0);
    setLogConsolidationTopDistinctFieldsLoadMessage('');
    setLogConsolidationTopDistinctFieldsJobId('');
    setLogConsolidationManualRuleDraft(null);
    setLogConsolidationManualRuleSubmitting(false);
    setLogConsolidationJobsPage(1);
    setLogConsolidationJobsPageSize(5);
    setLogConsolidationUsersPage(1);
    setLogConsolidationUsersPageSize(5);
    setLogConsolidationPlanActiveTab('general');
    setLogConsolidationPlanActionLabel(actionLabel);
    setLogConsolidationPlanTarget(row);
    setLogConsolidationPlanPreview(null);
    setLogConsolidationPlanLoading(true);
    setShowLogConsolidationPlanModal(true);
    startLogConsolidationPlanProgress('Consultando consolidação e calculando a prévia...');

    try {
      const base = resolveApiBase();
      const planRes = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row._id}/log-consolidation-plan`,
        {
          withCredentials: true,
          params: {
            optimizationProfile: 'v2',
            heavyMinPercent: DEFAULT_LOG_CONSOLIDATION_V2_THRESHOLDS.heavyMinPercent,
            mediumMinPercent: DEFAULT_LOG_CONSOLIDATION_V2_THRESHOLDS.mediumMinPercent,
            lightMinPercent: DEFAULT_LOG_CONSOLIDATION_V2_THRESHOLDS.lightMinPercent,
          }
        }
      );
      updateLogConsolidationPlanProgress(62, 'Processando usuários e limiares da consolidação...');

      applyLogConsolidationPlanPreview(planRes.data);
      finishLogConsolidationPlanProgress('Prévia carregada.');
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_plano_consolidacao');
      setShowLogConsolidationPlanModal(false);
      setError(`Não foi possível calcular o plano da consolidação v2 (${message}).`);
    } finally {
      stopLogConsolidationPlanProgress();
      setLogConsolidationPlanLoading(false);
    }
  };

  const approveV2LogConsolidationPlan = async () => {
    if (!logConsolidationPlanTarget) return;
    setLogConsolidationPlanSubmitting(true);
    try {
      await startLogConsolidationRequest(logConsolidationPlanTarget, {
        actionLabel: logConsolidationPlanActionLabel,
        tierThresholds: logConsolidationTierThresholds,
        splitHeavyUsersByCode: logConsolidationSplitHeavyByCode,
      });
      closeLogConsolidationPlanModal(true);
    } finally {
      setLogConsolidationPlanSubmitting(false);
    }
  };

  const recalculateLogConsolidationV2Preview = () => {
    if (!analyzerClientId || !logConsolidationPlanTarget) return;

    const fetchPreview = async (splitHeavyOverride?: boolean) => {
      const base = resolveApiBase();
      setLogConsolidationPlanLoading(true);
      startLogConsolidationPlanProgress('Recalculando a prévia da consolidação...');
      try {
        const splitHeavyUsersByCode = typeof splitHeavyOverride === 'boolean'
          ? splitHeavyOverride
          : logConsolidationSplitHeavyByCode;

        const planRes = await axios.get(
          `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${logConsolidationPlanTarget._id}/log-consolidation-plan`,
          {
            withCredentials: true,
            params: {
              optimizationProfile: 'v2',
              heavyMinPercent: logConsolidationTierThresholds.heavyMinPercent,
              mediumMinPercent: logConsolidationTierThresholds.mediumMinPercent,
              lightMinPercent: logConsolidationTierThresholds.lightMinPercent,
              splitHeavyUsersByCode,
            }
          }
        );
        updateLogConsolidationPlanProgress(64, 'Atualizando distribuição por faixa e jobs planejados...');

        applyLogConsolidationPlanPreview(planRes.data);

        setLogConsolidationSummaryPage(1);
        setLogConsolidationRulesPage(1);
        setLogConsolidationTopDistinctFieldsPage(1);
        setLogConsolidationJobsPage(1);
        setLogConsolidationUsersPage(1);
        finishLogConsolidationPlanProgress('Prévia recalculada.');
      } catch (e: any) {
        const backendMessage = e?.response?.data?.message;
        const message = typeof backendMessage === 'string' && backendMessage.length > 0
          ? backendMessage
          : (typeof e?.message === 'string' ? e.message : 'erro_carregar_plano_consolidacao');
        setError(`Não foi possível recalcular o plano da consolidação v2 (${message}).`);
      } finally {
        stopLogConsolidationPlanProgress();
        setLogConsolidationPlanLoading(false);
      }
    };

    fetchPreview().catch(() => undefined);
  };

  const openConfirmDialog = (options: {
    title: string;
    message: string;
    showPersistGroupOriginsToggle?: boolean;
    persistGroupOriginsToggleId?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmVariant?: 'primary' | 'danger' | 'warning' | 'secondary';
    onConfirm: () => Promise<void> | void;
  }) => {
    setDialogAction(() => options.onConfirm);
    setDialogState({
      show: true,
      mode: 'confirm',
      title: options.title,
      message: options.message,
      showPersistGroupOriginsToggle: options.showPersistGroupOriginsToggle,
      persistGroupOriginsToggleId: options.persistGroupOriginsToggleId,
      confirmLabel: options.confirmLabel,
      cancelLabel: options.cancelLabel,
      confirmVariant: options.confirmVariant
    });
  };

  const handleDialogConfirm = async () => {
    const action = dialogAction;
    closeDialog();
    if (!action) return;
    try {
      await action();
    } finally {
      setDialogBusy(false);
    }
  };

  const loadAnalyzerClients = async (targetPage = clientsPage, targetPageSize = clientsPageSize) => {
    const base = resolveApiBase();
    setClientsLoading(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/clients`, {
        withCredentials: true,
        params: { page: targetPage, pageSize: targetPageSize }
      });
      const items: AnalyzerClientRow[] = Array.isArray(res.data?.items) ? res.data.items : [];
      setAnalyzerClients(items);
      analyzerClientsRef.current = items;
      setClientsTotal(Number(res.data?.total || 0));
      setClientsPage(Number(res.data?.page || targetPage));
      setClientsPageSize(Number(res.data?.pageSize || targetPageSize));
      const hasDeletionProcessing = items.some((item) => ['queued', 'processing'].includes(String(item.deletionStatus || 'idle')));
      if (hasDeletionProcessing) {
        if (!clientsPollTimerRef.current) {
          clientsPollTimerRef.current = window.setInterval(() => {
            const processingIds = analyzerClientsRef.current
              .filter((row) => ['queued', 'processing'].includes(String(row.deletionStatus || 'idle')))
              .map((row) => row._id)
              .filter(Boolean);
            if (processingIds.length === 0) return;
            loadAnalyzerClientsProgress(processingIds).catch(() => undefined);
          }, 1500);
        }
      } else if (clientsPollTimerRef.current) {
        window.clearInterval(clientsPollTimerRef.current);
        clientsPollTimerRef.current = null;
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao carregar clientes do analisador.');
    } finally {
      setClientsLoading(false);
    }
  };

  const loadAnalyzerClientsProgress = async (ids: string[]) => {
    if (!Array.isArray(ids) || ids.length === 0) return;

    const base = resolveApiBase();
    const res = await axios.get(`${base}/api/sap-log-analyzer/clients/progress`, {
      withCredentials: true,
      params: { ids: ids.join(',') }
    });

    const updates: AnalyzerClientRow[] = Array.isArray(res.data?.items) ? res.data.items : [];
    const byId = new Map<string, AnalyzerClientRow>();
    updates.forEach((item) => {
      if (item?._id) byId.set(item._id, item);
    });

    let removedRows = 0;
    let hasProcessingInNext = false;

    setAnalyzerClients((prev) => {
      const next = prev
        .filter((row) => {
          const hasUpdate = byId.has(row._id);
          if (hasUpdate) return true;
          const wasDeleting = ['queued', 'processing'].includes(String(row.deletionStatus || 'idle'));
          if (wasDeleting) {
            removedRows += 1;
            return false;
          }
          return true;
        })
        .map((row) => {
          const update = byId.get(row._id);
          const merged = update ? { ...row, ...update } : row;
          if (['queued', 'processing'].includes(String(merged.deletionStatus || 'idle'))) hasProcessingInNext = true;
          return merged;
        });

      analyzerClientsRef.current = next;
      return next;
    });

    if (removedRows > 0) {
      setClientsTotal((prev) => Math.max(0, prev - removedRows));
    }

    if (!hasProcessingInNext && clientsPollTimerRef.current) {
      window.clearInterval(clientsPollTimerRef.current);
      clientsPollTimerRef.current = null;
    }
  };

  const loadProfileGenClients = async () => {
    const base = resolveApiBase();
    try {
      const res = await axios.get(`${base}/api/profilegen-clients`, {
        withCredentials: true,
        params: { page: 1, pageSize: 1000 }
      });
      const items: ProfileGenClientOption[] = Array.isArray(res.data?.items) ? res.data.items : [];
      setProfileGenClients(items.filter((item) => item?.clientId));
    } catch {
      setProfileGenClients([]);
    }
  };

  const loadCurrentClient = async () => {
    if (!analyzerClientId) {
      setCurrentClient(null);
      return;
    }
    const requestedAnalyzerClientId = analyzerClientId;
    const base = resolveApiBase();
    setLoadingCurrentClient(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/clients/${requestedAnalyzerClientId}`, { withCredentials: true });
      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setCurrentClient(res.data || null);
    } catch (e: any) {
      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setCurrentClient(null);
      if (String(e?.response?.data?.message || '') === 'client_deletion_processing') {
        navigate('/analisador-logs-sap');
        setInfo('Este cliente está em exclusão e não pode ser acessado no momento.');
        return;
      }
      setError(e?.response?.data?.message || e?.message || 'Erro ao carregar cliente do analisador.');
    } finally {
      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setLoadingCurrentClient(false);
    }
  };

  const loadUploads = async (targetPage = uploadsPage, targetPageSize = uploadsPageSize, options?: { silent?: boolean }) => {
    const requestedAnalyzerClientId = analyzerClientId;

    if (!requestedAnalyzerClientId) {
      setUploads([]);
      setUploadsTotal(0);
      return;
    }

    const base = resolveApiBase();
    if (!options?.silent) setUploadsLoading(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/uploads`, {
        withCredentials: true,
        params: {
          page: targetPage,
          pageSize: targetPageSize,
          analyzerClientId: requestedAnalyzerClientId,
          filters: JSON.stringify(uploadsColumnFilters)
        }
      });

      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;

      const items = Array.isArray(res.data?.items) ? res.data.items : [];

      if (uploadId && !items.some((item: UploadRow) => item._id === uploadId)) {
        setUploadId(null);
        setUploadStatus(null);
        setRows([]);
        setColumns([]);
        setRecordsTotal(0);
        setProcessingProcessedRecords(0);
        setProcessingTotalRecords(0);
        recordsCacheRef.current.clear();
        setInfo('Upload removido após conclusão da exclusão.');
      }

      const cleanupStatusMap: Record<string, string> = {};
      const uploadStatusMap: Record<string, string> = {};
      items.forEach((item: UploadRow) => {
        cleanupStatusMap[item._id] = String(item.cleanupStatus || 'idle');
        uploadStatusMap[item._id] = String(item.status || 'uploaded');
      });

      if (uploadId) {
        const previousCleanup = cleanupStatusRef.current[uploadId] || 'idle';
        const currentCleanup = cleanupStatusMap[uploadId] || previousCleanup;
        if (previousCleanup === 'processing' && currentCleanup === 'completed') {
          recordsCacheRef.current.clear();
          loadRecords(uploadId, 1, recordsPageSize, { includeTotal: true, force: true }).catch(() => undefined);
          setInfo('Higienização concluída para o upload selecionado.');
        }
        if (previousCleanup === 'processing' && currentCleanup === 'error') {
          setError('A higienização do upload selecionado finalizou com erro.');
        }

        const previousUpload = uploadStatusRef.current[uploadId] || 'uploaded';
        const currentUpload = uploadStatusMap[uploadId] || previousUpload;
        if ((previousUpload === 'processing' || previousUpload === 'uploaded') && currentUpload === 'processed') {
          recordsCacheRef.current.clear();
          loadRecords(uploadId, 1, recordsPageSize, { includeTotal: true, force: true }).catch(() => undefined);
          setInfo('Processamento do upload selecionado concluído.');
        }
        if ((previousUpload === 'processing' || previousUpload === 'uploaded') && currentUpload === 'error') {
          setError('O processamento do upload selecionado finalizou com erro.');
        }

      }

      cleanupStatusRef.current = { ...cleanupStatusRef.current, ...cleanupStatusMap };
      uploadStatusRef.current = { ...uploadStatusRef.current, ...uploadStatusMap };
      setUploads(items);
      setUploadsTotal(Number(res.data?.total || 0));
      setUploadsPage(Number(res.data?.page || targetPage));
      setUploadsPageSize(Number(res.data?.pageSize || targetPageSize));

      const hasCleanupProcessing = items.some((item: UploadRow) => ['queued', 'processing'].includes(String(item.cleanupStatus || 'idle')));
      const hasUploadProcessing = items.some((item: UploadRow) => item.status === 'uploaded' || item.status === 'processing');
      const hasDeleteProcessing = items.some((item: UploadRow) => ['queued', 'processing'].includes(String(item.deletionStatus || 'idle')));
      if (hasCleanupProcessing || hasUploadProcessing || hasDeleteProcessing) {
        if (!uploadsPollTimerRef.current) {
          uploadsPollTimerRef.current = window.setInterval(() => {
            loadUploads(targetPage, targetPageSize, { silent: true });
          }, 10000);
        }
      } else if (uploadsPollTimerRef.current) {
        window.clearInterval(uploadsPollTimerRef.current);
        uploadsPollTimerRef.current = null;
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao carregar uploads salvos.');
    } finally {
      if (!options?.silent) setUploadsLoading(false);
    }
  };

  const loadProfileConsolidations = async (targetPage = profileConsolidationsPage, targetPageSize = profileConsolidationsPageSize) => {
    if (!analyzerClientId) {
      setProfileConsolidations([]);
      setProfileConsolidationsTotal(0);
      return;
    }

    const requestedAnalyzerClientId = analyzerClientId;
    const base = resolveApiBase();
    setProfileConsolidationsLoading(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/clients/${requestedAnalyzerClientId}/profile-consolidations`, {
        withCredentials: true,
        params: { page: targetPage, pageSize: targetPageSize }
      });

      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;

      const items: ProfileConsolidationRow[] = Array.isArray(res.data?.items) ? res.data.items : [];
      setProfileConsolidations(items);
      setProfileConsolidationsTotal(Number(res.data?.total || 0));
      setProfileConsolidationsPage(Number(res.data?.page || targetPage));
      setProfileConsolidationsPageSize(Number(res.data?.pageSize || targetPageSize));

      profileConsolidationsRef.current = items;
      const hasStructureAdjustProcessing = items.some((item) => ['queued', 'processing'].includes(String(item.structureAdjustStatus || 'idle')));
      const hasDeletionProcessing = items.some((item) => ['queued', 'processing'].includes(String(item.deletionStatus || 'idle')));
      if (hasStructureAdjustProcessing || hasDeletionProcessing) {
        if (!consolidationsPollTimerRef.current) {
          consolidationsPollTimerRef.current = window.setInterval(() => {
            const currentIds = profileConsolidationsRef.current.map((item) => item._id).filter(Boolean);
            if (currentIds.length === 0) return;
            loadProfileConsolidationsProgress(currentIds).catch(() => undefined);
          }, 10000);
        }
      } else if (consolidationsPollTimerRef.current) {
        window.clearInterval(consolidationsPollTimerRef.current);
        consolidationsPollTimerRef.current = null;
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao carregar consolidações de log para perfis.');
    } finally {
      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setProfileConsolidationsLoading(false);
    }
  };

  const loadProfileConsolidationsProgress = async (ids: string[]) => {
    if (!analyzerClientId) return;
    if (!Array.isArray(ids) || ids.length === 0) return;

    const requestedAnalyzerClientId = analyzerClientId;
    const base = resolveApiBase();
    const res = await axios.get(`${base}/api/sap-log-analyzer/clients/${requestedAnalyzerClientId}/profile-consolidations/progress`, {
      withCredentials: true,
      params: { ids: ids.join(',') }
    });

    if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;

    const updates: ProfileConsolidationRow[] = Array.isArray(res.data?.items) ? res.data.items : [];
    const byId = new Map<string, ProfileConsolidationRow>();
    updates.forEach((item) => {
      if (item?._id) byId.set(item._id, item);
    });

    let removedRows = 0;

    setProfileConsolidations((prev) => {
      const next = prev
        .filter((row) => {
          const hasUpdate = byId.has(row._id);
          if (hasUpdate) return true;
          const wasDeleting = String(row.deletionStatus || 'idle') === 'processing';
          if (wasDeleting) {
            removedRows += 1;
            return false;
          }
          return true;
        })
        .map((row) => {
          const update = byId.get(row._id);
          if (!update) return row;
          const merged = { ...row, ...update };
          if (typeof update.userConsolidationMode === 'undefined' || update.userConsolidationMode === null) {
            merged.userConsolidationMode = row.userConsolidationMode;
          }
          return merged;
        });
      profileConsolidationsRef.current = next;
      return next;
    });

    if (removedRows > 0) {
      setProfileConsolidationsTotal((prev) => Math.max(0, prev - removedRows));
    }

    const hasStructureAdjustProcessing = updates.some((item) => ['queued', 'processing'].includes(String(item.structureAdjustStatus || 'idle')));
    const hasDeletionProcessing = updates.some((item) => ['queued', 'processing'].includes(String(item.deletionStatus || 'idle')));
    if (!hasStructureAdjustProcessing && !hasDeletionProcessing && consolidationsPollTimerRef.current) {
      window.clearInterval(consolidationsPollTimerRef.current);
      consolidationsPollTimerRef.current = null;
      loadProfileConsolidations(profileConsolidationsPage, profileConsolidationsPageSize).catch(() => undefined);
    }
  };

  const loadLoadGenerationExecutions = async (
    targetPage = loadGenerationExecutionsPage,
    targetPageSize = loadGenerationExecutionsPageSize,
    options?: { silent?: boolean }
  ) => {
    if (!analyzerClientId) {
      setLoadGenerationExecutions([]);
      setLoadGenerationExecutionsTotal(0);
      return;
    }

    const requestedAnalyzerClientId = analyzerClientId;
    const base = resolveApiBase();
    if (!options?.silent) setLoadGenerationExecutionsLoading(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/clients/${requestedAnalyzerClientId}/load-files/jobs`, {
        withCredentials: true,
        params: {
          page: targetPage,
          pageSize: targetPageSize,
        },
      });

      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;

      const items: LoadFileGenerationExecutionRow[] = Array.isArray(res.data?.items)
        ? res.data.items.map((item: any) => ({
          _id: String(item?._id || ''),
          consolidationId: String(item?.consolidationId || ''),
          sourceOption: String(item?.sourceOption || 'profilegen'),
          status: String(item?.status || 'unknown').trim().toLowerCase() as LoadFileGenerationExecutionRow['status'],
          progress: Math.max(0, Math.min(100, Number(item?.progress || 0))),
          progressMessage: String(item?.progressMessage || '').trim(),
          errorMessage: String(item?.errorMessage || '').trim(),
          totalGeneratedRows: Math.max(0, Number(item?.totalGeneratedRows || 0)),
          profilesCount: Math.max(0, Number(item?.profilesCount || 0)),
          previewLimit: Math.max(0, Number(item?.previewLimit || 0)),
          previewRowsCount: Math.max(0, Number(item?.previewRowsCount || 0)),
          previewTruncated: Boolean(item?.previewTruncated),
          createdByName: String(item?.createdByName || '-').trim() || '-',
          createdAt: item?.createdAt,
          startedAt: item?.startedAt,
          finishedAt: item?.finishedAt,
          updatedAt: item?.updatedAt,
        })).filter((item: LoadFileGenerationExecutionRow) => Boolean(item._id))
        : [];

      setLoadGenerationExecutions(items);
      setLoadGenerationExecutionsTotal(Number(res.data?.total || 0));
      setLoadGenerationExecutionsPage(Number(res.data?.page || targetPage));
      setLoadGenerationExecutionsPageSize(Number(res.data?.pageSize || targetPageSize));

      const hasRunningItems = items.some((item) => ['queued', 'processing'].includes(String(item.status || 'unknown')));
      if (hasRunningItems) {
        if (!loadGenerationListPollTimerRef.current) {
          loadGenerationListPollTimerRef.current = window.setInterval(() => {
            loadLoadGenerationExecutions(targetPage, targetPageSize, { silent: true }).catch(() => undefined);
          }, 3000);
        }
      } else if (loadGenerationListPollTimerRef.current) {
        window.clearInterval(loadGenerationListPollTimerRef.current);
        loadGenerationListPollTimerRef.current = null;
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao carregar gerações de arquivos de carga.');
    } finally {
      if (!options?.silent) setLoadGenerationExecutionsLoading(false);
    }
  };

  const viewLoadGenerationExecution = async (row: LoadFileGenerationExecutionRow) => {
    if (!analyzerClientId || !row?._id || !row?.consolidationId) return;
    try {
      const base = resolveApiBase();
      const statusRes = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row.consolidationId}/load-files/jobs/${row._id}`,
        { withCredentials: true }
      );

      setLoadGenerationTotalGeneratedRows(Math.max(0, Number(statusRes.data?.totalGeneratedRows || 0)));
      setLoadGenerationPreviewTruncated(false);
      setLoadGenerationResultRows([]);
      setLoadGenerationResultConsolidationId(row.consolidationId);
      setLoadGenerationResultJobId(row._id);
      setLoadGenerationResultTitle(`Carga consolidada${row.createdAt ? ` - ${new Date(row.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}`);
      setLoadGenerationResultActiveTab('authorization');
      setLoadGenerationResultPage(1);
      setLoadGenerationResultSortColumn(null);
      setLoadGenerationResultSortDirection('asc');
      setLoadGenerationResultFilters({});
      setLoadGenerationOrgLevelRows([]);
      setLoadGenerationOrgLevelPage(1);
      setLoadGenerationOrgLevelPageSize(25);
      setLoadGenerationOrgLevelSortColumn(null);
      setLoadGenerationOrgLevelSortDirection('asc');
      setLoadGenerationOrgLevelFilters({});
      setLoadGenerationTotalOrgLevelRows(0);
      setShowLoadGenerationResultModal(true);

      await loadLoadGenerationResultRows({
        consolidationId: row.consolidationId,
        jobId: row._id,
        targetPage: 1,
        targetPageSize: loadGenerationResultPageSize,
        sortColumn: null,
        sortDirection: 'asc',
        filters: {},
      });
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_visualizar_geracao_carga');
      setError(`Não foi possível carregar os detalhes da geração (${message}).`);
    }
  };

  const loadLoadGenerationResultRows = async (params?: {
    consolidationId?: string;
    jobId?: string;
    targetPage?: number;
    targetPageSize?: number;
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc';
    filters?: Record<string, string>;
  }) => {
    if (!analyzerClientId) return;

    const consolidationId = String(params?.consolidationId || loadGenerationResultConsolidationId || '').trim();
    const jobId = String(params?.jobId || loadGenerationResultJobId || '').trim();
    if (!consolidationId || !jobId) return;

    const targetPage = Math.max(1, Number(params?.targetPage || loadGenerationResultPage || 1));
    const targetPageSize = Math.max(1, Number(params?.targetPageSize || loadGenerationResultPageSize || 25));
    const sortColumn = params?.sortColumn === undefined ? loadGenerationResultSortColumn : params.sortColumn;
    const sortDirection = params?.sortDirection || loadGenerationResultSortDirection || 'asc';
    const filters = params?.filters || loadGenerationResultFilters || {};

    const base = resolveApiBase();
    setLoadGenerationResultLoading(true);
    try {
      const res = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${consolidationId}/load-files/jobs/${jobId}/generated-rows`,
        {
          withCredentials: true,
          params: {
            page: targetPage,
            pageSize: targetPageSize,
            sortBy: sortColumn || undefined,
            sortDirection,
            profileName: String(filters.profileName || '').trim() || undefined,
            object: String(filters.object || '').trim() || undefined,
            numProfile: String(filters.numProfile || '').trim() || undefined,
            field: String(filters.field || '').trim() || undefined,
            valueFrom: String(filters.valueFrom || '').trim() || undefined,
            valueTo: String(filters.valueTo || '').trim() || undefined,
          }
        }
      );

      const rows: LoadFileConsolidatedViewRow[] = Array.isArray(res.data?.items)
        ? res.data.items.map((item: any) => ({
          profileName: String(item?.profileName || '').trim(),
          object: String(item?.object || '').trim(),
          numProfile: String(item?.numProfile || '').trim(),
          field: String(item?.field || '').trim(),
          valueFrom: String(item?.valueFrom || '').trim(),
          valueTo: String(item?.valueTo || '').trim(),
        })).filter((item: LoadFileConsolidatedViewRow) => Boolean(item.profileName) && Boolean(item.field))
        : [];

      setLoadGenerationResultRows(rows);
      setLoadGenerationResultPage(Math.max(1, Number(res.data?.page || targetPage)));
      setLoadGenerationResultPageSize(Math.max(1, Number(res.data?.pageSize || targetPageSize)));
      setLoadGenerationTotalGeneratedRows(Math.max(0, Number(res.data?.total || 0)));
      setLoadGenerationPreviewTruncated(false);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_geracao_carga');
      setError(`Não foi possível carregar as linhas geradas (${message}).`);
    } finally {
      setLoadGenerationResultLoading(false);
    }
  };

  const loadLoadGenerationRowOrigins = async (
    row: LoadFileConsolidatedViewRow,
    targetPage = loadGenerationOriginPairsPage,
    targetPageSize = loadGenerationOriginPairsPageSize,
    sortColumn: string | null = loadGenerationOriginSortColumn,
    sortDirection: 'asc' | 'desc' = loadGenerationOriginSortDirection,
    filters: Record<string, string> = loadGenerationOriginFilters
  ) => {
    if (!analyzerClientId) return;
    const consolidationId = String(loadGenerationResultConsolidationId || '').trim();
    const jobId = String(loadGenerationResultJobId || '').trim();
    if (!consolidationId || !jobId) return;

    const base = resolveApiBase();
    setLoadGenerationOriginLoading(true);
    try {
      const res = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${consolidationId}/load-files/jobs/${jobId}/origins`,
        {
          withCredentials: true,
          params: {
            profileName: row.profileName,
            object: row.object,
            page: targetPage,
            pageSize: targetPageSize,
            sortBy: sortColumn || undefined,
            sortDirection,
            numProfile: String(filters.numProfile || '').trim() || undefined,
            user: String(filters.user || '').trim() || undefined,
            code: String(filters.code || '').trim() || undefined,
            field: String(filters.field || '').trim() || undefined,
            valueFrom: String(filters.valueFrom || '').trim() || undefined,
            valueTo: String(filters.valueTo || '').trim() || undefined,
          }
        }
      );

      const items: LoadFileGenerationOriginRow[] = Array.isArray(res.data?.items)
        ? res.data.items.map((item: any) => ({
          user: String(item?.user || '').trim(),
          code: String(item?.code || '').trim(),
          object: String(item?.object || '').trim(),
          field: String(item?.field || '').trim(),
          valueFrom: String(item?.valueFrom || '').trim(),
          valueTo: String(item?.valueTo || '').trim(),
          numProfile: String(item?.numProfile || '').trim(),
        }))
        : [];

      setLoadGenerationOriginRows(items);
      setLoadGenerationOriginPairsPage(Math.max(1, Number(res.data?.page || targetPage)));
      setLoadGenerationOriginPairsPageSize(Math.max(1, Number(res.data?.pageSize || targetPageSize)));
      setLoadGenerationOriginTotalPairs(Math.max(0, Number(res.data?.pairsCount || 0)));
      setLoadGenerationOriginTotalItems(Math.max(0, Number(res.data?.total || 0)));
      setLoadGenerationOriginUsersCount(Math.max(0, Number(res.data?.usersCount || 0)));
      setLoadGenerationOriginCodesCount(Math.max(0, Number(res.data?.codesCount || 0)));
      setLoadGenerationOriginTotalVersions(Math.max(0, Number(res.data?.totalVersionsForObject || 0)));
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_origens_geracao_carga');
      setError(`Não foi possível carregar as origens da linha selecionada (${message}).`);
      setLoadGenerationOriginRows([]);
      setLoadGenerationOriginTotalPairs(0);
      setLoadGenerationOriginTotalItems(0);
      setLoadGenerationOriginTotalVersions(0);
    } finally {
      setLoadGenerationOriginLoading(false);
    }
  };

  const loadLoadGenerationOrgLevelRows = async (params?: {
    consolidationId?: string;
    jobId?: string;
    targetPage?: number;
    targetPageSize?: number;
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc';
    filters?: Record<string, string>;
  }) => {
    if (!analyzerClientId) return;

    const consolidationId = String(params?.consolidationId || loadGenerationResultConsolidationId || '').trim();
    const jobId = String(params?.jobId || loadGenerationResultJobId || '').trim();
    if (!consolidationId || !jobId) return;

    const targetPage = Math.max(1, Number(params?.targetPage || loadGenerationOrgLevelPage || 1));
    const targetPageSize = Math.max(1, Number(params?.targetPageSize || loadGenerationOrgLevelPageSize || 25));
    const sortColumn = params?.sortColumn === undefined ? loadGenerationOrgLevelSortColumn : params.sortColumn;
    const sortDirection = params?.sortDirection || loadGenerationOrgLevelSortDirection || 'asc';
    const filters = params?.filters || loadGenerationOrgLevelFilters || {};

    const base = resolveApiBase();
    setLoadGenerationOrgLevelLoading(true);
    try {
      const res = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${consolidationId}/load-files/jobs/${jobId}/generated-org-level-rows`,
        {
          withCredentials: true,
          params: {
            page: targetPage,
            pageSize: targetPageSize,
            sortBy: sortColumn || undefined,
            sortDirection,
            profileName: String(filters.profileName || '').trim() || undefined,
            field: String(filters.field || '').trim() || undefined,
            valueFrom: String(filters.valueFrom || '').trim() || undefined,
            valueTo: String(filters.valueTo || '').trim() || undefined,
          }
        }
      );

      const rows: LoadFileOrgLevelViewRow[] = Array.isArray(res.data?.items)
        ? res.data.items.map((item: any) => ({
          profileName: String(item?.profileName || '').trim(),
          field: String(item?.field || '').trim(),
          valueFrom: String(item?.valueFrom || '').trim(),
          valueTo: String(item?.valueTo || '').trim(),
          variable: String(item?.variable || '').trim(),
        })).filter((item: LoadFileOrgLevelViewRow) => Boolean(item.profileName) && Boolean(item.field))
        : [];

      setLoadGenerationOrgLevelRows(rows);
      setLoadGenerationOrgLevelPage(Math.max(1, Number(res.data?.page || targetPage)));
      setLoadGenerationOrgLevelPageSize(Math.max(1, Number(res.data?.pageSize || targetPageSize)));
      setLoadGenerationTotalOrgLevelRows(Math.max(0, Number(res.data?.total || 0)));
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_niveis_organizacionais_geracao_carga');
      setError(`Não foi possível carregar os níveis organizacionais (${message}).`);
      setLoadGenerationOrgLevelRows([]);
      setLoadGenerationTotalOrgLevelRows(0);
    } finally {
      setLoadGenerationOrgLevelLoading(false);
    }
  };

  const openDownloadModal = async (row: LoadFileGenerationExecutionRow) => {
    if (!analyzerClientId || !row?._id || !row?.consolidationId) return;
    const status = String(row.status || '').toLowerCase();
    if (status !== 'completed') {
      setError('Somente gerações concluídas permitem download dos arquivos de carga.');
      return;
    }
    setDownloadModalRow(row);
    setDownloadAuthPartition(false);
    setDownloadAuthParts(2);
    setDownloadOrgPartition(false);
    setDownloadOrgParts(2);
    setDownloadModalAuthCount(null);
    setDownloadModalOrgLevelCount(null);
    setDownloadModalLoading(true);
    setShowDownloadModal(true);
    try {
      const base = resolveApiBase();
      const res = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row.consolidationId}/load-files/jobs/${row._id}/row-counts`,
        { withCredentials: true }
      );
      setDownloadModalAuthCount(typeof res.data?.authRowCount === 'number' ? res.data.authRowCount : null);
      setDownloadModalOrgLevelCount(typeof res.data?.orgLevelRowCount === 'number' ? res.data.orgLevelRowCount : null);
    } catch {
      setDownloadModalAuthCount(null);
      setDownloadModalOrgLevelCount(null);
    } finally {
      setDownloadModalLoading(false);
    }
  };

  const executeDownloadFiles = async () => {
    const row = downloadModalRow;
    if (!analyzerClientId || !row?._id || !row?.consolidationId) return;
    const base = resolveApiBase();
    setDownloadInProgress(true);
    setError(null);
    try {
      const authTotalParts = downloadAuthPartition ? Math.max(2, downloadAuthParts) : 1;
      const orgTotalParts = downloadOrgPartition ? Math.max(2, downloadOrgParts) : 1;

      for (let part = 1; part <= authTotalParts; part++) {
        const url = `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row.consolidationId}/load-files/jobs/${row._id}/download-authorization-xlsx?part=${part}&totalParts=${authTotalParts}`;
        const response = await axios.get(url, { withCredentials: true, responseType: 'blob' });
        const resolvedFileName = extractFileNameFromContentDisposition(
          response.headers?.['content-disposition'],
          authTotalParts > 1 ? `CLIENTE_auth_parte${part}.xlsx` : 'CLIENTE_auth.xlsx'
        );
        triggerBlobDownload(response.data, resolvedFileName, response.headers?.['content-type']);
      }

      for (let part = 1; part <= orgTotalParts; part++) {
        const url = `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row.consolidationId}/load-files/jobs/${row._id}/download-org-level-xlsx?part=${part}&totalParts=${orgTotalParts}`;
        const response = await axios.get(url, { withCredentials: true, responseType: 'blob' });
        const resolvedFileName = extractFileNameFromContentDisposition(
          response.headers?.['content-disposition'],
          orgTotalParts > 1 ? `CLIENTE_orglevel_parte${part}.xlsx` : 'CLIENTE_orglevel.xlsx'
        );
        triggerBlobDownload(response.data, resolvedFileName, response.headers?.['content-type']);
      }

      setInfo('Download dos arquivos de carga concluído.');
      setShowDownloadModal(false);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_download_arquivos_carga');
      setError(`Não foi possível baixar os arquivos de carga (${message}).`);
    } finally {
      setDownloadInProgress(false);
    }
  };

  const deleteLoadGenerationExecution = (row: LoadFileGenerationExecutionRow) => {
    if (!analyzerClientId || !row?._id || !row?.consolidationId) return;

    openConfirmDialog({
      title: 'Excluir geração de carga',
      message: 'Deseja excluir esta geração de arquivos de carga?',
      confirmLabel: 'Excluir',
      confirmVariant: 'danger',
      onConfirm: async () => {
        const base = resolveApiBase();
        setError(null);
        setInfo(null);

        try {
          await axios.delete(
            `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row.consolidationId}/load-files/jobs/${row._id}`,
            { withCredentials: true }
          );

          const nextPage = loadGenerationExecutions.length === 1 && loadGenerationExecutionsPage > 1
            ? loadGenerationExecutionsPage - 1
            : loadGenerationExecutionsPage;

          if (showLoadGenerationResultModal) {
            setShowLoadGenerationResultModal(false);
            setLoadGenerationResultRows([]);
            setLoadGenerationResultConsolidationId('');
            setLoadGenerationResultJobId('');
            setShowLoadGenerationRowOriginsModal(false);
            setSelectedLoadGenerationOriginRow(null);
            setLoadGenerationOriginRows([]);
          }

          await loadLoadGenerationExecutions(nextPage, loadGenerationExecutionsPageSize);
          setInfo('Geração de arquivos de carga excluída com sucesso.');
        } catch (e: any) {
          const backendMessage = e?.response?.data?.message;
          const message = typeof backendMessage === 'string' && backendMessage.length > 0
            ? backendMessage
            : (typeof e?.message === 'string' ? e.message : 'erro_excluir_geracao_carga');
          setError(`Não foi possível excluir a geração de carga (${message}).`);
        }
      }
    });
  };

  const loadConsolidationOptions = async () => {
    if (!analyzerClientId) {
      setConsolidationOptions([]);
      return;
    }

    const requestedAnalyzerClientId = analyzerClientId;
    const base = resolveApiBase();
    setConsolidationOptionsLoading(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/uploads`, {
        withCredentials: true,
        params: {
          page: 1,
          pageSize: 1000,
          analyzerClientId: requestedAnalyzerClientId
        }
      });

      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;

      const items: UploadRow[] = Array.isArray(res.data?.items) ? res.data.items : [];
      const eligible = items.filter((item) => {
        const status = String(item.status || '');
        const deletionStatus = String(item.deletionStatus || 'idle');
        return ['processed', 'cancelled'].includes(status) && !['processing', 'queued'].includes(deletionStatus);
      });

      setConsolidationOptions(eligible);
      setSelectedConsolidationUploadIds((prev) => prev.filter((id) => eligible.some((item) => item._id === id)));
    } catch (e: any) {
      setConsolidationOptions([]);
      setError(e?.response?.data?.message || e?.message || 'Erro ao carregar uploads elegíveis para consolidação.');
    } finally {
      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setConsolidationOptionsLoading(false);
    }
  };

  const loadUsorgSummary = async () => {
    if (!analyzerClientId) {
      setUsorgRowsTotal(0);
      return;
    }

    const requestedAnalyzerClientId = analyzerClientId;
    const base = resolveApiBase();
    setUsorgSummaryLoading(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/clients/${requestedAnalyzerClientId}/usorg`, {
        withCredentials: true
      });

      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setUsorgRowsTotal(Math.max(0, Number(res.data?.total || 0)));
    } catch {
      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setUsorgRowsTotal(0);
    } finally {
      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setUsorgSummaryLoading(false);
    }
  };

  const loadAdjustedStructureRows = async (
    consolidationId: string,
    targetPage = adjustedStructurePage,
    targetPageSize = adjustedStructurePageSize
  ) => {
    if (!analyzerClientId) {
      setAdjustedStructureRows([]);
      setAdjustedStructureTotal(0);
      return;
    }

    const requestedAnalyzerClientId = analyzerClientId;
    const base = resolveApiBase();
    setAdjustedStructureLoading(true);
    try {
      const res = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${requestedAnalyzerClientId}/profile-consolidations/${consolidationId}/adjusted-records`,
        {
          withCredentials: true,
          params: { page: targetPage, pageSize: targetPageSize, filters: JSON.stringify(adjustedStructureColumnFilters) }
        }
      );

      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;

      setAdjustedStructureRows(Array.isArray(res.data?.items) ? res.data.items : []);
      setAdjustedStructureTotal(Number(res.data?.total || 0));
      setAdjustedStructurePage(Number(res.data?.page || targetPage));
      setAdjustedStructurePageSize(Number(res.data?.pageSize || targetPageSize));
    } catch (e: any) {
      setAdjustedStructureRows([]);
      setAdjustedStructureTotal(0);
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_estrutura_ajustada');
      setError(`Não foi possível carregar a estrutura ajustada (${message}).`);
    } finally {
      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setAdjustedStructureLoading(false);
    }
  };

  const openAdjustedGroupOriginModal = async (sourceGroupId: string) => {
    if (!analyzerClientId || !adjustedStructureTarget?._id) return;
    const groupId = String(sourceGroupId || '').trim();
    if (!groupId) return;

    const requestedAnalyzerClientId = analyzerClientId;
    const consolidationId = adjustedStructureTarget._id;
    const base = resolveApiBase();

    setAdjustedGroupOriginLoading(true);
    setAdjustedGroupOriginItem(null);
    setShowAdjustedGroupOriginModal(true);

    try {
      const res = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${requestedAnalyzerClientId}/profile-consolidations/${consolidationId}/adjusted-groups/${groupId}/origins`,
        { withCredentials: true }
      );

      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setAdjustedGroupOriginItem(res.data?.item || null);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_origens_agrupamento');
      setError(`Não foi possível carregar as origens do agrupamento (${message}).`);
      setShowAdjustedGroupOriginModal(false);
    } finally {
      if (activeAnalyzerClientIdRef.current !== requestedAnalyzerClientId) return;
      setAdjustedGroupOriginLoading(false);
    }
  };

  const loadRecords = async (
    targetUploadId: string,
    targetPage = recordsPage,
    targetPageSize = recordsPageSize,
    options?: { includeTotal?: boolean; force?: boolean }
  ) => {
    const base = resolveApiBase();
    const includeTotal = options?.includeTotal !== false;
    const cacheKey = JSON.stringify({
      uploadId: targetUploadId,
      page: targetPage,
      pageSize: targetPageSize,
      filters: recordsColumnFilters
    });

    if (!options?.force) {
      const cached = recordsCacheRef.current.get(cacheKey);
      if (cached) {
        setRows(cached.items);
        setColumns(buildColumns(cached.items));
        setRecordsPage(cached.page);
        setRecordsPageSize(cached.pageSize);
        setRecordsTotal(cached.total);
        return;
      }
    }

    setRecordsLoading(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/uploads/${targetUploadId}/records`, {
        withCredentials: true,
        params: {
          page: targetPage,
          pageSize: targetPageSize,
          filters: JSON.stringify(recordsColumnFilters),
          includeTotal,
          knownTotal: includeTotal ? undefined : recordsTotal
        }
      });

      const items: JsonRow[] = Array.isArray(res.data?.items) ? res.data.items : [];
      const total = Number(res.data?.total || 0);
      setRows(items);
      const builtColumns = buildColumns(items);
      const hasActiveFilters = Object.values(recordsColumnFilters).some((value) => String(value || '').trim().length > 0);
      if (builtColumns.length > 0) {
        setColumns(builtColumns);
      } else if (!hasActiveFilters) {
        setColumns([]);
      }
      setRecordsSortColumn(null);
      setRecordsSortDirection('asc');
      setRecordsPage(Number(res.data?.page || targetPage));
      setRecordsPageSize(Number(res.data?.pageSize || targetPageSize));
      setRecordsTotal(total);

      recordsCacheRef.current.set(cacheKey, {
        items,
        total,
        page: Number(res.data?.page || targetPage),
        pageSize: Number(res.data?.pageSize || targetPageSize)
      });
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_registros');
      setRows([]);
      setColumns([]);
      setRecordsTotal(0);
      setError(`Não foi possível carregar os registros salvos (${message}).`);
      throw e;
    } finally {
      setRecordsLoading(false);
    }
  };

  // Intentionally run when route param changes; other refs are stable and managed by dedicated effects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    activeAnalyzerClientIdRef.current = analyzerClientId;
    setAnalyzerStep(1);
    setError(null);
    setInfo(null);
    setToasts([]);

    if (uploadsPollTimerRef.current) {
      window.clearInterval(uploadsPollTimerRef.current);
      uploadsPollTimerRef.current = null;
    }
    if (clientsPollTimerRef.current) {
      window.clearInterval(clientsPollTimerRef.current);
      clientsPollTimerRef.current = null;
    }
    if (consolidationsPollTimerRef.current) {
      window.clearInterval(consolidationsPollTimerRef.current);
      consolidationsPollTimerRef.current = null;
    }
    if (loadGenerationListPollTimerRef.current) {
      window.clearInterval(loadGenerationListPollTimerRef.current);
      loadGenerationListPollTimerRef.current = null;
    }

    setUploads([]);
    setUploadsTotal(0);
    setUploadsPage(1);
    setProfileConsolidations([]);
    setProfileConsolidationsTotal(0);
    setProfileConsolidationsPage(1);
    setLoadGenerationExecutions([]);
    setLoadGenerationExecutionsTotal(0);
    setLoadGenerationExecutionsPage(1);
    setUsorgRowsTotal(0);
    setShowConsolidationModal(false);
    setShowUsorgModal(false);
    setUsorgFile(null);
    setConsolidationOptions([]);
    setSelectedConsolidationUploadIds([]);
    setShowAdjustedStructureModal(false);
    setAdjustedStructureTarget(null);
    setAdjustedStructureRows([]);
    setAdjustedStructureTotal(0);
    setAdjustedStructurePage(1);
    setAdjustedStructureColumnFilters({});
    setUploadId(null);
    setUploadStatus(null);
    setRows([]);
    setColumns([]);
    setRecordsTotal(0);
    setProcessingProcessedRecords(0);
    setProcessingTotalRecords(0);
    cleanupStatusRef.current = {};
    uploadStatusRef.current = {};
    recordsCacheRef.current.clear();

    if (isClientDetail) {
      loadCurrentClient();
      loadUploads(1, uploadsPageSize);
    } else {
      loadAnalyzerClients(1, clientsPageSize);
      loadProfileGenClients();
    }
  }, [analyzerClientId]);

  // Triggered by upload column filters; pagination and source changes are handled elsewhere.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isClientDetail) return;
    loadUploads(1, uploadsPageSize);
  }, [uploadsColumnFilters]);

  // Step transitions orchestrate these loads; function deps are intentionally excluded to avoid duplicate reloads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isClientDetail) return;
    if (!analyzerClientId) return;
    if (analyzerStep === 2) {
      if (currentClient?.source !== 'profilegen') return;
      loadProfileConsolidations(1, profileConsolidationsPageSize);
      loadUsorgSummary();
    }
    if (analyzerStep === 3) {
      if (currentClient?.source === 'profilegen') {
        loadProfileConsolidations(1, profileConsolidationsPageSize);
      }
      loadLoadGenerationExecutions(1, loadGenerationExecutionsPageSize);
    }
  }, [isClientDetail, analyzerClientId, analyzerStep, currentClient?.source]);

  // Debounced record reload based on filter state and active upload.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!uploadId) return;
    const timeout = window.setTimeout(() => {
      recordsCacheRef.current.clear();
      loadRecords(uploadId, 1, recordsPageSize, { includeTotal: true, force: true });
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [recordsColumnFilters]);

  const createAnalyzerClient = async () => {
    const base = resolveApiBase();
    setError(null);
    setInfo(null);
    setCreatingClient(true);

    try {
      const payload: Record<string, string> = { source: newClientMode };
      if (newClientMode === 'profilegen') {
        if (!selectedProfileGenClientId) {
          setError('Selecione um cliente do gerador de perfis.');
          return;
        }
        payload.profileGenClientId = selectedProfileGenClientId;
      } else {
        if (!manualClientId.trim()) {
          setError('Informe o clientId do novo cliente.');
          return;
        }
        payload.clientId = manualClientId.trim().toUpperCase();
        if (manualClientName.trim()) payload.clientName = manualClientName.trim();
      }

      const res = await axios.post(`${base}/api/sap-log-analyzer/clients`, payload, { withCredentials: true });
      const created = res.data as AnalyzerClientRow;
      setInfo('Cliente criado com sucesso.');
      setShowCreateClientModal(false);
      guardedNavigate(`/analisador-logs-sap/cliente/${created._id}`);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_criar_cliente');
      setError(`Não foi possível criar o cliente (${message}).`);
    } finally {
      setCreatingClient(false);
    }
  };

  const openCreateClientModal = () => {
    setNewClientMode('profilegen');
    setSelectedProfileGenClientId('');
    setManualClientId('');
    setManualClientName('');
    setShowCreateClientModal(true);
  };

  const closeCreateClientModal = () => {
    if (creatingClient) return;
    setShowCreateClientModal(false);
  };

  const deleteAnalyzerClient = (client: AnalyzerClientRow) => {
    openConfirmDialog({
      title: 'Excluir cliente',
      message: `Deseja excluir o cliente ${client.clientId} e todos os dados carregados no analisador?`,
      confirmLabel: 'Excluir',
      confirmVariant: 'danger',
      onConfirm: async () => {
        const base = resolveApiBase();
        setError(null);
        setInfo(null);

        try {
          await axios.delete(`${base}/api/sap-log-analyzer/clients/${client._id}`, { withCredentials: true });
          setAnalyzerClients((prev) => {
            const next = prev.map((item) => (
              item._id === client._id
                ? {
                  ...item,
                  deletionStatus: 'queued' as const,
                  deletionProcessedItems: 0,
                  deletionTotalItems: 0,
                  deletionStage: 'queued',
                  deletionErrorMessage: undefined
                }
                : item
            ));
            analyzerClientsRef.current = next;
            return next;
          });

          if (!clientsPollTimerRef.current) {
            clientsPollTimerRef.current = window.setInterval(() => {
              const processingIds = analyzerClientsRef.current
                .filter((row) => ['queued', 'processing'].includes(String(row.deletionStatus || 'idle')))
                .map((row) => row._id)
                .filter(Boolean);
              if (processingIds.length === 0) return;
              loadAnalyzerClientsProgress(processingIds).catch(() => undefined);
            }, 10000);
          }

          setInfo('Exclusão do cliente enfileirada. O progresso será atualizado na tabela.');
        } catch (e: any) {
          const backendMessage = e?.response?.data?.message;
          const message = typeof backendMessage === 'string' && backendMessage.length > 0
            ? backendMessage
            : (typeof e?.message === 'string' ? e.message : 'erro_exclusao_cliente');
          setError(`Não foi possível excluir o cliente (${message}).`);
        }
      }
    });
  };

  const selectUpload = async (selectedUploadId: string) => {
    setUploadId(selectedUploadId);
    setError(null);
    setInfo('Carregando registros salvos do upload selecionado...');
    recordsCacheRef.current.clear();
    try {
      await loadRecords(selectedUploadId, 1, recordsPageSize);
      const selected = uploads.find((item) => item._id === selectedUploadId);
      if (selected) setUploadStatus(selected.status);
      if (selected?.status === 'processing') {
        setProcessingProcessedRecords(Number(selected.processedRecords || 0));
        setProcessingTotalRecords(Number(selected.estimatedRecords || selected.totalRecords || 0));
      } else {
        setProcessingProcessedRecords(0);
        setProcessingTotalRecords(0);
      }
      setInfo('Registros carregados do banco com sucesso.');
    } catch {
      setInfo(null);
    }
  };

  const deleteUpload = (selectedUploadId: string) => {
    openConfirmDialog({
      title: 'Excluir upload',
      message: 'Deseja realmente excluir este upload e todos os registros/mapeamentos relacionados?',
      confirmLabel: 'Excluir',
      confirmVariant: 'danger',
      onConfirm: async () => {
        const base = resolveApiBase();
        setError(null);
        setInfo(null);

        try {
          await axios.delete(`${base}/api/sap-log-analyzer/uploads/${selectedUploadId}`, { withCredentials: true });
          await loadUploads(1, uploadsPageSize, { silent: true });
          setInfo('Exclusão iniciada. O progresso será exibido na coluna Processados até concluir.');
        } catch (e: any) {
          const backendMessage = e?.response?.data?.message;
          const message = typeof backendMessage === 'string' && backendMessage.length > 0
            ? backendMessage
            : (typeof e?.message === 'string' ? e.message : 'erro_exclusao');
          setError(`Não foi possível excluir o upload (${message}).`);
        }
      }
    });
  };

  const openConsolidationModal = () => {
    setSelectedConsolidationUploadIds([]);
    setShowConsolidationModal(true);
    loadConsolidationOptions();
  };

  const closeConsolidationModal = () => {
    if (creatingConsolidation) return;
    setShowConsolidationModal(false);
    setSelectedConsolidationUploadIds([]);
  };

  const openUsorgModal = () => {
    setError(null);
    setInfo(null);
    setUsorgFile(null);
    setShowUsorgModal(true);
  };

  const closeUsorgModal = () => {
    if (usorgUploading) return;
    setShowUsorgModal(false);
    setUsorgFile(null);
  };

  const uploadUsorg = async () => {
    if (!analyzerClientId) {
      setError('Cliente do analisador inválido para upload da USORG.');
      return;
    }
    if (!usorgFile) {
      setError('Selecione um arquivo USORG para envio.');
      return;
    }

    const base = resolveApiBase();
    setUsorgUploading(true);
    setError(null);
    setInfo(null);
    try {
      const formData = new FormData();
      formData.append('file', usorgFile);

      const res = await axios.post(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/usorg`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setInfo(`USORG carregada com sucesso (${Number(res.data?.total || 0).toLocaleString()} linhas válidas).`);
      setShowUsorgModal(false);
      setUsorgFile(null);
      await loadUsorgSummary();
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_usorg');
      setError(`Não foi possível carregar a USORG (${message}).`);
    } finally {
      setUsorgUploading(false);
    }
  };

  const toggleConsolidationUploadSelection = (selectedUploadId: string) => {
    setSelectedConsolidationUploadIds((prev) => {
      if (prev.includes(selectedUploadId)) {
        return prev.filter((id) => id !== selectedUploadId);
      }
      return [...prev, selectedUploadId];
    });
  };

  const toggleSelectAllConsolidationUploads = () => {
    const allSelected = consolidationOptions.length > 0
      && consolidationOptions.every((item) => selectedConsolidationUploadIds.includes(item._id));

    if (allSelected) {
      setSelectedConsolidationUploadIds([]);
      return;
    }

    setSelectedConsolidationUploadIds(consolidationOptions.map((item) => item._id));
  };

  const createProfileConsolidation = async () => {
    if (!analyzerClientId) {
      setError('Cliente do analisador inválido para consolidação.');
      return;
    }
    if (selectedConsolidationUploadIds.length === 0) {
      setError('Selecione pelo menos um upload para gerar a consolidação.');
      return;
    }

    const base = resolveApiBase();
    setCreatingConsolidation(true);
    setError(null);
    setInfo(null);
    try {
      await axios.post(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations`,
        { uploadIds: selectedConsolidationUploadIds },
        { withCredentials: true }
      );

      setInfo('Consolidação de logs para perfis gerada com sucesso.');
      setShowConsolidationModal(false);
      setSelectedConsolidationUploadIds([]);
      await loadProfileConsolidations(1, profileConsolidationsPageSize);
    } catch (e: any) {
      const backendCode = String(e?.response?.data?.code || '').trim();
      if (backendCode === 'incompatible_upload_structures_for_consolidation') {
        const incompatibilities = Array.isArray(e?.response?.data?.incompatibilities)
          ? e.response.data.incompatibilities
          : [];
        const details = incompatibilities
          .map((item: any) => {
            const fileName = String(item?.fileName || item?.uploadId || '-').trim() || '-';
            const missing = Array.isArray(item?.missingComparedToBaseline) ? item.missingComparedToBaseline : [];
            const extra = Array.isArray(item?.extraComparedToBaseline) ? item.extraComparedToBaseline : [];
            const missingPart = missing.length > 0 ? `faltando: ${missing.join(', ')}` : '';
            const extraPart = extra.length > 0 ? `extras: ${extra.join(', ')}` : '';
            return `${fileName} (${[missingPart, extraPart].filter(Boolean).join(' | ')})`;
          })
          .filter(Boolean)
          .join(' ; ');

        setError(details
          ? `Estrutura dos logs incompatível para consolidação (ignora ROWNUMBER): ${details}.`
          : 'Estrutura dos logs incompatível para consolidação (ignora ROWNUMBER).');
        return;
      }

      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_gerar_consolidacao');
      setError(`Não foi possível gerar a consolidação (${message}).`);
    } finally {
      setCreatingConsolidation(false);
    }
  };

  const deleteProfileConsolidation = (row: ProfileConsolidationRow) => {
    if (!analyzerClientId) return;

    openConfirmDialog({
      title: 'Excluir consolidação',
      message: 'Deseja excluir este registro de consolidação?',
      confirmLabel: 'Excluir',
      confirmVariant: 'danger',
      onConfirm: async () => {
        const base = resolveApiBase();
        setError(null);
        setInfo(null);

        try {
          await axios.delete(`${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row._id}`, {
            withCredentials: true
          });
          setProfileConsolidations((prev) => {
            const next: ProfileConsolidationRow[] = prev.map((item): ProfileConsolidationRow => {
              if (item._id !== row._id) return item;
              return {
                ...item,
                deletionStatus: 'queued',
                deletionProcessedItems: 0,
                deletionTotalItems: 0,
                deletionStage: 'queued',
                deletionErrorMessage: undefined
              };
            });
            profileConsolidationsRef.current = next;
            return next;
          });

          if (!consolidationsPollTimerRef.current) {
            consolidationsPollTimerRef.current = window.setInterval(() => {
              const currentIds = profileConsolidationsRef.current.map((item) => item._id).filter(Boolean);
              if (currentIds.length === 0) return;
              loadProfileConsolidationsProgress(currentIds).catch(() => undefined);
            }, 10000);
          }

          setInfo('Exclusão da consolidação enfileirada. O progresso será atualizado na tabela.');
        } catch (e: any) {
          const backendMessage = e?.response?.data?.message;
          const message = typeof backendMessage === 'string' && backendMessage.length > 0
            ? backendMessage
            : (typeof e?.message === 'string' ? e.message : 'erro_excluir_consolidacao');
          setError(`Não foi possível excluir a consolidação (${message}).`);
        }
      }
    });
  };

  const runLogConsolidationByUser = (
    row: ProfileConsolidationRow,
    options?: {
      actionLabel?: string;
    }
  ) => {
    if (!analyzerClientId) return;

    const actionLabel = options?.actionLabel || 'Consolidação de logs';
    setError(null);
    setInfo(null);

    openV2LogConsolidationPlanModal(row, actionLabel).catch(() => undefined);
  };

  const cancelProfileConsolidationStructure = (row: ProfileConsolidationRow) => {
    if (!analyzerClientId) return;

    openConfirmDialog({
      title: 'Interromper consolidação de logs',
      message: 'Deseja interromper a consolidação de logs em andamento?',
      confirmLabel: 'Interromper consolidação',
      confirmVariant: 'warning',
      onConfirm: async () => {
        const base = resolveApiBase();
        setError(null);
        setInfo(null);

        setProfileConsolidations((prev) => {
          const next = prev.map((item) => (
            item._id === row._id
              ? { ...item, structureAdjustStage: 'cancelling' }
              : item
          ));
          profileConsolidationsRef.current = next;
          return next;
        });
        setInfo('Enviando solicitação de interrupção da consolidação...');

        try {
          await axios.post(
            `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row._id}/cancel-adjust-structure`,
            {},
            { withCredentials: true }
          );
          setInfo('Solicitação de interrupção da consolidação enviada. Aguarde a atualização do progresso.');
        } catch (e: any) {
          const backendMessage = e?.response?.data?.message;
          const message = typeof backendMessage === 'string' && backendMessage.length > 0
            ? backendMessage
            : (typeof e?.message === 'string' ? e.message : 'erro_cancelar_consolidacao_logs');
          setError(`Não foi possível interromper a consolidação de logs (${message}).`);
        }
      }
    });
  };

  const openAdjustedStructureModal = (row: ProfileConsolidationRow) => {
    setAdjustedStructureTarget(row);
    setAdjustedStructureRows([]);
    setAdjustedStructureTotal(0);
    setAdjustedStructurePage(1);
    setAdjustedStructureColumnFilters({});
    setShowAdjustedStructureModal(true);
    loadAdjustedStructureRows(row._id, 1, adjustedStructurePageSize);
  };

  const closeAdjustedStructureModal = () => {
    if (adjustedStructureLoading) return;
    setShowAdjustedStructureModal(false);
    setAdjustedStructureTarget(null);
    setShowAdjustedGroupOriginModal(false);
    setAdjustedGroupOriginItem(null);
  };

  // Modal/filter controlled reload with debounce; loader function intentionally excluded.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!showAdjustedStructureModal) return;
    if (!adjustedStructureTarget?._id) return;
    const timeout = window.setTimeout(() => {
      loadAdjustedStructureRows(adjustedStructureTarget._id, 1, adjustedStructurePageSize);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [showAdjustedStructureModal, adjustedStructureTarget?._id, adjustedStructureColumnFilters]);

  const cancelUploadProcessing = (selectedUploadId: string) => {
    openConfirmDialog({
      title: 'Interromper carga',
      message: 'Deseja interromper o processamento desta carga?',
      confirmLabel: 'Interromper',
      confirmVariant: 'warning',
      onConfirm: async () => {
        const base = resolveApiBase();
        setError(null);
        try {
          await axios.post(`${base}/api/sap-log-analyzer/uploads/${selectedUploadId}/cancel-processing`, {}, { withCredentials: true });
          setInfo('Solicitação de interrupção enviada para a carga.');
          await loadUploads(uploadsPage, uploadsPageSize, { silent: true });
        } catch (e: any) {
          const backendMessage = e?.response?.data?.message;
          const message = typeof backendMessage === 'string' && backendMessage.length > 0
            ? backendMessage
            : (typeof e?.message === 'string' ? e.message : 'erro_cancelar_carga');
          setError(`Não foi possível interromper a carga (${message}).`);
        }
      }
    });
  };

  const cancelCleanupProcessing = (selectedUploadId: string) => {
    openConfirmDialog({
      title: 'Interromper higienização',
      message: 'Deseja interromper a higienização deste upload?',
      confirmLabel: 'Interromper',
      confirmVariant: 'warning',
      onConfirm: async () => {
        const base = resolveApiBase();
        setError(null);
        try {
          await axios.post(`${base}/api/sap-log-analyzer/uploads/${selectedUploadId}/cancel-cleanup`, {}, { withCredentials: true });
          setInfo('Solicitação de interrupção enviada para a higienização.');
          await loadUploads(uploadsPage, uploadsPageSize, { silent: true });
        } catch (e: any) {
          const backendMessage = e?.response?.data?.message;
          const message = typeof backendMessage === 'string' && backendMessage.length > 0
            ? backendMessage
            : (typeof e?.message === 'string' ? e.message : 'erro_cancelar_higienizacao');
          setError(`Não foi possível interromper a higienização (${message}).`);
        }
      }
    });
  };

  const openCleanupModal = (upload: UploadRow) => {
    setCleanupUpload(upload);
    setAvailableCleanupColumns([]);
    setSelectedCleanupColumns([]);
    setAvailableSelection([]);
    setRemoveSelection([]);
    setCleanupRemoveEmptyFieldValuePairs(false);
    setCleanupRemoveDuplicates(false);
    setCleanupRules([]);
    setCleanupRunning(false);
    setShowCleanupModal(true);
    loadUploadColumns(upload._id);
  };

  const closeCleanupModal = () => {
    if (cleanupRunning) return;
    setShowCleanupModal(false);
    setCleanupUpload(null);
    setAvailableCleanupColumns([]);
    setSelectedCleanupColumns([]);
    setAvailableSelection([]);
    setRemoveSelection([]);
    setCleanupRemoveEmptyFieldValuePairs(false);
    setCleanupRemoveDuplicates(false);
    setCleanupRules([]);
  };

  const loadUploadColumns = async (targetUploadId: string) => {
    const base = resolveApiBase();
    setCleanupColumnsLoading(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/uploads/${targetUploadId}/columns`, { withCredentials: true });
      const columns = Array.isArray(res.data?.columns) ? res.data.columns.map((item: unknown) => String(item || '')) : [];
      setAvailableCleanupColumns(columns);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_colunas_cleanup');
      setError(`Não foi possível carregar colunas do upload (${message}).`);
    } finally {
      setCleanupColumnsLoading(false);
    }
  };

  const addSelectedColumnsToRemove = () => {
    if (availableSelection.length === 0) return;
    const toRemoveSet = new Set(selectedCleanupColumns);
    availableSelection.forEach((item) => toRemoveSet.add(item));
    setSelectedCleanupColumns(Array.from(toRemoveSet).sort((a, b) => a.localeCompare(b)));
    setAvailableSelection([]);
  };

  const removeSelectedColumnsFromRemove = () => {
    if (removeSelection.length === 0) return;
    const removeSet = new Set(removeSelection);
    setSelectedCleanupColumns((prev) => prev.filter((item) => !removeSet.has(item)));
    setRemoveSelection([]);
  };

  const addCleanupRule = () => {
    setCleanupRules((prev) => [...prev, createCleanupRule()]);
  };

  const applyRecommendedCleanupPreset = () => {
    const recommendedColumns = Array.from(new Set(RECOMMENDED_CLEANUP_COLUMNS.map((item) => String(item).trim()).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right));

    const recommendedRules: CleanupRuleDraft[] = RECOMMENDED_CLEANUP_RULES.map((rule) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      conditions: rule.conditions.map((condition) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        column: String(condition.column || '').trim(),
        operator: condition.operator,
        value: String(condition.value || '').trim()
      }))
    }));

    setSelectedCleanupColumns(recommendedColumns);
    setAvailableSelection([]);
    setRemoveSelection([]);
    setCleanupRemoveEmptyFieldValuePairs(false);
    setCleanupRules(recommendedRules);
    setCleanupRemoveDuplicates(true);
    setInfo('Configuração de higienização recomendada carregada na tela.');
  };

  const removeCleanupRule = (ruleId: string) => {
    setCleanupRules((prev) => prev.filter((rule) => rule.id !== ruleId));
  };

  const addCleanupRuleCondition = (ruleId: string) => {
    setCleanupRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? { ...rule, conditions: [...rule.conditions, createCleanupCondition()] }
          : rule
      )
    );
  };

  const removeCleanupRuleCondition = (ruleId: string, conditionId: string) => {
    setCleanupRules((prev) =>
      prev.map((rule) => {
        if (rule.id !== ruleId) return rule;
        const nextConditions = rule.conditions.filter((condition) => condition.id !== conditionId);
        return { ...rule, conditions: nextConditions.length > 0 ? nextConditions : [createCleanupCondition()] };
      })
    );
  };

  const updateCleanupRuleCondition = (
    ruleId: string,
    conditionId: string,
    field: 'column' | 'operator' | 'value',
    value: string
  ) => {
    setCleanupRules((prev) =>
      prev.map((rule) => {
        if (rule.id !== ruleId) return rule;
        return {
          ...rule,
          conditions: rule.conditions.map((condition) =>
            condition.id === conditionId
              ? {
                  ...condition,
                  [field]: value,
                  ...(field === 'operator' && (value === 'empty' || value === 'not_empty') ? { value: '' } : {})
                }
              : condition
          )
        };
      })
    );
  };

  const cleanupRulesPayload = cleanupRules
    .map((rule) => ({
      conditions: rule.conditions
        .map((condition) => ({
          column: condition.column.trim(),
          operator: condition.operator,
          value: condition.value.trim()
        }))
        .filter((condition) => {
          if (!condition.column) return false;
          const operatorConfig = CLEANUP_OPERATOR_OPTIONS.find((item) => item.value === condition.operator);
          if (!operatorConfig) return false;
          if (!operatorConfig.requiresValue) return true;
          return condition.value.length > 0;
        })
    }))
    .filter((rule) => rule.conditions.length > 0);

  const runCleanup = async () => {
    if (!cleanupUpload?._id) {
      setError('Selecione um upload válido para higienização.');
      return;
    }

    const columns = selectedCleanupColumns;

    if (columns.length === 0 && !cleanupRemoveEmptyFieldValuePairs && !cleanupRemoveDuplicates && cleanupRulesPayload.length === 0) {
      setError('Informe ao menos uma opção: remover pares FIELD/VALUE vazios, remover colunas, remover duplicadas ou regras por coluna/valor.');
      return;
    }

    const base = resolveApiBase();
    setCleanupRunning(true);
    setError(null);
    setInfo(null);

    try {
      const res = await axios.post(
        `${base}/api/sap-log-analyzer/uploads/${cleanupUpload._id}/cleanup`,
        {
          removeColumns: columns,
          removeEmptyFieldValuePairs: cleanupRemoveEmptyFieldValuePairs,
          removeDuplicates: cleanupRemoveDuplicates,
          removeRules: cleanupRulesPayload
        },
        { withCredentials: true }
      );

      const processed = Number(res.data?.processedRecords || 0);
      const total = Number(res.data?.totalRecords || 0);
      setInfo(`Higienização iniciada em background (${processed.toLocaleString()} de ${total.toLocaleString()}).`);
      setShowCleanupModal(false);
      setCleanupUpload(null);
      setCleanupRunning(false);
      await loadUploads(uploadsPage, uploadsPageSize, { silent: true });
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_higienizacao');
      setError(`Não foi possível executar a higienização (${message}).`);
      setCleanupRunning(false);
    }
  };

  const openActivityModal = async (upload: UploadRow) => {
    setActivityUpload(upload);
    setActivityItems([]);
    setShowActivityModal(true);

    const base = resolveApiBase();
    setActivityLoading(true);
    try {
      const res = await axios.get(`${base}/api/sap-log-analyzer/uploads/${upload._id}/activity`, { withCredentials: true });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setActivityItems(items as UploadActivityItem[]);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_log_upload');
      setError(`Não foi possível carregar o log do upload (${message}).`);
    } finally {
      setActivityLoading(false);
    }
  };

  const closeActivityModal = () => {
    if (activityLoading) return;
    setShowActivityModal(false);
    setActivityUpload(null);
    setActivityItems([]);
  };

  const openConsolidationActivityModal = async (row: ProfileConsolidationRow) => {
    if (!analyzerClientId) return;

    setConsolidationActivityTarget(row);
    setConsolidationActivityItems([]);
    setShowConsolidationActivityModal(true);

    const base = resolveApiBase();
    setConsolidationActivityLoading(true);
    try {
      const res = await axios.get(
        `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${row._id}/activity`,
        { withCredentials: true }
      );
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setConsolidationActivityItems(items as ConsolidationActivityItem[]);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_carregar_log_consolidacao');
      setError(`Não foi possível carregar o log da consolidação (${message}).`);
    } finally {
      setConsolidationActivityLoading(false);
    }
  };

  const closeConsolidationActivityModal = () => {
    if (consolidationActivityLoading) return;
    setShowConsolidationActivityModal(false);
    setConsolidationActivityTarget(null);
    setConsolidationActivityItems([]);
  };

  const startStatusPolling = (targetUploadId: string) => {
    const base = resolveApiBase();

    if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);

    pollTimerRef.current = window.setInterval(async () => {
      try {
        const statusRes = await axios.get(`${base}/api/sap-log-analyzer/uploads/${targetUploadId}/status`, { withCredentials: true });
        const status = statusRes.data?.status as 'uploaded' | 'processing' | 'processed' | 'error' | 'cancelled';
        const processedRecords = Number(statusRes.data?.processedRecords || 0);
        const totalRecords = Number(statusRes.data?.totalRecords || 0);
        const estimatedRecords = Number(statusRes.data?.estimatedRecords || 0);
        const progressTotal = estimatedRecords > 0 ? estimatedRecords : totalRecords;

        upsertUploadRow({
          _id: targetUploadId,
          fileName: fileName || logFile?.name || 'upload.json',
          status,
          processedRecords,
          totalRecords,
          estimatedRecords,
          createdAt: new Date().toISOString()
        });

        setProcessingProcessedRecords(processedRecords);
        setProcessingTotalRecords(progressTotal);

        setUploadStatus(status);

        if (status === 'processed') {
          if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
          setProcessingProcessedRecords(totalRecords);
          setProcessingTotalRecords(totalRecords);
          await loadRecords(targetUploadId, 1, recordsPageSize);
          await loadUploads(1, uploadsPageSize);
          setInfo(`Processamento concluído. ${totalRecords.toLocaleString()} registros salvos no banco.`);
        }

        if (status === 'error' || status === 'cancelled') {
          if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
          const backendError = statusRes.data?.errorMessage ? ` (${statusRes.data.errorMessage})` : '';
          if (status === 'cancelled') {
            setInfo(`Processamento interrompido pelo usuário${backendError}.`);
            setError(null);
          } else {
            setError(`Falha no processamento do arquivo no backend${backendError}.`);
            setInfo(null);
          }
          setProcessingProcessedRecords(0);
          setProcessingTotalRecords(0);
          await loadUploads(uploadsPage, uploadsPageSize);
        }
      } catch {
        if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
        setError('Não foi possível consultar o status do processamento.');
      }
    }, 10000);
  };

  const handleLogFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setLogFile(file);
    setFileName(file?.name || '');
  };

  const handleMappingFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setMappingFile(file);
    setMappingFileName(file?.name || '');
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    if (isUploading) return;
    setShowUploadModal(false);
    setUploadTransferProgress(null);
  };

  const handleUpload = async () => {
    const selectedOption = LOG_TYPE_OPTIONS.find((item) => item.value === selectedLogType);
    const isSupported = Boolean(selectedOption?.supported);
    const backendLogType: UploadModelType = selectedOption?.value || 'STUSERTRACE (json)';

    if (!analyzerClientId) {
      setError('Selecione um cliente do analisador antes de carregar arquivos.');
      return;
    }

    if (!isSupported) {
      setError('Modelo de log não suportado - Em desenvolvimento.');
      return;
    }

    if (!logFile) {
      setError('Selecione o arquivo de log (.json).');
      return;
    }

    if (!mappingFile) {
      setError('Selecione o arquivo de mapeamento (txt tabulado ou ;).');
      return;
    }

    setError(null);
    setInfo(null);
    setRows([]);
    setColumns([]);
    setRecordsColumnFilters({});
    setRecordsSortColumn(null);
    setRecordsSortDirection('asc');
    setRecordsTotal(0);
    setRecordsPage(1);
    setProcessingProcessedRecords(0);
    setProcessingTotalRecords(0);
    setUploadTransferProgress({ loaded: 0, total: null, percent: null });
    recordsCacheRef.current.clear();
    setIsUploading(true);

    try {
      const base = resolveApiBase();
      const formData = new FormData();
      formData.append('file', logFile);
      formData.append('mappingFile', mappingFile);
      formData.append('analyzerClientId', analyzerClientId);
      formData.append('logType', backendLogType);

      const uploadRes = await axios.post(`${base}/api/sap-log-analyzer/uploads`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const loaded = Number(progressEvent.loaded || 0);
          const total = typeof progressEvent.total === 'number' && progressEvent.total > 0
            ? Number(progressEvent.total)
            : null;
          const percent = total ? Math.min(100, Math.max(0, (loaded / total) * 100)) : null;
          setUploadTransferProgress({
            loaded,
            total,
            percent: percent !== null ? Number(percent.toFixed(2)) : null
          });
        }
      });

      const newUploadId = String(uploadRes.data?.uploadId || '');
      if (!newUploadId) throw new Error('upload_id_not_returned');

      const optimisticUpload: UploadRow = {
        _id: newUploadId,
        analyzerClientId,
        clientId: currentClient?.clientId,
        fileName: logFile?.name || fileName || 'upload.json',
        uploadedByName: String(uploadRes.data?.uploadedByName || '').trim() || '-',
        logType: backendLogType,
        status: 'uploaded',
        totalRecords: 0,
        estimatedRecords: Number(uploadRes.data?.estimatedRecords || 0),
        processedRecords: 0,
        createdAt: new Date().toISOString()
      };
      upsertUploadRow(optimisticUpload);
      setUploadsTotal((prev) => prev + 1);

      setUploadId(newUploadId);
      setUploadStatus('uploaded');
      setShowUploadModal(false);
      setLogFile(null);
      setMappingFile(null);
      setFileName('');
      setMappingFileName('');
      setUploadTransferProgress(null);
      setInfo('Upload do log e mapeamento concluído. Iniciando processamento no servidor...');
      await loadUploads(1, uploadsPageSize, { silent: true });
      startStatusPolling(newUploadId);
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      const message = typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : (typeof e?.message === 'string' ? e.message : 'erro_upload');
      setError(`Não foi possível enviar/processar o arquivo (${message}).`);
    } finally {
      setIsUploading(false);
    }
  };

  const recordsData = rows.map((row, idx) => ({ __index: idx, ...row }));
  const sortedRecordsData = useMemo(() => {
    const data = [...recordsData];

    if (!recordsSortColumn) return data;

    return data.sort((left, right) => {
      const leftRaw = (left as Record<string, unknown>)[recordsSortColumn];
      const rightRaw = (right as Record<string, unknown>)[recordsSortColumn];
      const toSortableValue = (value: unknown): string | number => {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
            const numeric = Number(trimmed);
            if (!Number.isNaN(numeric)) return numeric;
          }
          return trimmed.toLowerCase();
        }
        return formatCell(value).toLowerCase();
      };

      const leftValue = toSortableValue(leftRaw);
      const rightValue = toSortableValue(rightRaw);

      if (leftValue === rightValue) return 0;

      const comparison =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue));

      return recordsSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [recordsData, recordsSortColumn, recordsSortDirection]);

  const selectedUploadName = useMemo(() => {
    if (!uploadId) return '';
    const selected = uploads.find((item) => item._id === uploadId);
    return String(selected?.fileName || '').trim();
  }, [uploadId, uploads]);

  const handleRecordsSort = (columnKey: string, direction?: 'asc' | 'desc') => {
    if (direction) {
      setRecordsSortColumn(columnKey);
      setRecordsSortDirection(direction);
      return;
    }
    if (recordsSortColumn !== columnKey) {
      setRecordsSortColumn(columnKey);
      setRecordsSortDirection('asc');
      return;
    }
    setRecordsSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  return (
    <>
      
      <AppToastContainer toasts={toasts} onDismiss={dismissToast} />

      <div className="container py-5">
        {!isClientDetail && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h1 className="mb-0">Analisador de logs SAP</h1>
                <p className="lead mb-0">Cadastre ou selecione um cliente para iniciar a análise de logs.</p>
              </div>
            </div>

            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Clientes do analisador</h5>
                <button className="btn btn-secondary" type="button" onClick={openCreateClientModal}>
                  Novo Cliente
                </button>
              </div>
              <DataTable
                columns={[
                  { key: 'clientId', label: 'Client ID', sortable: true },
                  { key: 'clientName', label: 'Nome', sortable: true },
                  {
                    key: 'source',
                    label: 'Origem',
                    sortable: true,
                    render: (value: any, row: AnalyzerClientRow) => {
                      if (String(row.deletionStage || '').toLowerCase() === 'cancelling') {
                        return <span className="badge bg-warning text-dark">Interrompendo exclusão</span>;
                      }
                      if (String(row.deletionStatus || 'idle') === 'queued') {
                        return <span className="badge bg-secondary">Exclusão na fila</span>;
                      }
                      if (String(row.deletionStatus || 'idle') === 'processing') {
                        return <span className="badge bg-warning text-dark">Excluindo</span>;
                      }
                      const source = String(value || '').toLowerCase();
                      if (source === 'manual') {
                        return <span className="badge bg-secondary">Manual</span>;
                      }
                      if (source === 'profilegen') {
                        return <span className="badge bg-primary">Gerador de Perfis</span>;
                      }
                      return <span className="badge bg-light text-dark">-</span>;
                    }
                  },
                  {
                    key: 'deletionProcessedItems',
                    label: 'Exclusão',
                    sortable: false,
                    render: (_value: any, row: AnalyzerClientRow) => {
                      const deletionStatus = String(row.deletionStatus || 'idle').toLowerCase();
                      if (deletionStatus === 'queued') {
                        return <span className="badge bg-secondary">Na fila</span>;
                      }
                      if (deletionStatus === 'processing') {
                        const processed = Number(row.deletionProcessedItems || 0);
                        const total = Number(row.deletionTotalItems || 1);
                        const progress = getProgressPercents(processed, total);
                        const stageLabel = formatClientDeletionStage(row.deletionStage);
                        return (
                          <div style={{ minWidth: 180 }}>
                            {renderProgressFill(progress.visualPercent)}
                            <div className="small text-muted mt-1">
                              {stageLabel ? `${stageLabel} - ` : ''}{processed.toLocaleString()} / {progress.safeTotal.toLocaleString()} ({progress.realPercent.toFixed(2)}%)
                            </div>
                          </div>
                        );
                      }

                      if (deletionStatus === 'error') {
                        return <span className="badge bg-danger">Erro na exclusão</span>;
                      }

                      return <span className="text-muted">-</span>;
                    }
                  },
                  {
                    key: 'createdByName',
                    label: 'Criado por',
                    sortable: true,
                    render: (value: any) => String(value || '-').trim() || '-'
                  },
                  {
                    key: 'createdAt',
                    label: 'Criado em',
                    sortable: true,
                    render: (value: any) => {
                      if (!value) return '-';
                      const dt = new Date(value);
                      if (Number.isNaN(dt.getTime())) return '-';
                      return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
                    }
                  },
                  {
                    key: 'acoes',
                    label: 'Ações',
                    sortable: false,
                    render: (_value: any, row: AnalyzerClientRow) => (
                      <ActionMenu
                        actions={[
                          
                          {
                            key: 'acessar',
                            label: 'Acessar cliente',
                            icon: Icons.Eye,
                            onClick: () => guardedNavigate(`/analisador-logs-sap/cliente/${row._id}`),
                            disabled: ['processing', 'queued'].includes(String(row.deletionStatus || 'idle'))
                          },
                          {
                            key: 'excluir',
                            label: 'Excluir cliente',
                            icon: Icons.Trash,
                            onClick: () => deleteAnalyzerClient(row),
                            disabled: ['processing', 'queued'].includes(String(row.deletionStatus || 'idle'))
                          }
                        ]}
                      />
                    )
                  }
                ]}
                data={analyzerClients}
                keyExtractor={(row: AnalyzerClientRow) => row._id}
                loading={clientsLoading}
                striped
                hover
                responsive
                pagination={true}
                currentPage={clientsPage}
                pageSize={clientsPageSize}
                totalItems={clientsTotal}
                onPageChange={(nextPage) => loadAnalyzerClients(nextPage, clientsPageSize)}
                onPageSizeChange={(nextPageSize) => loadAnalyzerClients(1, nextPageSize)}
                pageSizeOptions={[10, 20, 50, 100]}
                emptyMessage="Nenhum cliente criado no analisador."
              />
            </div>

            <AppModal
              show={showCreateClientModal}
              title="Novo cliente no analisador"
              onClose={closeCreateClientModal}
              closeDisabled={creatingClient}
              size="lg"
              footer={(
                <>
                  <button type="button" className="btn btn-secondary" onClick={closeCreateClientModal} disabled={creatingClient}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={createAnalyzerClient}
                    disabled={creatingClient}
                  >
                    {creatingClient ? 'Criando...' : 'Criar e acessar cliente'}
                  </button>
                </>
              )}
            >
              <div className="mb-3">
                <label className="form-label">Origem do cliente</label>
                <select
                  className="form-select"
                  value={newClientMode}
                  onChange={(e) => setNewClientMode(e.target.value === 'manual' ? 'manual' : 'profilegen')}
                  disabled={creatingClient}
                >
                  <option value="profilegen">Cliente existente do Gerador de Perfis</option>
                  <option value="manual">Novo cliente manual</option>
                </select>
              </div>

              {newClientMode === 'profilegen' ? (
                <div className="mb-3">
                  <label className="form-label">Cliente do Gerador de Perfis</label>
                  <select
                    className="form-select"
                    value={selectedProfileGenClientId}
                    onChange={(e) => setSelectedProfileGenClientId(e.target.value)}
                    disabled={creatingClient}
                  >
                    <option value="">Selecione um cliente</option>
                    {profileGenClients.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.clientId}{item.clientName ? ` - ${item.clientName}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <label className="form-label">Client ID</label>
                    <input
                      className="form-control"
                      value={manualClientId}
                      onChange={(e) => setManualClientId(e.target.value.toUpperCase())}
                      disabled={creatingClient}
                      placeholder="Ex.: PTP"
                    />
                  </div>
                  <div className="mb-0">
                    <label className="form-label">Nome do cliente</label>
                    <input
                      className="form-control"
                      value={manualClientName}
                      onChange={(e) => setManualClientName(e.target.value)}
                      disabled={creatingClient}
                      placeholder="Opcional"
                    />
                  </div>
                </>
              )}
            </AppModal>
          </>
        )}

        {isClientDetail && (
          <>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h1 className="mb-0">Analisador de logs SAP</h1>
                <p className="lead mb-0">
                  {loadingCurrentClient
                    ? 'Carregando cliente...'
                    : `Cliente: ${currentClient?.clientId || '-'}${currentClient?.clientName ? ` - ${currentClient.clientName}` : ''}`}
                </p>
              </div>
              <button className="btn btn-outline-secondary" type="button" onClick={() => guardedNavigate('/analisador-logs-sap')}>
                Voltar para clientes
              </button>
            </div>

            <StepWizard
              steps={[...ANALYZER_STEP_LABELS]}
              currentStep={analyzerStep}
              onStepChange={(step) => guardedSetAnalyzerStep(step as 1 | 2 | 3)}
              infoItems={[
                { label: 'Cliente (ID):', value: currentClient?.clientId || '-' },
                { label: 'Nome do cliente:', value: currentClient?.clientName || '-' },
                { label: 'Fase atual:', value: ANALYZER_STEP_LABELS[analyzerStep - 1] }
              ]}
            />

            {analyzerStep === 1 ? (
              <>

            <div className="mb-3 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={isUploading}
                onClick={openUploadModal}
              >
                {isUploading ? 'Enviando...' : 'Carregar dados'}
              </button>
            </div>


            <div className="mb-4">
              <h5 className="mb-3">Uploads salvos no cliente</h5>
              <DataTable
                columns={[
                  {
                    key: 'fileName',
                    label: 'Arquivo',
                    sortable: true,
                    render: (value: any, row: UploadRow) => {
                      const isSelected = row._id === uploadId;
                      const safeFileName = String(value || '-').trim() || '-';
                      return (
                        <span className="d-inline-flex align-items-center gap-2">
                          {isSelected && (
                            <span
                              className="text-primary d-inline-flex align-items-center"
                              title="Upload em visualização na tabela de registros abaixo"
                              aria-label="Upload em visualização"
                            >
                              {Icons.Eye}
                            </span>
                          )}
                          <span className="text-muted d-inline-flex align-items-center" title="Arquivo de log">
                            {Icons.FileEarmark}
                          </span>
                          <span className={isSelected ? 'fw-semibold text-primary' : ''}>{safeFileName}</span>
                        </span>
                      );
                    }
                  },
                  {
                    key: 'uploadedByName',
                    label: 'Carregado por',
                    sortable: true,
                    render: (value: any) => String(value || '-').trim() || '-'
                  },
                  { key: 'logType', label: 'Tipo de log', sortable: true },
                  {
                    key: 'totalRecords',
                    label: 'Total de registros',
                    sortable: true,
                    render: (value: any) => Number(value || 0).toLocaleString()
                  },
                  {
                    key: 'status',
                    label: 'Status',
                    sortable: true,
                    render: (value: any, row: UploadRow) => {
                      const status = String(value || '').toLowerCase();
                      const cleanupStatus = String(row.cleanupStatus || 'idle').toLowerCase();

                      return (
                        <div className="d-flex flex-wrap gap-1">
                          {status === 'uploaded' && <span className="badge bg-secondary">1 - Processamento na fila</span>}
                          {status === 'processing' && <span className="badge bg-primary">1 - Processando upload</span>}
                          {status === 'processed' && <span className="badge bg-success">1 - Processamento concluído</span>}
                          {status === 'cancelled' && <span className="badge bg-warning text-dark">1 - Processamento interrompido</span>}
                          {status === 'error' && <span className="badge bg-danger">1 - Falha no processamento</span>}

                          {cleanupStatus === 'queued' && <span className="badge bg-secondary">2 - Higienização na fila</span>}
                          {cleanupStatus === 'processing' && <span className="badge bg-primary">2 - Higienizando dados</span>}
                          {cleanupStatus === 'completed' && <span className="badge bg-success">2 - Higienização concluída</span>}
                          {cleanupStatus === 'cancelled' && <span className="badge bg-warning text-dark">2 - Higienização interrompida</span>}
                          {cleanupStatus === 'error' && <span className="badge bg-danger">2 - Falha na higienização</span>}
                        </div>
                      );
                    }
                  },
                  {
                    key: 'processedRecords',
                    label: 'Progresso',
                    sortable: true,
                    render: (value: any, row: UploadRow) => {
                      if (row.deletionStatus === 'queued') {
                        return <span className="badge bg-secondary">Exclusão na fila</span>;
                      }

                      if (row.deletionStatus === 'processing') {
                        const processed = Number(row.deletionProcessedItems || 0);
                        const total = Number(row.deletionTotalItems || 1);
                        const progress = getProgressPercents(processed, total);
                        return (
                          <div style={{ minWidth: 180 }}>
                            {renderProgressFill(progress.visualPercent)}
                            <div className="small text-muted mt-1">
                              Excluindo... {processed.toLocaleString()} / {progress.safeTotal.toLocaleString()} ({progress.realPercent.toFixed(4)}%)
                            </div>
                          </div>
                        );
                      }

                      const cleanupStatus = String(row.cleanupStatus || 'idle').toLowerCase();
                      const cleanupProcessed = Number(row.cleanupProcessedRecords || 0);
                      const cleanupTotal = Number(row.cleanupTotalRecords || 0);
                      const cleanupActionIndex = Math.max(0, Number(row.cleanupActionIndex || 0));
                      const cleanupActionCount = Math.max(0, Number(row.cleanupActionCount || 0));
                      const cleanupActionLabel = String(row.cleanupActionLabel || '').trim();
                      const cleanupActionProcessed = Number(row.cleanupActionProcessed || cleanupProcessed || 0);
                      const cleanupActionTotal = Number(row.cleanupActionTotal || cleanupTotal || 0);

                      if (cleanupStatus === 'queued') {
                        return <span className="badge bg-secondary">Higienização na fila</span>;
                      }

                      if (cleanupStatus === 'processing') {
                        const progress = getProgressPercents(cleanupActionProcessed, cleanupActionTotal > 0 ? cleanupActionTotal : 1);
                        const stageLabel = formatCleanupStage(row.cleanupStage);
                        const actionPrefix = cleanupActionCount > 0 && cleanupActionIndex > 0
                          ? `Ação ${cleanupActionIndex}/${cleanupActionCount}`
                          : 'Ação';
                        const topLabel = cleanupActionLabel
                          ? `${actionPrefix}: ${cleanupActionLabel}`
                          : (stageLabel ? `${actionPrefix} (${stageLabel})` : actionPrefix);
                        return (
                          <div style={{ minWidth: 190 }}>
                            <div className="small mb-1">{topLabel}</div>
                            {renderProgressFill(progress.visualPercent)}
                            <div className="small text-muted mt-1">{cleanupActionProcessed.toLocaleString()} / {progress.safeTotal.toLocaleString()} ({progress.realPercent.toFixed(4)}%)</div>
                          </div>
                        );
                      }

                      if (cleanupStatus === 'completed') {
                        return <span className="badge bg-success">Higienização concluída</span>;
                      }

                      const processed = Number(value || 0);
                      const estimated = Number(row.estimatedRecords || 0);
                      const total = Number(row.totalRecords || 0);
                      const progressTotal = estimated > 0 ? estimated : (total > 0 ? total : 1);

                      if (row.status === 'processing') {
                        const progress = getProgressPercents(processed, progressTotal);
                        return (
                          <div style={{ minWidth: 180 }}>
                            {renderProgressFill(progress.visualPercent)}
                            <div className="small text-muted mt-1">{processed.toLocaleString()} / {progressTotal.toLocaleString()} ({progress.realPercent.toFixed(4)}%)</div>
                          </div>
                        );
                      }

                      return processed.toLocaleString();
                    }
                  },
                  {
                    key: 'createdAt',
                    label: 'Criado em',
                    sortable: true,
                    render: (value: any) => {
                      if (!value) return '-';
                      const dt = new Date(value);
                      if (Number.isNaN(dt.getTime())) return '-';
                      return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
                    }
                  },
                  {
                    key: 'acoes',
                    label: 'Ações',
                    sortable: false,
                    render: (_value: any, row: UploadRow) => (
                      <ActionMenu
                        actions={[
                          {
                            key: 'visualizar',
                            label: 'Visualizar registros',
                            icon: Icons.Eye,
                            onClick: () => selectUpload(row._id)
                          },
                          {
                            key: 'excluir',
                            label: 'Excluir upload',
                            icon: Icons.Trash,
                            onClick: () => deleteUpload(row._id),
                            disabled: ['processing', 'queued'].includes(String(row.deletionStatus || 'idle')) || !['processed', 'cancelled', 'error'].includes(String(row.status || ''))
                          },
                          {
                            key: 'cancel-upload',
                            label: 'Interromper carga',
                            icon: Icons.Stop,
                            onClick: () => cancelUploadProcessing(row._id),
                            disabled: !(row.status === 'uploaded' || row.status === 'processing')
                          },
                          {
                            key: 'higienizar',
                            label: 'Higienizar dados',
                            icon: Icons.Gear,
                            onClick: () => openCleanupModal(row),
                            disabled: ['processing', 'queued'].includes(String(row.cleanupStatus || 'idle')) || !['processed', 'cancelled'].includes(String(row.status || ''))
                          },
                          {
                            key: 'cancel-cleanup',
                            label: 'Interromper higienização',
                            icon: Icons.Stop,
                            onClick: () => cancelCleanupProcessing(row._id),
                            disabled: row.cleanupStatus !== 'processing'
                          },
                          {
                            key: 'ver-log-upload',
                            label: 'Ver log do upload',
                            icon: Icons.InfoCircle,
                            onClick: () => openActivityModal(row)
                          }
                        ]}
                      />
                    )
                  }
                ]}
                data={uploads}
                keyExtractor={(row: UploadRow) => row._id}
                loading={uploadsLoading}
                filters={uploadsColumnFilters}
                onFilter={(columnKey: string, value: string) => {
                  setUploadsColumnFilters((prev) => ({ ...prev, [columnKey]: value }));
                }}
                striped
                hover
                responsive
                pagination={true}
                currentPage={uploadsPage}
                pageSize={uploadsPageSize}
                totalItems={uploadsTotal}
                onPageChange={(nextPage) => loadUploads(nextPage, uploadsPageSize)}
                onPageSizeChange={(nextPageSize) => loadUploads(1, nextPageSize)}
                pageSizeOptions={[10, 20, 50, 100]}
                emptyMessage="Nenhum upload salvo encontrado para este cliente."
              />
            </div>

            <div>
              <h5 className="mb-3">
                Registros do upload selecionado
                {selectedUploadName ? <span className="ms-2 fw-normal text-muted">— {selectedUploadName}</span> : null}
              </h5>
              <DataTable
                key={`records-${uploadId || 'none'}-${columns.join('|')}`}
                sortColumn={recordsSortColumn}
                sortDirection={recordsSortDirection}
                onSort={handleRecordsSort}
                columns={columns.map((column) => ({
                  key: column,
                  label: column,
                  sortable: true,
                  render: (value: unknown) => formatCell(value)
                }))}
                data={sortedRecordsData}
                keyExtractor={(row: JsonRow & { __index: number }) => String(row.__index)}
                loading={recordsLoading}
                filters={recordsColumnFilters}
                onFilter={(columnKey: string, value: string) => {
                  setRecordsColumnFilters((prev) => ({ ...prev, [columnKey]: value }));
                }}
                striped
                hover
                responsive
                pagination={true}
                currentPage={recordsPage}
                pageSize={recordsPageSize}
                totalItems={recordsTotal}
                onPageChange={(nextPage) => {
                  if (!uploadId) return;
                  loadRecords(uploadId, nextPage, recordsPageSize, { includeTotal: false });
                }}
                onPageSizeChange={(nextPageSize) => {
                  if (!uploadId) return;
                  recordsCacheRef.current.clear();
                  loadRecords(uploadId, 1, nextPageSize, { includeTotal: true });
                }}
                pageSizeOptions={[25, 50, 100, 250, 500]}
                emptyMessage={uploadId ? 'Nenhum registro para este upload.' : 'Selecione um upload salvo para visualizar os registros.'}
              />
            </div>

              </>
            ) : analyzerStep === 2 ? (
              <>
                <div className="d-flex justify-content-end mb-3 gap-2">
                  {currentClient?.source === 'profilegen' && (
                    <>
                      <button
                        type="button"
                        className={`btn ${Number(usorgRowsTotal || 0) > 0 ? 'btn-success' : 'btn-danger'}`}
                        onClick={openUsorgModal}
                      >
                        Carregar USORG
                        <span className="ms-2 badge bg-secondary">
                          {usorgSummaryLoading ? '...' : Number(usorgRowsTotal || 0).toLocaleString()}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={openConsolidationModal}
                      >
                        Gerar consolidação
                      </button>
                    </>
                  )}
                </div>

                {currentClient?.source !== 'profilegen' ? (
                  <div className="alert alert-warning mb-0">
                    Este passo está disponível apenas para clientes originados do Gerador de Perfis.
                  </div>
                ) : (
                  <>
                    <h5 className="mb-3">Consolidação de log para perfis</h5>
                    <DataTable
                      columns={[
                        {
                          key: 'status',
                          label: 'Status',
                          sortable: true,
                          render: (value: any, row: ProfileConsolidationRow) => {
                            const status = String(value || '').toLowerCase();
                            const structureAdjustStatus = String(row?.structureAdjustStatus || 'idle').toLowerCase();
                            const deletionStatus = String(row?.deletionStatus || 'idle').toLowerCase();
                            return (
                              <div className="d-flex flex-wrap gap-1">
                                <span className="badge bg-secondary">1 - Logs selecionados</span>
                                {deletionStatus === 'queued' && (
                                  <span className="badge bg-secondary">Exclusão na fila</span>
                                )}
                                {deletionStatus === 'processing' && (
                                  <span className="badge bg-warning text-dark">Excluindo consolidação</span>
                                )}
                                {structureAdjustStatus === 'queued' && (
                                  <span className="badge bg-secondary">2 - Ajuste na fila</span>
                                )}
                                {structureAdjustStatus === 'processing' && (
                                  <span className="badge bg-primary">2 - Ajustando estrutura</span>
                                )}
                                {structureAdjustStatus === 'cancelled' && (
                                  <span className="badge bg-warning text-dark">2 - Ajuste interrompido</span>
                                )}
                                {status === 'structure_adjusted' && !['queued', 'processing', 'cancelling'].includes(structureAdjustStatus) && (
                                  <span className="badge bg-success">
                                    2 - Estrutura ajustada
                                    {row.structureAdjustedAt && row.structureAdjustStartedAt && (() => {
                                      const mins = Math.round((new Date(row.structureAdjustedAt).getTime() - new Date(row.structureAdjustStartedAt).getTime()) / 60000);
                                      return <> ({mins} min)</>;
                                    })()}
                                  </span>
                                )}
                                {structureAdjustStatus === 'error' && (
                                  <span className="badge bg-danger">2 - Falha no ajuste</span>
                                )}
                              </div>
                            );
                          }
                        },
                        {
                          key: 'structureAdjustProcessedRecords',
                          label: 'Progresso',
                          sortable: false,
                          render: (_value: any, row: ProfileConsolidationRow) => {
                            const deletionStatus = String(row.deletionStatus || 'idle').toLowerCase();
                            if (String(row.deletionStage || '').toLowerCase() === 'cancelling') {
                              return <span className="badge bg-warning text-dark">Interrompendo exclusão</span>;
                            }
                            if (deletionStatus === 'queued') {
                              return <span className="badge bg-secondary">Exclusão na fila</span>;
                            }
                            if (deletionStatus === 'processing') {
                              const processed = Number(row.deletionProcessedItems || 0);
                              const total = Number(row.deletionTotalItems || 1);
                              const progress = getProgressPercents(processed, total);
                              return (
                                <div style={{ minWidth: 180 }}>
                                  {renderProgressFill(progress.visualPercent)}
                                  <div className="small text-muted mt-1">
                                    Excluindo... {processed.toLocaleString()} / {progress.safeTotal.toLocaleString()} ({progress.realPercent.toFixed(4)}%)
                                  </div>
                                </div>
                              );
                            }

                            if (deletionStatus === 'error') {
                              return <span className="badge bg-danger">Erro na exclusão</span>;
                            }

                            const adjustStatus = String(row.structureAdjustStatus || 'idle').toLowerCase();
                            const userProcessed = Math.max(0, Number(row.userConsolidationProcessedUsers || 0));
                            const userTotalRaw = Math.max(0, Number(row.userConsolidationTotalUsers || 0));
                            const userTotal = userTotalRaw > 0 ? userTotalRaw : 1;
                            const userProgress = getProgressPercents(userProcessed, userTotal);
                            const hasUserConsolidationProgress = userTotalRaw > 0
                              || String(row.structureAdjustStage || '').toLowerCase().includes('user_consolidation');
                            const failedUsers = Math.max(0, Number(row.userConsolidationFailedUsers || 0));
                            const consolidationMode = String(row.userConsolidationMode || '').trim().toLowerCase();
                            const stageKey = String(row.structureAdjustStage || '').toLowerCase();
                            const actionLabel = String(row.structureAdjustActionLabel || '').trim();
                            const actionLabelNormalized = actionLabel.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                            const isUserCodeConsolidation = consolidationMode === 'user_code'
                              || stageKey.includes('user_code_consolidation')
                              || actionLabelNormalized.includes('v2')
                              || actionLabelNormalized.includes('usuario+codigo');

                            if (adjustStatus === 'queued') {
                              return <span className="badge bg-secondary">Ajuste na fila</span>;
                            }

                            if (adjustStatus === 'processing') {
                              const safeTotalRaw = userTotalRaw > 0 ? userTotalRaw : 0;
                              return (
                                <div style={{ minWidth: 180 }}>
                                  <div className="small mb-1 fw-semibold">
                                    {isUserCodeConsolidation ? 'Usuários e Codes concluídos' : 'Usuários concluídos'}: {userProcessed.toLocaleString()} / {safeTotalRaw.toLocaleString()} ({hasUserConsolidationProgress ? userProgress.realPercent.toFixed(2) : '0.00'}%)
                                    {failedUsers > 0 ? ` | Falhas: ${failedUsers.toLocaleString()}` : ''}
                                  </div>
                                  {renderProgressFill(hasUserConsolidationProgress ? userProgress.visualPercent : 0)}
                                </div>
                              );
                            }

                            if (String(row.status || '').toLowerCase() === 'structure_adjusted' || adjustStatus === 'completed') {
                              return <span className="badge bg-success">Concluído</span>;
                            }

                            if (adjustStatus === 'error') {
                              return <span className="badge bg-danger">Erro</span>;
                            }

                            if (adjustStatus === 'cancelled') {
                              return <span className="badge bg-warning text-dark">Interrompido</span>;
                            }

                            return <span className="text-muted">-</span>;
                          }
                        },
                        {
                          key: 'uploadCount',
                          label: 'Qtd. logs',
                          sortable: true,
                          render: (value: any) => Number(value || 0).toLocaleString()
                        },
                        {
                          key: 'uploadFileNames',
                          label: 'Arquivos considerados',
                          sortable: false,
                          render: (value: any) => {
                            const names = Array.isArray(value) ? value.filter(Boolean) : [];
                            if (names.length === 0) return '-';
                            return (
                              <div className="d-flex flex-column gap-1">
                                {names.map((name: string, index: number) => (
                                  <span
                                    key={`${name}-${index}`}
                                    className="d-inline-flex align-items-center gap-2"
                                    title={name}
                                  >
                                    <span className="text-muted d-inline-flex align-items-center">{Icons.FileEarmark}</span>
                                    <span>{name}</span>
                                  </span>
                                ))}
                              </div>
                            );
                          }
                        },
                        {
                          key: 'createdByName',
                          label: 'Criado por',
                          sortable: true,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'createdAt',
                          label: 'Criado em',
                          sortable: true,
                          render: (value: any) => {
                            if (!value) return '-';
                            const dt = new Date(value);
                            if (Number.isNaN(dt.getTime())) return '-';
                            return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
                          }
                        },
                        {
                          key: 'acoes',
                          label: 'Ações',
                          sortable: false,
                          render: (_value: any, row: ProfileConsolidationRow) => (
                            <ActionMenu
                              actions={[
                                {
                                  key: 'consolidacao-logs',
                                  label: 'Consolidação de logs',
                                  icon: Icons.Gear,
                                  onClick: () => runLogConsolidationByUser(row),
                                  disabled: ['processing', 'queued'].includes(String(row.structureAdjustStatus || 'idle')) || ['processing', 'queued'].includes(String(row.deletionStatus || 'idle'))
                                },
                                {
                                  key: 'cancelar-ajuste-estrutura-log',
                                  label: 'Interromper consolidação de logs',
                                  icon: Icons.Stop,
                                  onClick: () => cancelProfileConsolidationStructure(row),
                                  disabled: !['processing', 'queued'].includes(String(row.structureAdjustStatus || 'idle')) || ['processing', 'queued'].includes(String(row.deletionStatus || 'idle'))
                                },
                                {
                                  key: 'visualizar-estrutura-ajustada',
                                  label: 'Visualizar estrutura ajustada',
                                  icon: Icons.Eye,
                                  onClick: () => openAdjustedStructureModal(row),
                                  disabled: ['processing', 'queued'].includes(String(row.deletionStatus || 'idle'))
                                },
                                {
                                  key: 'visualizar-log-consolidacao',
                                  label: 'Visualizar log da consolidação',
                                  icon: Icons.InfoCircle,
                                  onClick: () => openConsolidationActivityModal(row),
                                  disabled: ['processing', 'queued'].includes(String(row.deletionStatus || 'idle'))
                                },
                                {
                                  key: 'excluir-consolidacao',
                                  label: 'Excluir consolidação',
                                  icon: Icons.Trash,
                                  onClick: () => deleteProfileConsolidation(row),
                                  disabled: ['processing', 'queued'].includes(String(row.deletionStatus || 'idle'))
                                }
                              ]}
                            />
                          )
                        }
                      ]}
                      data={profileConsolidations}
                      keyExtractor={(row: ProfileConsolidationRow) => row._id}
                      loading={profileConsolidationsLoading}
                      striped
                      hover
                      responsive
                      pagination={true}
                      currentPage={profileConsolidationsPage}
                      pageSize={profileConsolidationsPageSize}
                      totalItems={profileConsolidationsTotal}
                      onPageChange={(nextPage) => loadProfileConsolidations(nextPage, profileConsolidationsPageSize)}
                      onPageSizeChange={(nextPageSize) => loadProfileConsolidations(1, nextPageSize)}
                      pageSizeOptions={[10, 20, 50, 100]}
                      emptyMessage="Nenhuma consolidação gerada para este cliente."
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <div className="d-flex justify-content-end mb-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={openLoadFileGenerationModal}
                    disabled={currentClient?.source !== 'profilegen'}
                  >
                    Gerar arquivos de carga
                  </button>
                </div>

                {currentClient?.source !== 'profilegen' ? (
                  <div className="alert alert-warning mb-3">
                    A opção 1 (base do Gerador de Perfis) está indisponível para clientes manuais. A opção 2 será disponibilizada em uma próxima etapa.
                  </div>
                ) : null}

                <h5 className="mb-3">Gerações de arquivos de carga</h5>
                <DataTable
                  columns={[
                    {
                      key: 'status',
                      label: 'Status',
                      sortable: true,
                      render: (value: any) => {
                        const status = String(value || '').toLowerCase();
                        if (status === 'completed') return <span className="badge bg-success">Concluído</span>;
                        if (status === 'failed') return <span className="badge bg-danger">Falhou</span>;
                        if (status === 'cancelled') return <span className="badge bg-warning text-dark">Cancelado</span>;
                        if (status === 'queued') return <span className="badge bg-secondary">Na fila</span>;
                        if (status === 'processing') return <span className="badge bg-primary">Processando</span>;
                        return <span className="badge bg-secondary">{status || 'unknown'}</span>;
                      }
                    },
                    {
                      key: 'progress',
                      label: 'Progresso',
                      sortable: true,
                      render: (value: any, row: LoadFileGenerationExecutionRow) => {
                        const status = String(row.status || '').toLowerCase();
                        const progress = Math.max(0, Math.min(100, Number(value || 0)));
                        if (['queued', 'processing'].includes(status)) {
                          return (
                            <div style={{ minWidth: 180 }}>
                              {renderProgressFill(progress)}
                              <div className="small text-muted mt-1">
                                {progress.toFixed(0)}%{row.progressMessage ? ` - ${row.progressMessage}` : ''}
                              </div>
                            </div>
                          );
                        }
                        if (status === 'failed') {
                          return <span className="text-danger">{String(row.errorMessage || 'erro na execução')}</span>;
                        }
                        return <span>{progress.toFixed(0)}%</span>;
                      }
                    },
                    {
                      key: 'totalGeneratedRows',
                      label: 'Linhas geradas',
                      sortable: true,
                      render: (value: any) => Number(value || 0).toLocaleString()
                    },
                    {
                      key: 'profilesCount',
                      label: 'Perfis',
                      sortable: true,
                      render: (value: any) => Number(value || 0).toLocaleString()
                    },
                    {
                      key: 'createdByName',
                      label: 'Executado por',
                      sortable: true,
                      render: (value: any) => String(value || '-').trim() || '-'
                    },
                    {
                      key: 'createdAt',
                      label: 'Criado em',
                      sortable: true,
                      render: (value: any) => {
                        if (!value) return '-';
                        const dt = new Date(value);
                        if (Number.isNaN(dt.getTime())) return '-';
                        return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
                      }
                    },
                    {
                      key: 'acoes',
                      label: 'Ações',
                      sortable: false,
                      render: (_value: any, row: LoadFileGenerationExecutionRow) => (
                        <ActionMenu
                          actions={[
                            {
                              key: 'visualizar-carga-consolidada',
                              label: 'Visualizar carga consolidada',
                              icon: Icons.Eye,
                              onClick: () => viewLoadGenerationExecution(row),
                              disabled: String(row.status || '').toLowerCase() !== 'completed'
                            },
                            {
                              key: 'download-arquivos-carga',
                              label: 'Realizar download de arquivos de carga',
                              icon: Icons.FileEarmark,
                              onClick: () => openDownloadModal(row),
                              disabled: String(row.status || '').toLowerCase() !== 'completed'
                            },
                            {
                              key: 'excluir-geracao-carga',
                              label: 'Excluir geração de carga',
                              icon: Icons.Trash,
                              onClick: () => deleteLoadGenerationExecution(row),
                              disabled: ['queued', 'processing'].includes(String(row.status || '').toLowerCase())
                            }
                          ]}
                        />
                      )
                    }
                  ]}
                  data={loadGenerationExecutions}
                  keyExtractor={(row: LoadFileGenerationExecutionRow) => row._id}
                  loading={loadGenerationExecutionsLoading}
                  striped
                  hover
                  responsive
                  pagination={true}
                  currentPage={loadGenerationExecutionsPage}
                  pageSize={loadGenerationExecutionsPageSize}
                  totalItems={loadGenerationExecutionsTotal}
                  onPageChange={(nextPage) => loadLoadGenerationExecutions(nextPage, loadGenerationExecutionsPageSize)}
                  onPageSizeChange={(nextPageSize) => loadLoadGenerationExecutions(1, nextPageSize)}
                  pageSizeOptions={[10, 20, 50, 100]}
                  emptyMessage="Nenhuma geração de arquivo de carga encontrada para este cliente."
                />
              </>
            )}

            <AppModal
              show={showLoadFileGenerationModal}
              title="Geração de arquivos de carga"
              onClose={closeLoadFileGenerationModal}
              closeDisabled={loadGenerationRunning}
              size="xl"
              footer={(
                <>
                  <button type="button" className="btn btn-secondary" onClick={() => closeLoadFileGenerationModal()} disabled={loadGenerationRunning}>
                    Fechar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={runLoadFileGeneration}
                    disabled={loadGenerationRunning || loadGenerationSourceOption !== 'profilegen' || !selectedLoadGenerationConsolidationId}
                  >
                    {loadGenerationRunning ? 'Gerando...' : 'Iniciar geração'}
                  </button>
                </>
              )}
            >
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Consolidações disponíveis (estrutura ajustada)</label>
                  <DataTable
                    columns={[
                      {
                        key: '_select',
                        label: 'Sel.',
                        sortable: false,
                        render: (_value: any, row: ProfileConsolidationRow) => (
                          <input
                            type="radio"
                            className="form-check-input"
                            name="load-generation-consolidation"
                            checked={selectedLoadGenerationConsolidationId === row._id}
                            onChange={() => setSelectedLoadGenerationConsolidationId(row._id)}
                            disabled={loadGenerationRunning}
                          />
                        )
                      },
                      {
                        key: 'createdAt',
                        label: 'Data/hora',
                        sortable: true,
                        render: (value: any) => {
                          if (!value) return '-';
                          const dt = new Date(String(value));
                          if (Number.isNaN(dt.getTime())) return '-';
                          return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
                        }
                      },
                      {
                        key: 'uploadCount',
                        label: 'Qtd. logs',
                        sortable: true,
                        render: (value: any) => Math.max(0, Number(value || 0)).toLocaleString('pt-BR')
                      },
                      {
                        key: 'structuredRowsTotal',
                        label: 'Linhas estruturadas',
                        sortable: true,
                        render: (value: any) => Math.max(0, Number(value || 0)).toLocaleString('pt-BR')
                      },
                      {
                        key: 'createdByName',
                        label: 'Consolidado por',
                        sortable: true,
                        render: (value: any) => String(value || '-').trim() || '-'
                      },
                      {
                        key: 'structureAdjustStatus',
                        label: 'Status',
                        sortable: true,
                        render: (value: any) => {
                          const status = String(value || '').trim().toLowerCase();
                          if (status === 'completed') return <span className="badge bg-success">Concluída</span>;
                          if (status === 'processing') return <span className="badge bg-primary">Processando</span>;
                          if (status === 'queued') return <span className="badge bg-info text-dark">Na fila</span>;
                          if (status === 'error') return <span className="badge bg-danger">Erro</span>;
                          if (status === 'cancelled') return <span className="badge bg-warning text-dark">Cancelada</span>;
                          return <span className="badge bg-secondary">{status || 'n/d'}</span>;
                        }
                      }
                    ]}
                    data={loadGenerationEligibleConsolidations}
                    keyExtractor={(row: ProfileConsolidationRow) => row._id}
                    onRowClick={(row: ProfileConsolidationRow) => {
                      if (loadGenerationRunning) return;
                      setSelectedLoadGenerationConsolidationId(row._id);
                    }}
                    striped
                    hover
                    responsive
                    clientSidePagination={true}
                    pagination={true}
                    pageSize={5}
                    pageSizeOptions={[5, 10, 20, 50]}
                    totalItems={loadGenerationEligibleConsolidations.length}
                    emptyMessage="Nenhuma consolidação com estrutura ajustada disponível."
                  />
                </div>

                {selectedLoadGenerationConsolidation && (
                  <div className="col-12">
                    <div className="border rounded-3 p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Detalhes da consolidação selecionada</h6>
                        <span className="small text-muted">ID: {selectedLoadGenerationConsolidation._id}</span>
                      </div>

                      <div className="row g-2 mb-2">
                        <div className="col-12 col-md-3">
                          <div className="small text-muted">Criada em</div>
                          <div>
                            {selectedLoadGenerationConsolidation.createdAt
                              ? new Date(String(selectedLoadGenerationConsolidation.createdAt)).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                              : '-'}
                          </div>
                        </div>
                        <div className="col-12 col-md-3">
                          <div className="small text-muted">Ajustada em</div>
                          <div>
                            {selectedLoadGenerationConsolidation.structureAdjustedAt
                              ? new Date(String(selectedLoadGenerationConsolidation.structureAdjustedAt)).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                              : '-'}
                          </div>
                        </div>
                        <div className="col-12 col-md-3">
                          <div className="small text-muted">Consolidado por</div>
                          <div>{String(selectedLoadGenerationConsolidation.createdByName || '-').trim() || '-'}</div>
                        </div>
                        <div className="col-12 col-md-3">
                          <div className="small text-muted">Qtd. logs</div>
                          <div>{Math.max(0, Number(selectedLoadGenerationConsolidation.uploadCount || 0)).toLocaleString('pt-BR')}</div>
                        </div>
                      </div>

                      <div className="small text-muted mb-1">Arquivos de log consolidados</div>
                      {Array.isArray(selectedLoadGenerationConsolidation.uploadFileNames) && selectedLoadGenerationConsolidation.uploadFileNames.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {selectedLoadGenerationConsolidation.uploadFileNames.map((fileName, index) => (
                            <span key={`${fileName}-${index}`} className="badge text-bg-light border">
                              {String(fileName || '').trim() || '-'}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="small text-muted">Não há nomes de arquivos disponíveis para esta consolidação.</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="col-12 col-lg-6">
                  <label className="form-label">Origem da base</label>
                  <div className="border rounded-2 p-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="load-source-profilegen"
                        name="load-source"
                        checked={loadGenerationSourceOption === 'profilegen'}
                        onChange={() => setLoadGenerationSourceOption('profilegen')}
                        disabled={loadGenerationRunning || currentClient?.source !== 'profilegen'}
                      />
                      <label className="form-check-label" htmlFor="load-source-profilegen">
                        Opção 1: Base do gerador de perfis (implementada)
                      </label>
                    </div>
                    <div className="form-check mt-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="load-source-upload"
                        name="load-source"
                        checked={loadGenerationSourceOption === 'upload'}
                        onChange={() => setLoadGenerationSourceOption('upload')}
                        disabled
                      />
                      <label className="form-check-label text-muted" htmlFor="load-source-upload">
                        Opção 2: Base por carga (em breve)
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </AppModal>

            <AppModal
              show={showLoadGenerationResultModal}
              title={loadGenerationResultTitle || 'Carga consolidada'}
              onClose={closeLoadGenerationResultModal}
              size="xl"
              footer={(
                <>
                  <div className="me-auto small text-muted align-self-center">
                    Total gerado: {(loadGenerationResultActiveTab === 'authorization' ? loadGenerationTotalGeneratedRows : loadGenerationTotalOrgLevelRows).toLocaleString()}
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={closeLoadGenerationResultModal}>
                    Fechar
                  </button>
                </>
              )}
            >
              <ModalTabs
                activeKey={loadGenerationResultActiveTab}
                onChange={setLoadGenerationResultActiveTab}
                items={[
                  {
                    key: 'authorization',
                    label: 'Carga de autorização',
                    icon: Icons.FileEarmark,
                  },
                  {
                    key: 'orgLevels',
                    label: 'Carga de níveis organizacionais',
                    icon: Icons.Funnel,
                    onSelect: () => {
                      if (loadGenerationOrgLevelRows.length === 0 && !loadGenerationOrgLevelLoading) {
                        loadLoadGenerationOrgLevelRows({
                          targetPage: 1,
                          targetPageSize: loadGenerationOrgLevelPageSize,
                          sortColumn: null,
                          sortDirection: 'asc',
                          filters: {},
                        }).catch(() => undefined);
                      }
                    },
                  },
                ]}
              />

              {loadGenerationResultActiveTab === 'authorization' ? (
                <>
                  <div className="small text-muted mb-2">
                    Clique em uma linha para visualizar as origens consideradas na relação do objeto com o perfil.
                  </div>
                  {loadGenerationResultLoading && (
                    <div className="mb-3">
                      <div className="progress" style={{ height: '8px' }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          style={{ width: '100%' }}
                          aria-label="Carregando linhas consolidadas"
                        />
                      </div>
                      <div className="small text-muted mt-1">Carregando linhas consolidadas...</div>
                    </div>
                  )}
                  <DataTable
                    columns={[
                      { key: 'profileName', label: 'Perfil', sortable: true },
                      { key: 'object', label: 'Objeto', sortable: true },
                      { key: 'numProfile', label: 'Versão', sortable: true, render: (value: any) => String(value || '-').trim() || '-' },
                      { key: 'field', label: 'Campo', sortable: true },
                      { key: 'valueFrom', label: 'Valor de', sortable: true },
                      { key: 'valueTo', label: 'Valor até', sortable: true },
                    ]}
                    data={loadGenerationResultRows}
                    keyExtractor={(row: LoadFileConsolidatedViewRow) => `${row.profileName}-${row.object}-${row.numProfile}-${row.field}-${row.valueFrom}-${row.valueTo}`}
                    striped
                    hover
                    responsive
                    onRowClick={(row: LoadFileConsolidatedViewRow) => openLoadGenerationRowOriginsModal(row)}
                    loading={loadGenerationResultLoading}
                    sortColumn={loadGenerationResultSortColumn}
                    sortDirection={loadGenerationResultSortDirection}
                    onSort={(column, direction) => {
                      const nextDirection = direction || (loadGenerationResultSortColumn === column && loadGenerationResultSortDirection === 'asc' ? 'desc' : 'asc');
                      setLoadGenerationResultSortColumn(column);
                      setLoadGenerationResultSortDirection(nextDirection);
                      loadLoadGenerationResultRows({ targetPage: 1, targetPageSize: loadGenerationResultPageSize, sortColumn: column, sortDirection: nextDirection, filters: loadGenerationResultFilters }).catch(() => undefined);
                    }}
                    onClearSort={() => {
                      setLoadGenerationResultSortColumn(null);
                      setLoadGenerationResultSortDirection('asc');
                      loadLoadGenerationResultRows({ targetPage: 1, targetPageSize: loadGenerationResultPageSize, sortColumn: null, sortDirection: 'asc', filters: loadGenerationResultFilters }).catch(() => undefined);
                    }}
                    onFilter={(columnKey, value) => {
                      const nextFilters = { ...loadGenerationResultFilters, [columnKey]: value };
                      setLoadGenerationResultFilters(nextFilters);
                      loadLoadGenerationResultRows({ targetPage: 1, targetPageSize: loadGenerationResultPageSize, sortColumn: loadGenerationResultSortColumn, sortDirection: loadGenerationResultSortDirection, filters: nextFilters }).catch(() => undefined);
                    }}
                    onClearFilters={() => {
                      setLoadGenerationResultFilters({});
                      loadLoadGenerationResultRows({ targetPage: 1, targetPageSize: loadGenerationResultPageSize, sortColumn: loadGenerationResultSortColumn, sortDirection: loadGenerationResultSortDirection, filters: {} }).catch(() => undefined);
                    }}
                    filters={loadGenerationResultFilters}
                    pagination={true}
                    currentPage={loadGenerationResultPage}
                    pageSize={loadGenerationResultPageSize}
                    totalItems={loadGenerationTotalGeneratedRows}
                    onPageChange={(nextPage) => {
                      loadLoadGenerationResultRows({ targetPage: nextPage, targetPageSize: loadGenerationResultPageSize, sortColumn: loadGenerationResultSortColumn, sortDirection: loadGenerationResultSortDirection, filters: loadGenerationResultFilters }).catch(() => undefined);
                    }}
                    onPageSizeChange={(nextPageSize) => {
                      loadLoadGenerationResultRows({ targetPage: 1, targetPageSize: nextPageSize, sortColumn: loadGenerationResultSortColumn, sortDirection: loadGenerationResultSortDirection, filters: loadGenerationResultFilters }).catch(() => undefined);
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    emptyMessage="Nenhuma linha consolidada encontrada para esta geração."
                  />
                </>
              ) : (
                <>
                  <div className="small text-muted mb-2">
                    Valores de níveis organizacionais materializados por perfil a partir dos campos USORG carregados para o cliente.
                  </div>
                  {loadGenerationOrgLevelLoading && (
                    <div className="mb-3">
                      <div className="progress" style={{ height: '8px' }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          style={{ width: '100%' }}
                          aria-label="Carregando níveis organizacionais"
                        />
                      </div>
                      <div className="small text-muted mt-1">Carregando níveis organizacionais...</div>
                    </div>
                  )}
                  <DataTable
                    columns={[
                      { key: 'profileName', label: 'Perfil', sortable: true },
                      { key: 'field', label: 'Campo', sortable: true },
                      { key: 'valueFrom', label: 'Valor de', sortable: true },
                      { key: 'valueTo', label: 'Valor até', sortable: true },
                    ]}
                    data={loadGenerationOrgLevelRows}
                    keyExtractor={(row: LoadFileOrgLevelViewRow) => `${row.profileName}-${row.field}-${row.valueFrom}-${row.valueTo}`}
                    striped
                    hover
                    responsive
                    loading={loadGenerationOrgLevelLoading}
                    sortColumn={loadGenerationOrgLevelSortColumn}
                    sortDirection={loadGenerationOrgLevelSortDirection}
                    onSort={(column, direction) => {
                      const nextDirection = direction || (loadGenerationOrgLevelSortColumn === column && loadGenerationOrgLevelSortDirection === 'asc' ? 'desc' : 'asc');
                      setLoadGenerationOrgLevelSortColumn(column);
                      setLoadGenerationOrgLevelSortDirection(nextDirection);
                      loadLoadGenerationOrgLevelRows({ targetPage: 1, targetPageSize: loadGenerationOrgLevelPageSize, sortColumn: column, sortDirection: nextDirection, filters: loadGenerationOrgLevelFilters }).catch(() => undefined);
                    }}
                    onClearSort={() => {
                      setLoadGenerationOrgLevelSortColumn(null);
                      setLoadGenerationOrgLevelSortDirection('asc');
                      loadLoadGenerationOrgLevelRows({ targetPage: 1, targetPageSize: loadGenerationOrgLevelPageSize, sortColumn: null, sortDirection: 'asc', filters: loadGenerationOrgLevelFilters }).catch(() => undefined);
                    }}
                    onFilter={(columnKey, value) => {
                      const nextFilters = { ...loadGenerationOrgLevelFilters, [columnKey]: value };
                      setLoadGenerationOrgLevelFilters(nextFilters);
                      loadLoadGenerationOrgLevelRows({ targetPage: 1, targetPageSize: loadGenerationOrgLevelPageSize, sortColumn: loadGenerationOrgLevelSortColumn, sortDirection: loadGenerationOrgLevelSortDirection, filters: nextFilters }).catch(() => undefined);
                    }}
                    onClearFilters={() => {
                      setLoadGenerationOrgLevelFilters({});
                      loadLoadGenerationOrgLevelRows({ targetPage: 1, targetPageSize: loadGenerationOrgLevelPageSize, sortColumn: loadGenerationOrgLevelSortColumn, sortDirection: loadGenerationOrgLevelSortDirection, filters: {} }).catch(() => undefined);
                    }}
                    filters={loadGenerationOrgLevelFilters}
                    pagination={true}
                    currentPage={loadGenerationOrgLevelPage}
                    pageSize={loadGenerationOrgLevelPageSize}
                    totalItems={loadGenerationTotalOrgLevelRows}
                    onPageChange={(nextPage) => {
                      loadLoadGenerationOrgLevelRows({ targetPage: nextPage, targetPageSize: loadGenerationOrgLevelPageSize, sortColumn: loadGenerationOrgLevelSortColumn, sortDirection: loadGenerationOrgLevelSortDirection, filters: loadGenerationOrgLevelFilters }).catch(() => undefined);
                    }}
                    onPageSizeChange={(nextPageSize) => {
                      loadLoadGenerationOrgLevelRows({ targetPage: 1, targetPageSize: nextPageSize, sortColumn: loadGenerationOrgLevelSortColumn, sortDirection: loadGenerationOrgLevelSortDirection, filters: loadGenerationOrgLevelFilters }).catch(() => undefined);
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    emptyMessage="Nenhum nível organizacional encontrado para esta geração."
                  />
                </>
              )}
            </AppModal>

            <AppModal
              show={showLoadGenerationRowOriginsModal}
              title="Origens consideradas"
              onClose={() => {
                setShowLoadGenerationRowOriginsModal(false);
                setSelectedLoadGenerationOriginRow(null);
                setLoadGenerationOriginRows([]);
                setLoadGenerationOriginSortColumn(null);
                setLoadGenerationOriginSortDirection('asc');
                setLoadGenerationOriginFilters({});
                setLoadGenerationOriginPairsPage(1);
              }}
              size="xl"
              footer={(
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowLoadGenerationRowOriginsModal(false);
                    setSelectedLoadGenerationOriginRow(null);
                    setLoadGenerationOriginRows([]);
                    setLoadGenerationOriginSortColumn(null);
                    setLoadGenerationOriginSortDirection('asc');
                    setLoadGenerationOriginFilters({});
                    setLoadGenerationOriginPairsPage(1);
                  }}
                >
                  Fechar
                </button>
              )}
            >
              {!selectedLoadGenerationOriginRow ? (
                <div className="text-muted">Nenhum detalhe de origem encontrado para esta linha.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  <div className="border rounded-3 p-3">
                    <div className="small text-muted mb-1">Escopo da origem (objeto completo)</div>
                    <div className="fw-semibold">
                      Perfil: {selectedLoadGenerationOriginRow.profileName || '-'} | Objeto: {selectedLoadGenerationOriginRow.object || '-'} | Campo: {selectedLoadGenerationOriginRow.field || '-'}
                    </div>
                    <div className="small text-muted mt-1">
                      A origem é calculada no nível do objeto inteiro. Clicar em qualquer linha do mesmo objeto exibe o mesmo conjunto de origens.
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-3">
                      <div className="border rounded-3 p-3 h-100">
                        <div className="small text-muted mb-1">Usuários considerados</div>
                        <div className="fw-semibold">{loadGenerationOriginUsersCount.toLocaleString()}</div>
                        <div className="small text-muted mt-2">
                          Total distinto de usuários vinculados ao perfil para este objeto.
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="border rounded-3 p-3 h-100">
                        <div className="small text-muted mb-1">Codes considerados</div>
                        <div className="fw-semibold">{loadGenerationOriginCodesCount.toLocaleString()}</div>
                        <div className="small text-muted mt-2">
                          Total distinto de codes vinculados ao perfil para este objeto.
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="border rounded-3 p-3 h-100">
                        <div className="small text-muted mb-1">Pares usuário/code</div>
                        <div className="fw-semibold">{loadGenerationOriginTotalPairs.toLocaleString()}</div>
                        <div className="small text-muted mt-2">
                          Clique na tabela abaixo para navegar pelas origens consideradas.
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="border rounded-3 p-3 h-100">
                        <div className="small text-muted mb-1">Versões do objeto</div>
                        <div className="fw-semibold">{loadGenerationOriginTotalVersions.toLocaleString()}</div>
                        <div className="small text-muted mt-2">
                          {loadGenerationOriginTotalVersions > 1 
                            ? `Este objeto tem ${loadGenerationOriginTotalVersions} versões distintas (segregadas).` 
                            : 'Uma única versão de origem para este objeto.'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-3 p-3">
                    <div className="small text-muted mb-2">Pares de origem considerados</div>
                    {loadGenerationOriginLoading && (
                      <div className="mb-3">
                        <div className="progress" style={{ height: '8px' }}>
                          <div
                            className="progress-bar progress-bar-striped progress-bar-animated"
                            role="progressbar"
                            style={{ width: '100%' }}
                            aria-label="Carregando origens"
                          />
                        </div>
                        <div className="small text-muted mt-1">Carregando origens...</div>
                      </div>
                    )}
                    <DataTable
                      columns={[
                        {
                          key: 'numProfile',
                          label: 'Versão',
                          sortable: true,
                          render: (value: any) => {
                            const numProfile = String(value || '').trim();
                            if (!numProfile) return '-';
                            return numProfile;
                          }
                        },
                        {
                          key: 'user',
                          label: 'Usuário',
                          sortable: true,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'code',
                          label: 'Code',
                          sortable: true,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'object',
                          label: 'Objeto',
                          sortable: true,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'field',
                          label: 'Campo',
                          sortable: true,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'valueFrom',
                          label: 'Valor de',
                          sortable: true,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'valueTo',
                          label: 'Valor até',
                          sortable: true,
                          render: (value: any) => String(value || '-').trim() || '-'
                        }
                      ]}
                      data={loadGenerationOriginRows}
                      keyExtractor={(row: LoadFileGenerationOriginRow, index: number) => `${row.numProfile}-${row.user}-${row.code}-${row.object}-${row.field}-${row.valueFrom}-${row.valueTo}-${index}`}
                      loading={loadGenerationOriginLoading}
                      sortColumn={loadGenerationOriginSortColumn}
                      sortDirection={loadGenerationOriginSortDirection}
                      onSort={(column, direction) => {
                        const nextDirection = direction || (loadGenerationOriginSortColumn === column && loadGenerationOriginSortDirection === 'asc' ? 'desc' : 'asc');
                        setLoadGenerationOriginSortColumn(column);
                        setLoadGenerationOriginSortDirection(nextDirection);
                        if (!selectedLoadGenerationOriginRow) return;
                        loadLoadGenerationRowOrigins(selectedLoadGenerationOriginRow, 1, loadGenerationOriginPairsPageSize, column, nextDirection, loadGenerationOriginFilters).catch(() => undefined);
                      }}
                      onClearSort={() => {
                        setLoadGenerationOriginSortColumn(null);
                        setLoadGenerationOriginSortDirection('asc');
                        if (!selectedLoadGenerationOriginRow) return;
                        loadLoadGenerationRowOrigins(selectedLoadGenerationOriginRow, 1, loadGenerationOriginPairsPageSize, null, 'asc', loadGenerationOriginFilters).catch(() => undefined);
                      }}
                      onFilter={(columnKey, value) => {
                        const nextFilters = { ...loadGenerationOriginFilters, [columnKey]: value };
                        setLoadGenerationOriginFilters(nextFilters);
                        if (!selectedLoadGenerationOriginRow) return;
                        loadLoadGenerationRowOrigins(selectedLoadGenerationOriginRow, 1, loadGenerationOriginPairsPageSize, loadGenerationOriginSortColumn, loadGenerationOriginSortDirection, nextFilters).catch(() => undefined);
                      }}
                      onClearFilters={() => {
                        setLoadGenerationOriginFilters({});
                        if (!selectedLoadGenerationOriginRow) return;
                        loadLoadGenerationRowOrigins(selectedLoadGenerationOriginRow, 1, loadGenerationOriginPairsPageSize, loadGenerationOriginSortColumn, loadGenerationOriginSortDirection, {}).catch(() => undefined);
                      }}
                      filters={loadGenerationOriginFilters}
                      striped
                      hover
                      responsive
                      pagination={true}
                      currentPage={loadGenerationOriginPairsPage}
                      pageSize={loadGenerationOriginPairsPageSize}
                      totalItems={loadGenerationOriginTotalItems}
                      onPageChange={(nextPage) => {
                        if (!selectedLoadGenerationOriginRow) return;
                        loadLoadGenerationRowOrigins(
                          selectedLoadGenerationOriginRow,
                          nextPage,
                          loadGenerationOriginPairsPageSize,
                          loadGenerationOriginSortColumn,
                          loadGenerationOriginSortDirection,
                          loadGenerationOriginFilters
                        ).catch(() => undefined);
                      }}
                      onPageSizeChange={(nextPageSize) => {
                        if (!selectedLoadGenerationOriginRow) return;
                        loadLoadGenerationRowOrigins(
                          selectedLoadGenerationOriginRow,
                          1,
                          nextPageSize,
                          loadGenerationOriginSortColumn,
                          loadGenerationOriginSortDirection,
                          loadGenerationOriginFilters
                        ).catch(() => undefined);
                      }}
                      pageSizeOptions={[10, 25, 50, 100]}
                      emptyMessage="Nenhum par de origem registrado."
                    />
                  </div>
                </div>
              )}
            </AppModal>

            <AppModal
              show={showUploadModal}
              title="Carregar dados do log"
              onClose={closeUploadModal}
              closeDisabled={isUploading}
              size="lg"
              footer={(
                <>
                  <button type="button" className="btn btn-secondary" onClick={closeUploadModal} disabled={isUploading}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isUploading || !LOG_TYPE_OPTIONS.find((item) => item.value === selectedLogType)?.supported}
                    onClick={handleUpload}
                  >
                    {isUploading ? 'Enviando...' : 'Enviar arquivos'}
                  </button>
                </>
              )}
            >
              <div className="row g-3">
                <div className="col-12">
                  <label htmlFor="sap-log-type" className="form-label">Tipo de log</label>
                  <select
                    id="sap-log-type"
                    className="form-select"
                    value={selectedLogType}
                    disabled={isUploading}
                    onChange={(e) => {
                      const value = String(e.target.value || 'STUSERTRACE (json)') as UploadModelType;
                      if (LOG_TYPE_OPTIONS.some((option) => option.value === value)) setSelectedLogType(value);
                    }}
                  >
                    {LOG_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                {LOG_TYPE_OPTIONS.find((item) => item.value === selectedLogType)?.supported ? (
                  <>
                <div className="col-12">
                  <label htmlFor="sap-log-json" className="form-label">Arquivo de log (.json)</label>
                  <input
                    id="sap-log-json"
                    type="file"
                    accept=".json,application/json"
                    className="form-control"
                    disabled={isUploading}
                    onChange={handleLogFileChange}
                  />
                  {fileName && <div className="small text-muted mt-2">Log: {fileName}</div>}
                </div>

                <div className="col-12">
                  <label htmlFor="sap-mapping-file" className="form-label">Arquivo TOBJ/mapeamento (txt / ; / tab)</label>
                  <input
                    id="sap-mapping-file"
                    type="file"
                    accept=".txt,.csv,text/plain"
                    className="form-control"
                    disabled={isUploading}
                    onChange={handleMappingFileChange}
                  />
                  {mappingFileName && <div className="small text-muted mt-2">Mapeamento: {mappingFileName}</div>}
                </div>

                {isUploading && uploadTransferProgress && (
                  <div className="col-12">
                    <label className="form-label mb-1">Progresso do envio</label>
                    {renderProgressFill(uploadTransferProgress.percent ?? 0)}
                    <div className="small text-muted mt-1">
                      {uploadTransferProgress.total
                        ? `${formatBytes(uploadTransferProgress.loaded)} / ${formatBytes(uploadTransferProgress.total)} (${(uploadTransferProgress.percent ?? 0).toFixed(2)}%)`
                        : `${formatBytes(uploadTransferProgress.loaded)} enviados`}
                    </div>
                  </div>
                )}
                  </>
                ) : (
                  <div className="col-12">
                    <div className="alert alert-warning mb-0">Modelo de log não suportado - Em desenvolvimento</div>
                  </div>
                )}
              </div>
            </AppModal>

            <AppModal
              show={showCleanupModal}
              title="Higienizar upload de log"
              onClose={closeCleanupModal}
              closeDisabled={cleanupRunning}
              size="lg"
              footer={(
                <>
                  <button type="button" className="btn btn-secondary" onClick={closeCleanupModal} disabled={cleanupRunning}>
                    Cancelar
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={applyRecommendedCleanupPreset} disabled={cleanupRunning || cleanupColumnsLoading}>
                    Higienização recomendada
                  </button>
                  <button type="button" className="btn btn-primary" onClick={runCleanup} disabled={cleanupRunning}>
                    {cleanupRunning ? 'Processando...' : 'Aplicar higienização'}
                  </button>
                </>
              )}
            >
              <div className="border rounded-3 p-3 mb-3">
                <label className="form-label fw-semibold mb-2">Seleção de colunas para eliminar</label>
                <div className="row g-2 align-items-center">
                  <div className="col-12 col-md-5">
                    <div className="small text-muted mb-1">Colunas existentes</div>
                    <select
                      multiple
                      size={10}
                      className="form-select"
                      value={availableSelection}
                      onChange={(e) => {
                        const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                        setAvailableSelection(values);
                      }}
                      disabled={cleanupRunning || cleanupColumnsLoading}
                    >
                      {availableCleanupColumns
                        .filter((item) => !selectedCleanupColumns.includes(item))
                        .map((column) => (
                          <option key={column} value={column}>{column}</option>
                        ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-2 d-flex flex-column gap-2 align-items-center justify-content-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={addSelectedColumnsToRemove}
                      disabled={cleanupRunning || cleanupColumnsLoading || availableSelection.length === 0}
                    >
                      &gt;
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={removeSelectedColumnsFromRemove}
                      disabled={cleanupRunning || cleanupColumnsLoading || removeSelection.length === 0}
                    >
                      &lt;
                    </button>
                  </div>
                  <div className="col-12 col-md-5">
                    <div className="small text-muted mb-1">Colunas para eliminar</div>
                    <select
                      multiple
                      size={10}
                      className="form-select"
                      value={removeSelection}
                      onChange={(e) => {
                        const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                        setRemoveSelection(values);
                      }}
                      disabled={cleanupRunning || cleanupColumnsLoading}
                    >
                      {selectedCleanupColumns.map((column) => (
                        <option key={column} value={column}>{column}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-text mt-2">
                  Selecione na lista da esquerda e mova para a direita as colunas que deseja apagar do banco.
                </div>
                {cleanupColumnsLoading && <div className="small text-muted mt-1">Carregando colunas do upload...</div>}
              </div>

              <div className="border rounded-3 p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-semibold mb-0">Regras de limpeza por coluna e valor</label>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={addCleanupRule}
                    disabled={cleanupRunning || cleanupColumnsLoading}
                  >
                    Nova regra
                  </button>
                </div>

                {cleanupRules.length === 0 ? (
                  <div className="small text-muted">Nenhuma regra criada.</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {cleanupRules.map((rule, ruleIndex) => (
                      <div key={rule.id} className="border rounded p-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="small fw-semibold">Regra {ruleIndex + 1} (todas as condições abaixo devem bater)</div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeCleanupRule(rule.id)}
                            disabled={cleanupRunning}
                          >
                            Remover regra
                          </button>
                        </div>

                        <div className="d-flex flex-column gap-2">
                          {rule.conditions.map((condition) => (
                            <div key={condition.id} className="row g-2 align-items-center">
                              <div className="col-12 col-md-4">
                                <select
                                  className="form-select"
                                  value={condition.column}
                                  onChange={(e) => updateCleanupRuleCondition(rule.id, condition.id, 'column', e.target.value)}
                                  disabled={cleanupRunning || cleanupColumnsLoading}
                                >
                                  <option value="">Selecione a coluna</option>
                                  {availableCleanupColumns.map((column) => (
                                    <option key={column} value={column}>{column}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-12 col-md-3">
                                <select
                                  className="form-select"
                                  value={condition.operator}
                                  onChange={(e) => updateCleanupRuleCondition(rule.id, condition.id, 'operator', e.target.value)}
                                  disabled={cleanupRunning}
                                >
                                  {CLEANUP_OPERATOR_OPTIONS.map((operator) => (
                                    <option key={operator.value} value={operator.value}>{operator.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-12 col-md-3">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={condition.value}
                                  onChange={(e) => updateCleanupRuleCondition(rule.id, condition.id, 'value', e.target.value)}
                                  placeholder={condition.operator === 'empty' || condition.operator === 'not_empty' ? 'Sem valor' : 'Informe o valor'}
                                  disabled={cleanupRunning || condition.operator === 'empty' || condition.operator === 'not_empty'}
                                />
                              </div>
                              <div className="col-12 col-md-2 d-grid">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => removeCleanupRuleCondition(rule.id, condition.id)}
                                  disabled={cleanupRunning}
                                >
                                  Remover
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => addCleanupRuleCondition(rule.id)}
                            disabled={cleanupRunning || cleanupColumnsLoading}
                          >
                            + condição
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-text mt-2">
                  Cada regra remove os registros que atendem todas as condições da própria regra. Entre regras, a remoção é combinada (OU).
                </div>
              </div>

              <div className="border rounded-3 p-3">
                <label className="form-label fw-semibold mb-2">Remoção de duplicadas</label>
                <div className="form-check mb-3">
                  <input
                    id="cleanup-duplicates"
                    className="form-check-input"
                    type="checkbox"
                    checked={cleanupRemoveDuplicates}
                    onChange={(e) => setCleanupRemoveDuplicates(e.target.checked)}
                    disabled={cleanupRunning}
                  />
                  <label className="form-check-label" htmlFor="cleanup-duplicates">
                    Remover registros duplicados (geral)
                  </label>
                </div>

                <label className="form-label fw-semibold mb-2">Tratamento de pares FIELD/VALUE</label>
                <div className="form-check mb-0">
                  <input
                    id="cleanup-empty-field-value-pairs"
                    className="form-check-input"
                    type="checkbox"
                    checked={cleanupRemoveEmptyFieldValuePairs}
                    onChange={(e) => setCleanupRemoveEmptyFieldValuePairs(e.target.checked)}
                    disabled={cleanupRunning}
                  />
                  <label className="form-check-label" htmlFor="cleanup-empty-field-value-pairs">
                    Remover pares FIELDx + VALUEx quando ambos estiverem vazios
                  </label>
                </div>
              </div>
            </AppModal>

            <AppModal
              show={showConsolidationModal}
              title="Gerar consolidação de log para perfis"
              onClose={closeConsolidationModal}
              closeDisabled={creatingConsolidation}
              size="xl"
              footer={(
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeConsolidationModal}
                    disabled={creatingConsolidation}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={createProfileConsolidation}
                    disabled={creatingConsolidation || selectedConsolidationUploadIds.length === 0}
                  >
                    {creatingConsolidation ? 'Gerando...' : 'Gerar consolidação'}
                  </button>
                </>
              )}
            >
              <p className="text-muted mb-3">
                Selecione os uploads da etapa 1 que serão considerados nesta consolidação.
              </p>

              {consolidationOptionsLoading ? (
                <div className="text-muted">Carregando uploads elegíveis...</div>
              ) : consolidationOptions.length === 0 ? (
                <div className="alert alert-warning mb-0">Nenhum upload elegível encontrado (somente uploads concluídos/interrompidos podem ser selecionados).</div>
              ) : (
                <DataTable
                  columns={[
                    {
                      key: '_select',
                      label: 'Sel.',
                      sortable: false,
                      checkboxColumn: true,
                      allChecked: consolidationOptions.length > 0 && consolidationOptions.every((item) => selectedConsolidationUploadIds.includes(item._id)),
                      onCheckboxHeaderClick: toggleSelectAllConsolidationUploads,
                      render: (_value: any, row: UploadRow) => (
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedConsolidationUploadIds.includes(row._id)}
                          onChange={() => toggleConsolidationUploadSelection(row._id)}
                          disabled={creatingConsolidation}
                        />
                      )
                    },
                    {
                      key: 'fileName',
                      label: 'Arquivo',
                      sortable: true,
                      render: (value: any) => String(value || '-').trim() || '-'
                    },
                    {
                      key: 'logType',
                      label: 'Tipo',
                      sortable: true,
                      render: (value: any) => String(value || '-').trim() || '-'
                    },
                    {
                      key: 'status',
                      label: 'Status',
                      sortable: true,
                          render: (value: any) => (
                            String(value || '') === 'processed'
                              ? <span className="badge bg-success">Concluído</span>
                              : <span className="badge bg-warning text-dark">Interrompido</span>
                          )
                    },
                    {
                      key: 'createdAt',
                      label: 'Criado em',
                      sortable: true,
                      render: (value: any) => {
                        if (!value) return '-';
                        const dt = new Date(value);
                        if (Number.isNaN(dt.getTime())) return '-';
                        return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
                      }
                    }
                  ]}
                  data={consolidationOptions}
                  keyExtractor={(row: UploadRow) => row._id}
                  striped
                  hover
                  responsive
                  pagination={true}
                  clientSidePagination={true}
                  pageSize={10}
                  totalItems={consolidationOptions.length}
                  pageSizeOptions={[10, 20, 50, 100]}
                  emptyMessage="Nenhum upload elegível encontrado para consolidação."
                />
              )}

              <div className="small text-muted mt-3">
                Selecionados: {selectedConsolidationUploadIds.length}
              </div>
            </AppModal>

            <AppModal
              show={showUsorgModal}
              title="Carregar USORG"
              onClose={closeUsorgModal}
              closeDisabled={usorgUploading}
              size="lg"
              footer={(
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeUsorgModal}
                    disabled={usorgUploading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={uploadUsorg}
                    disabled={usorgUploading || !usorgFile}
                  >
                    {usorgUploading ? 'Enviando...' : 'Enviar USORG'}
                  </button>
                </>
              )}
            >
              <div className="mb-3">
                <label htmlFor="usorg-file-input" className="form-label">Arquivo USORG</label>
                <input
                  id="usorg-file-input"
                  type="file"
                  className="form-control"
                  accept=".csv,.txt,.xlsx,.xlsm,.xls"
                  disabled={usorgUploading}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
                    setUsorgFile(file);
                  }}
                />
              </div>
              <div className="small text-muted">
                Estrutura obrigatória: colunas <strong>CAMPO</strong> e <strong>VARIAVEL</strong>.
              </div>
              <div className="small text-muted mt-1">
                Formatos aceitos: CSV/TXT e Excel (.xlsx, .xlsm). Para .xls, converta para .xlsx antes do envio.
              </div>
            </AppModal>

            <AppModal
              show={showLogConsolidationPlanModal}
              title={logConsolidationPlanActionLabel}
              onClose={closeLogConsolidationPlanModal}
              closeDisabled={logConsolidationPlanSubmitting}
              size="xl"
              footer={(
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => closeLogConsolidationPlanModal()}
                    disabled={logConsolidationPlanSubmitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={approveV2LogConsolidationPlan}
                    disabled={logConsolidationPlanSubmitting || logConsolidationPlanLoading || !logConsolidationPlanPreview || !logConsolidationPlanTarget}
                  >
                    {logConsolidationPlanSubmitting ? 'Iniciando...' : 'Aprovar e iniciar consolidação'}
                  </button>
                </>
              )}
            >
              {logConsolidationPlanLoading ? (
                <div>
                  <div className="text-muted mb-2">{logConsolidationPlanLoadMessage || 'Calculando plano de consolidação v2...'}</div>
                  <div className="progress" role="progressbar" aria-valuenow={logConsolidationPlanLoadProgress} aria-valuemin={0} aria-valuemax={100} style={{ height: '10px' }}>
                    <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${Math.max(2, logConsolidationPlanLoadProgress)}%` }} />
                  </div>
                  <div className="small text-muted mt-2">{Math.round(logConsolidationPlanLoadProgress)}%</div>
                </div>
              ) : !logConsolidationPlanPreview ? (
                <div className="alert alert-warning mb-0">Não foi possível montar a prévia da consolidação.</div>
              ) : (
                <>
                  <ModalTabs
                    activeKey={logConsolidationPlanActiveTab}
                    onChange={setLogConsolidationPlanActiveTab}
                    items={[
                      { key: 'general', label: 'Parâmetros gerais', icon: Icons.Gear },
                      { key: 'rules', label: 'Regras de consolidação', icon: Icons.Funnel },
                      { key: 'analysis', label: 'Visão e análise', icon: Icons.Eye },
                    ]}
                  />

                  {logConsolidationPlanActiveTab === 'general' && (
                    <>
                  <p className="text-muted mb-3">
                    Ajuste os percentuais de volume por faixa. O cálculo considera o total consolidado dos logs selecionados. Clique em "Recalcular prévia" para atualizar os dados exibidos.
                  </p>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <label className="form-label" htmlFor="v2-heavy-min-percent">Faixa pesada (% do total)</label>
                      <input
                        id="v2-heavy-min-percent"
                        type="number"
                        className="form-control"
                        min={0}
                        max={100}
                        step={0.01}
                        value={logConsolidationTierThresholds.heavyMinPercent}
                        onChange={(e) => {
                          const heavyMinPercent = Math.max(0, Math.min(100, Number(e.target.value || 0)));
                          setLogConsolidationTierThresholds((prev) => {
                            const medium = Math.min(prev.mediumMinPercent, heavyMinPercent);
                            const light = Math.min(prev.lightMinPercent, medium);
                            return {
                              heavyMinPercent,
                              mediumMinPercent: Number(medium.toFixed(4)),
                              lightMinPercent: Number(light.toFixed(4)),
                            };
                          });
                        }}
                        disabled={logConsolidationPlanSubmitting}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label" htmlFor="v2-medium-min-percent">Faixa média (% do total)</label>
                      <input
                        id="v2-medium-min-percent"
                        type="number"
                        className="form-control"
                        min={0}
                        max={logConsolidationTierThresholds.heavyMinPercent}
                        step={0.01}
                        value={logConsolidationTierThresholds.mediumMinPercent}
                        onChange={(e) => {
                          const mediumMinPercent = Math.max(0, Math.min(logConsolidationTierThresholds.heavyMinPercent, Number(e.target.value || 0)));
                          setLogConsolidationTierThresholds((prev) => ({
                            heavyMinPercent: prev.heavyMinPercent,
                            mediumMinPercent: Number(mediumMinPercent.toFixed(4)),
                            lightMinPercent: Number(Math.min(prev.lightMinPercent, mediumMinPercent).toFixed(4)),
                          }));
                        }}
                        disabled={logConsolidationPlanSubmitting}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label" htmlFor="v2-light-min-percent">Faixa leve (% do total)</label>
                      <input
                        id="v2-light-min-percent"
                        type="number"
                        className="form-control"
                        min={0}
                        max={logConsolidationTierThresholds.mediumMinPercent}
                        step={0.01}
                        value={logConsolidationTierThresholds.lightMinPercent}
                        onChange={(e) => {
                          const lightMinPercent = Math.max(0, Math.min(logConsolidationTierThresholds.mediumMinPercent, Number(e.target.value || 0)));
                          setLogConsolidationTierThresholds((prev) => ({
                            ...prev,
                            lightMinPercent: Number(lightMinPercent.toFixed(4)),
                          }));
                        }}
                        disabled={logConsolidationPlanSubmitting}
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-end mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={recalculateLogConsolidationV2Preview}
                      disabled={logConsolidationPlanSubmitting || logConsolidationPlanLoading || logConsolidationPlanUsersBase.length === 0}
                    >
                      Recalcular prévia
                    </button>
                  </div>

                  <div className="small text-muted mb-3">
                    Limiares absolutos calculados para esta consolidação:
                    {' '}
                    pesado &gt;=
                    {' '}
                    {Number(logConsolidationPlanPreview.tierThresholds?.heavyMinCount || 0).toLocaleString('pt-BR')}
                    {' | '}
                    médio &gt;=
                    {' '}
                    {Number(logConsolidationPlanPreview.tierThresholds?.mediumMinCount || 0).toLocaleString('pt-BR')}
                    {' | '}
                    leve &gt;=
                    {' '}
                    {Number(logConsolidationPlanPreview.tierThresholds?.lightMinCount || 0).toLocaleString('pt-BR')}
                    {' | '}
                    simples &lt;
                    {' '}
                    {Number(logConsolidationPlanPreview.tierThresholds?.lightMinCount || 0).toLocaleString('pt-BR')}
                  </div>

                  <div className="row g-3 mb-3">
                    {(['heavy', 'medium', 'light', 'simple'] as LogConsolidationPlanTier[]).map((tier) => {
                      const summaryRow = (logConsolidationPlanPreview.summaryByTier || []).find((item) => item.tier === tier);
                      return (
                        <div className="col-md-3" key={`tier-indicator-${tier}`}>
                          <div className="border rounded p-3 h-100">
                            <div className="text-muted small">{formatLogConsolidationTier(tier)}</div>
                            <div className="fs-5 fw-semibold">{Number(summaryRow?.totalUsers || 0).toLocaleString('pt-BR')} usuário(s)</div>
                            <div className="small text-muted">{Number(summaryRow?.totalLogCount || 0).toLocaleString('pt-BR')} registros</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Total de usuários</div>
                        <div className="fs-4 fw-semibold">{Number(logConsolidationPlanPreview.totalUsers || 0).toLocaleString('pt-BR')}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Total de registros</div>
                        <div className="fs-4 fw-semibold">{Number(logConsolidationPlanPreview.totalLogCount || 0).toLocaleString('pt-BR')}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Total de jobs planejados</div>
                        <div className="fs-4 fw-semibold">{Number(logConsolidationPlanPreview.totalJobs || 0).toLocaleString('pt-BR')}</div>
                      </div>
                    </div>
                  </div>

                  {Number(logConsolidationPlanPreview.heavyUsersCount || 0) > 0 && (
                    <div className="alert alert-info py-2 mb-3">
                      <div>
                        Pesados identificados: {Number(logConsolidationPlanPreview.heavyUsersCount || 0).toLocaleString('pt-BR')} usuário(s).
                        {` `}
                        Pares usuário + código desses pesados: {Number(logConsolidationPlanPreview.heavyUserCodePairsCount || 0).toLocaleString('pt-BR')}.
                      </div>
                      <div>
                        Jobs totais sem split por código: {Number(logConsolidationPlanPreview.totalJobsIfHeavyUserOnly || 0).toLocaleString('pt-BR')}.
                        {` `}
                        Jobs totais com split por código: {Number(logConsolidationPlanPreview.totalJobsIfHeavySplitByCode || 0).toLocaleString('pt-BR')}.
                      </div>
                    </div>
                  )}

                  <div className="form-check form-switch mb-3">
                    <input
                      id="persist-group-origins-log-consolidation-v2-plan"
                      className="form-check-input"
                      type="checkbox"
                      checked={persistGroupOriginsChoice}
                      onChange={(e) => {
                        setPersistGroupOriginsChoice(e.target.checked);
                        persistGroupOriginsChoiceRef.current = e.target.checked;
                      }}
                      disabled={logConsolidationPlanSubmitting}
                    />
                    <label className="form-check-label" htmlFor="persist-group-origins-log-consolidation-v2-plan">
                      Persistir origens dos agrupamentos gerados na estrutura ajustada
                    </label>
                  </div>

                  <div className="form-check form-switch mb-3">
                    <input
                      id="split-heavy-users-by-code-log-consolidation-v2-plan"
                      className="form-check-input"
                      type="checkbox"
                      checked={logConsolidationSplitHeavyByCode}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setLogConsolidationSplitHeavyByCode(checked);

                        if (!analyzerClientId || !logConsolidationPlanTarget) return;

                        const base = resolveApiBase();
                        setLogConsolidationPlanLoading(true);
                        startLogConsolidationPlanProgress('Aplicando modo de usuários pesados e recalculando a prévia...');
                        axios.get(
                          `${base}/api/sap-log-analyzer/clients/${analyzerClientId}/profile-consolidations/${logConsolidationPlanTarget._id}/log-consolidation-plan`,
                          {
                            withCredentials: true,
                            params: {
                              optimizationProfile: 'v2',
                              heavyMinPercent: logConsolidationTierThresholds.heavyMinPercent,
                              mediumMinPercent: logConsolidationTierThresholds.mediumMinPercent,
                              lightMinPercent: logConsolidationTierThresholds.lightMinPercent,
                              splitHeavyUsersByCode: checked,
                            }
                          }
                        ).then((planRes) => {
                          updateLogConsolidationPlanProgress(64, 'Reprocessando users, tiers e jobs planejados...');
                          applyLogConsolidationPlanPreview(planRes.data);
                          setLogConsolidationSummaryPage(1);
                          setLogConsolidationRulesPage(1);
                          setLogConsolidationTopDistinctFieldsPage(1);
                          setLogConsolidationJobsPage(1);
                          setLogConsolidationUsersPage(1);
                          finishLogConsolidationPlanProgress('Prévia recalculada.');
                        }).catch((e: any) => {
                          const backendMessage = e?.response?.data?.message;
                          const message = typeof backendMessage === 'string' && backendMessage.length > 0
                            ? backendMessage
                            : (typeof e?.message === 'string' ? e.message : 'erro_carregar_plano_consolidacao');
                          setError(`Não foi possível recalcular o plano da consolidação v2 (${message}).`);
                        }).finally(() => {
                          stopLogConsolidationPlanProgress();
                          setLogConsolidationPlanLoading(false);
                        });
                      }}
                      disabled={logConsolidationPlanSubmitting}
                    />
                    <label className="form-check-label" htmlFor="split-heavy-users-by-code-log-consolidation-v2-plan">
                      Usuários pesados: processar por usuário + código (mais jobs, maior paralelismo)
                    </label>
                    {Number(logConsolidationPlanPreview.heavyUsersCount || 0) > 0 && (
                      <div className="small text-muted mt-1">
                        {logConsolidationSplitHeavyByCode
                          ? `Modo atual: ${Number(logConsolidationPlanPreview.totalJobs || 0).toLocaleString('pt-BR')} job(s), usando ${Number(logConsolidationPlanPreview.heavyUserCodePairsCount || 0).toLocaleString('pt-BR')} par(es) pesado(s) usuário + código.`
                          : `Se ativar este modo: ${Number(logConsolidationPlanPreview.totalJobsIfHeavySplitByCode || 0).toLocaleString('pt-BR')} job(s) no total, contra ${Number(logConsolidationPlanPreview.totalJobsIfHeavyUserOnly || 0).toLocaleString('pt-BR')} sem split por código.`}
                      </div>
                    )}
                  </div>
                    </>
                  )}

                  {logConsolidationPlanActiveTab === 'analysis' && (
                  <div className="mb-4">
                    <h6 className="mb-2">Resumo por faixa</h6>
                    <DataTable
                      columns={[
                        {
                          key: 'tier',
                          label: 'Faixa',
                          sortable: true,
                          render: (value: any) => formatLogConsolidationTier(value as LogConsolidationPlanTier)
                        },
                        {
                          key: 'usersPerJob',
                          label: 'Usuários por job',
                          sortable: true,
                          render: (value: any) => Number(value || 0).toLocaleString('pt-BR')
                        },
                        {
                          key: 'totalUsers',
                          label: 'Usuários',
                          sortable: true,
                          render: (value: any) => Number(value || 0).toLocaleString('pt-BR')
                        },
                        {
                          key: 'totalLogCount',
                          label: 'Registros',
                          sortable: true,
                          render: (value: any) => Number(value || 0).toLocaleString('pt-BR')
                        },
                        {
                          key: 'totalJobs',
                          label: 'Jobs planejados',
                          sortable: true,
                          render: (value: any) => Number(value || 0).toLocaleString('pt-BR')
                        }
                      ]}
                      data={Array.isArray(logConsolidationPlanPreview.summaryByTier) ? logConsolidationPlanPreview.summaryByTier : []}
                      keyExtractor={(row: LogConsolidationPlanTierSummary) => row.tier}
                      striped
                      hover
                      responsive
                      clientSidePagination={true}
                      pagination={true}
                      currentPage={logConsolidationSummaryPage}
                      pageSize={logConsolidationSummaryPageSize}
                      onPageChange={(page: number) => setLogConsolidationSummaryPage(page)}
                      onPageSizeChange={(nextPageSize: number) => {
                        setLogConsolidationSummaryPageSize(nextPageSize);
                        setLogConsolidationSummaryPage(1);
                      }}
                      pageSizeOptions={[5, 10, 20, 50]}
                      totalItems={Array.isArray(logConsolidationPlanPreview.summaryByTier) ? logConsolidationPlanPreview.summaryByTier.length : 0}
                      emptyMessage="Nenhuma faixa identificada para a consolidação."
                    />
                  </div>

                  )}

                  {logConsolidationPlanActiveTab === 'rules' && (
                    <>
                  <div className="mb-4">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                      <h6 className="mb-0">Regras de consolidação</h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={addManualConsolidationRuleDraft}
                        disabled={Boolean(logConsolidationManualRuleDraft) || logConsolidationManualRuleSubmitting || logConsolidationPlanSubmitting}
                      >
                        Definir regras manualmente
                      </button>
                    </div>

                    {logConsolidationManualRuleDraft && (
                      <div className="border rounded-3 p-3 mb-3">
                        <div className="small fw-semibold mb-2">Nova regra manual</div>

                        <div className="row g-2 align-items-end">
                          <div className="col-12 col-md-4">
                            <label className="form-label form-label-sm mb-1">Tipo de regra</label>
                            <select
                              className="form-select form-select-sm"
                              value={logConsolidationManualRuleDraft.ruleType}
                              onChange={(e) => updateManualConsolidationRuleDraft('ruleType', e.target.value as LogConsolidationManualRuleDraft['ruleType'])}
                              disabled={logConsolidationManualRuleSubmitting}
                            >
                              <option value="custom_pre_group_wildcard">Personalizado (pré agrupamento)</option>
                              <option value="global_authorization_threshold_post_group">Regra geral para autorização global (pós agrupamento)</option>
                            </select>
                          </div>

                          {logConsolidationManualRuleDraft.ruleType === 'custom_pre_group_wildcard' ? (
                            <>
                              <div className="col-12 col-md-2">
                                <label className="form-label form-label-sm mb-1">Usuário</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Todos ou específico"
                                  value={logConsolidationManualRuleDraft.username === '*' ? '' : logConsolidationManualRuleDraft.username}
                                  onChange={(e) => updateManualConsolidationRuleDraft('username', e.target.value.trim() || '*')}
                                  disabled={logConsolidationManualRuleSubmitting}
                                />
                              </div>
                              <div className="col-12 col-md-2">
                                <label className="form-label form-label-sm mb-1">Code</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Todos ou específico"
                                  value={logConsolidationManualRuleDraft.code === '*' ? '' : logConsolidationManualRuleDraft.code}
                                  onChange={(e) => updateManualConsolidationRuleDraft('code', e.target.value.trim() || '*')}
                                  disabled={logConsolidationManualRuleSubmitting}
                                />
                              </div>
                              <div className="col-12 col-md-2">
                                <label className="form-label form-label-sm mb-1">Object</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Todos ou específico"
                                  value={logConsolidationManualRuleDraft.object === '*' ? '' : logConsolidationManualRuleDraft.object}
                                  onChange={(e) => updateManualConsolidationRuleDraft('object', e.target.value.trim() || '*')}
                                  disabled={logConsolidationManualRuleSubmitting}
                                />
                              </div>
                              <div className="col-12 col-md-2">
                                <label className="form-label form-label-sm mb-1">Campo</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Todos ou específico"
                                  value={logConsolidationManualRuleDraft.field === '*' ? '' : logConsolidationManualRuleDraft.field}
                                  onChange={(e) => updateManualConsolidationRuleDraft('field', e.target.value.trim() || '*')}
                                  disabled={logConsolidationManualRuleSubmitting}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="col-12 col-md-3">
                                <label className="form-label form-label-sm mb-1">Limiar campos comuns</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  min={0}
                                  step={1}
                                  value={Math.max(0, Number(logConsolidationManualRuleDraft.thresholdDistinctValuesCommon || 0))}
                                  onChange={(e) => updateManualConsolidationRuleDraft('thresholdDistinctValuesCommon', Math.max(0, Number(e.target.value || 0)))}
                                  disabled={logConsolidationManualRuleSubmitting}
                                />
                              </div>
                              <div className="col-12 col-md-3">
                                <label className="form-label form-label-sm mb-1">Limiar campos USORG</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  min={0}
                                  step={1}
                                  value={Math.max(0, Number(logConsolidationManualRuleDraft.thresholdDistinctValuesOrgLevel || 0))}
                                  onChange={(e) => updateManualConsolidationRuleDraft('thresholdDistinctValuesOrgLevel', Math.max(0, Number(e.target.value || 0)))}
                                  disabled={logConsolidationManualRuleSubmitting}
                                />
                              </div>
                            </>
                          )}

                          <div className="col-12 col-md-2 d-flex gap-2 justify-content-md-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={() => setLogConsolidationManualRuleDraft(null)}
                              disabled={logConsolidationManualRuleSubmitting}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => {
                                submitManualConsolidationRuleDraft().catch(() => undefined);
                              }}
                              disabled={logConsolidationManualRuleSubmitting}
                            >
                              {logConsolidationManualRuleSubmitting ? 'Salvando...' : 'Salvar regra'}
                            </button>
                          </div>
                        </div>

                        <div className="small text-muted mt-2">
                          Deixe o campo vazio para aplicar em todos os valores daquela dimensão.
                        </div>
                      </div>
                    )}

                    <DataTable
                      columns={[
                        {
                          key: 'ruleType',
                          label: 'Tipo de regra',
                          sortable: false,
                          render: (value: any) => {
                            const ruleType = String(value || '').trim();
                            if (ruleType === 'global_authorization') return 'Autorização global';
                            if (ruleType === 'custom_pre_group_wildcard') return 'Personalizado (pré agrupamento)';
                            if (ruleType === 'global_authorization_threshold_post_group') return 'Regra geral (pós agrupamento)';
                            return ruleType || '-';
                          }
                        },
                        {
                          key: 'username',
                          label: 'Usuário',
                          sortable: false,
                          render: (value: any) => {
                            const normalized = String(value || '').trim();
                            if (!normalized || normalized === '*') return 'Todos';
                            return normalized;
                          }
                        },
                        {
                          key: 'code',
                          label: 'Code',
                          sortable: false,
                          render: (value: any) => {
                            const normalized = String(value || '').trim();
                            if (!normalized || normalized === '*') return 'Todos';
                            return normalized;
                          }
                        },
                        {
                          key: 'object',
                          label: 'Object',
                          sortable: false,
                          render: (value: any, row: LogConsolidationRule) => {
                            if (row.ruleType === 'global_authorization_threshold_post_group') return 'Todos';
                            const normalized = String(value || '').trim();
                            if (!normalized || normalized === '*') return 'Todos';
                            return normalized;
                          }
                        },
                        {
                          key: 'field',
                          label: 'Campo',
                          sortable: false,
                          render: (value: any, row: LogConsolidationRule) => {
                            if (row.ruleType === 'global_authorization_threshold_post_group') {
                              const scopeLabel = row.fieldScope === 'org_level'
                                ? 'USORG'
                                : row.fieldScope === 'common'
                                ? 'Comum'
                                : 'Todos';
                              return `${scopeLabel}: ${Math.max(1, Number(row.thresholdDistinctValues || 1)).toLocaleString('pt-BR')}`;
                            }
                            const normalized = String(value || '').trim();
                            if (!normalized || normalized === '*') return 'Todos';
                            return normalized;
                          }
                        },
                        {
                          key: 'marker',
                          label: 'Marcador',
                          sortable: false,
                          render: (_value: any, row: LogConsolidationRule) => (
                            row.ruleType === 'global_authorization_threshold_post_group'
                              ? <span className="badge text-bg-info">*&nbsp;automático</span>
                              : row.ruleType === 'custom_pre_group_wildcard'
                              ? <span className="badge text-bg-primary">*&nbsp;pré</span>
                              : <span className="badge text-bg-warning">*</span>
                          )
                        },
                        {
                          key: 'acoes',
                          label: 'Ações',
                          sortable: false,
                          render: (_value: any, row: LogConsolidationRule) => (
                            <ActionMenu
                              triggerSize="md"
                              actions={[
                                {
                                  key: 'eliminar-regra',
                                  label: 'Eliminar regra',
                                  icon: Icons.Trash,
                                  variant: 'danger',
                                  onClick: () => {
                                    removeConsolidationRule(row).catch(() => undefined);
                                  }
                                }
                              ]}
                            />
                          )
                        }
                      ]}
                      data={Array.isArray(logConsolidationPlanPreview.consolidationRules) ? logConsolidationPlanPreview.consolidationRules : []}
                      keyExtractor={(row: LogConsolidationRule, index: number) => `consolidation-rule-${row.ruleType}-${row.username}-${row.code}-${row.object}-${row.field}-${index}`}
                      striped
                      hover
                      responsive
                      clientSidePagination={true}
                      pagination={true}
                      currentPage={logConsolidationRulesPage}
                      pageSize={logConsolidationRulesPageSize}
                      onPageChange={(page: number) => setLogConsolidationRulesPage(page)}
                      onPageSizeChange={(nextPageSize: number) => {
                        setLogConsolidationRulesPageSize(nextPageSize);
                        setLogConsolidationRulesPage(1);
                      }}
                      pageSizeOptions={[5, 10, 20, 50]}
                      totalItems={Array.isArray(logConsolidationPlanPreview.consolidationRules) ? logConsolidationPlanPreview.consolidationRules.length : 0}
                      emptyMessage="Nenhuma regra de consolidação definida."
                    />
                  </div>

                  <div className="mb-4">
                    <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
                      <h6 className="mb-0">Top {Number(logConsolidationTopDistinctFieldsLimit || 10)} campos críticos (valores distintos)</h6>
                      <div className="d-flex align-items-end gap-2">
                        <div>
                          <label className="form-label form-label-sm mb-1" htmlFor="top-critical-fields-limit">Tamanho da lista</label>
                          <input
                            id="top-critical-fields-limit"
                            type="number"
                            className="form-control form-control-sm"
                            min={1}
                            max={100}
                            step={1}
                            style={{ width: 96 }}
                            value={logConsolidationTopDistinctFieldsLimit}
                            onChange={(e) => {
                              const raw = Number(e.target.value || 10);
                              const next = Math.max(1, Math.min(100, Number.isFinite(raw) ? raw : 10));
                              setLogConsolidationTopDistinctFieldsLimit(next);
                            }}
                            disabled={logConsolidationPlanSubmitting || logConsolidationTopDistinctFieldsLoading}
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            loadLogConsolidationTopDistinctFields().catch(() => undefined);
                          }}
                          disabled={logConsolidationPlanSubmitting || logConsolidationTopDistinctFieldsLoading || !logConsolidationPlanTarget || !analyzerClientId}
                        >
                          {logConsolidationTopDistinctFieldsLoading ? 'Mapeando...' : 'Mapear top campos críticos'}
                        </button>
                      </div>
                    </div>

                    {!logConsolidationTopDistinctFieldsLoaded && !logConsolidationTopDistinctFieldsLoading && (
                      <div className="small text-muted mb-2">
                        O levantamento dos campos críticos agora é sob demanda para evitar timeout no carregamento inicial da prévia.
                      </div>
                    )}

                    {logConsolidationTopDistinctFieldsLoading && (
                      <div className="mb-3">
                        <div className="text-muted mb-2">{logConsolidationTopDistinctFieldsLoadMessage || 'Mapeando campos críticos...'}</div>
                        <div className="progress" role="progressbar" aria-valuenow={logConsolidationTopDistinctFieldsLoadProgress} aria-valuemin={0} aria-valuemax={100} style={{ height: '10px' }}>
                          <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${Math.max(3, logConsolidationTopDistinctFieldsLoadProgress)}%` }} />
                        </div>
                        <div className="small text-muted mt-1">{Math.round(logConsolidationTopDistinctFieldsLoadProgress)}%</div>
                        {logConsolidationTopDistinctFieldsJobId && (
                          <div className="small text-muted">Job: {logConsolidationTopDistinctFieldsJobId}</div>
                        )}
                      </div>
                    )}

                    <DataTable
                      columns={[
                        {
                          key: 'username',
                          label: 'Usuário',
                          sortable: false,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'code',
                          label: 'Code',
                          sortable: false,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'object',
                          label: 'Object',
                          sortable: false,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'field',
                          label: 'Campo',
                          sortable: false,
                          render: (value: any, row: LogConsolidationTopDistinctFieldRow) => (
                            <div className="d-flex align-items-center gap-2">
                              <span>{String(value || '-').trim() || '-'}</span>
                              {row.isGlobalAuthorization && (
                                <span className="badge text-bg-warning" title="Autorização global">*</span>
                              )}
                            </div>
                          )
                        },
                        {
                          key: 'distinctValuesCount',
                          label: 'Valores distintos',
                          sortable: false,
                          render: (value: any) => Number(value || 0).toLocaleString('pt-BR')
                        },
                        {
                          key: 'acoes',
                          label: 'Ações',
                          sortable: false,
                          render: (_value: any, row: LogConsolidationTopDistinctFieldRow) => (
                            <ActionMenu
                              triggerSize="md"
                              actions={[
                                {
                                  key: 'set-global-authorization',
                                  label: 'Autorização global',
                                  icon: Icons.Gear,
                                  disabled: Boolean(row.isGlobalAuthorization),
                                  onClick: () => {
                                    setTopDistinctFieldRule(row, true).catch(() => undefined);
                                  }
                                },
                                {
                                  key: 'clear-global-authorization',
                                  label: 'Limpar regra',
                                  icon: Icons.Trash,
                                  variant: 'danger',
                                  disabled: !Boolean(row.isGlobalAuthorization),
                                  onClick: () => {
                                    setTopDistinctFieldRule(row, false).catch(() => undefined);
                                  }
                                }
                              ]}
                            />
                          )
                        }
                      ]}
                      data={Array.isArray(logConsolidationPlanPreview.topDistinctFields) ? logConsolidationPlanPreview.topDistinctFields : []}
                      keyExtractor={(row: LogConsolidationTopDistinctFieldRow, index: number) => `top-distinct-field-${row.username}-${row.code}-${row.object}-${row.field}-${index}`}
                      striped
                      hover
                      responsive
                      clientSidePagination={true}
                      pagination={true}
                      currentPage={logConsolidationTopDistinctFieldsPage}
                      pageSize={logConsolidationTopDistinctFieldsPageSize}
                      onPageChange={(page: number) => setLogConsolidationTopDistinctFieldsPage(page)}
                      onPageSizeChange={(nextPageSize: number) => {
                        setLogConsolidationTopDistinctFieldsPageSize(nextPageSize);
                        setLogConsolidationTopDistinctFieldsPage(1);
                      }}
                      pageSizeOptions={[5, 10, 20, 50]}
                      totalItems={Array.isArray(logConsolidationPlanPreview.topDistinctFields) ? logConsolidationPlanPreview.topDistinctFields.length : 0}
                      emptyMessage={logConsolidationTopDistinctFieldsLoaded
                        ? 'Nenhum campo crítico identificado para esta consolidação.'
                        : 'Clique em "Mapear top campos críticos" para carregar esta análise.'}
                    />
                  </div>

                    </>
                  )}

                  {logConsolidationPlanActiveTab === 'analysis' && (
                    <>
                  <div className="mb-4">
                    <h6 className="mb-2">Usuários por faixa (prévia detalhada)</h6>
                    <DataTable
                      columns={[
                        {
                          key: 'label',
                          label: 'Item',
                          sortable: false,
                          render: (value: any, row: LogConsolidationPlanHierarchyRow) => {
                            const label = String(value || row?.label || '').trim() || '-';
                            const paddingLeft = `${Math.max(0, Number(row?.level || 0)) * 24}px`;
                            const isUserRow = row.rowType === 'user';
                            const isCodeRow = row.rowType === 'code';
                            const isObjectRow = row.rowType === 'object';
                            const isExpanded = isUserRow
                              ? Boolean(logConsolidationExpandedUsers[row.username])
                              : isCodeRow
                                ? Boolean(logConsolidationExpandedCodes[`${row.username}|||${row.code || ''}`])
                                : false;

                            return (
                              <div className="d-flex align-items-center gap-2" style={{ paddingLeft }}>
                                {row.expandable ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link text-decoration-none py-0 px-1 d-inline-flex align-items-center justify-content-center"
                                    title={isExpanded ? 'Recolher' : 'Expandir'}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      if (isUserRow) {
                                        toggleLogConsolidationUserHierarchy(row.username).catch(() => undefined);
                                        return;
                                      }
                                      if (isCodeRow) {
                                        toggleLogConsolidationCodeHierarchy(row.username, row.code || '');
                                      }
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.15s ease',
                                        lineHeight: 1
                                      }}
                                    >
                                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ width: 14, height: 14 }}>
                                        <path d="M10 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </span>
                                  </button>
                                ) : (
                                  <span style={{ width: 28, display: 'inline-block' }} />
                                )}
                                <span className={row.rowType === 'status' ? 'text-muted' : undefined}>
                                  {isCodeRow ? `CODE: ${label}` : isObjectRow ? `OBJECT: ${label}` : label}
                                </span>
                                {isObjectRow && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center p-0"
                                    style={{ width: 22, height: 22 }}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      openLogConsolidationObjectFieldsModal(row).catch(() => undefined);
                                    }}
                                    disabled={logConsolidationPlanSubmitting || logConsolidationPlanLoading}
                                    title="Visualizar campos e valores distintos"
                                    aria-label="Visualizar regras e campos"
                                  >
                                    <span style={{ display: 'inline-flex', transform: 'scale(0.85)', lineHeight: 1 }}>
                                      {Icons.Gear}
                                    </span>
                                  </button>
                                )}
                              </div>
                            );
                          }
                        },
                        {
                          key: 'rowType',
                          label: 'Nível',
                          sortable: false,
                          render: (value: any) => {
                            const rowType = String(value || '').trim().toLowerCase();
                            if (rowType === 'user') return 'Usuário';
                            if (rowType === 'code') return 'Code';
                            if (rowType === 'object') return 'Object';
                            return '';
                          }
                        },
                        {
                          key: 'workloadTier',
                          label: 'Faixa',
                          sortable: false,
                          render: (value: any, row: LogConsolidationPlanHierarchyRow) => {
                            if (row.rowType !== 'user') return '';
                            return formatLogConsolidationTier(value as LogConsolidationPlanTier);
                          }
                        },
                        {
                          key: 'logCount',
                          label: 'Total de registros',
                          sortable: false,
                          render: (value: any, row: LogConsolidationPlanHierarchyRow) => row.rowType === 'status' ? '' : Number(value || 0).toLocaleString('pt-BR')
                        },
                        {
                          key: 'logSharePercent',
                          label: '% do total consolidado',
                          sortable: false,
                          render: (value: any, row: LogConsolidationPlanHierarchyRow) => row.rowType === 'status'
                            ? ''
                            : `${Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`
                        }
                      ]}
                      data={logConsolidationHierarchyRows}
                      keyExtractor={(row: LogConsolidationPlanHierarchyRow) => row.key}
                      striped
                      hover
                      responsive
                      clientSidePagination={true}
                      pagination={true}
                      currentPage={logConsolidationUsersPage}
                      pageSize={logConsolidationUsersPageSize}
                      onPageChange={(page: number) => setLogConsolidationUsersPage(page)}
                      onPageSizeChange={(nextPageSize: number) => {
                        setLogConsolidationUsersPageSize(nextPageSize);
                        setLogConsolidationUsersPage(1);
                      }}
                      pageSizeOptions={[5, 10, 20, 50, 100]}
                      totalItems={logConsolidationHierarchyRows.length}
                      filters={logConsolidationUsersFilters}
                      onFilter={(columnKey: string, value: string) => {
                        setLogConsolidationUsersFilters((prev) => ({ ...prev, [columnKey]: value }));
                      }}
                      emptyMessage="Nenhum usuário encontrado para os limiares informados."
                    />
                  </div>

                  <div>
                    <h6 className="mb-2">Jobs planejados</h6>
                    <DataTable
                      columns={[
                        {
                          key: 'jobIndex',
                          label: 'Job',
                          sortable: true,
                          render: (value: any) => `#${Number(value || 0)}`
                        },
                        {
                          key: 'tier',
                          label: 'Faixa',
                          sortable: true,
                          render: (value: any) => formatLogConsolidationTier(value as LogConsolidationPlanTier)
                        },
                        {
                          key: 'userCount',
                          label: 'Usuários no job',
                          sortable: true,
                          render: (value: any) => Number(value || 0).toLocaleString('pt-BR')
                        },
                        {
                          key: 'totalLogCount',
                          label: 'Registros no job',
                          sortable: true,
                          render: (value: any) => Number(value || 0).toLocaleString('pt-BR')
                        },
                        {
                          key: 'users',
                          label: 'Usuários x registros',
                          sortable: false,
                          render: (_value: any, row: LogConsolidationPlanGroup) => (
                            <div className="d-flex flex-column gap-1">
                              {Array.isArray(row.users) && row.users.length > 0
                                ? row.users.map((user) => (
                                  <span key={`${row.jobIndex}-${user.username}`}>
                                    {user.username} ({Number(user.logCount || 0).toLocaleString('pt-BR')})
                                  </span>
                                ))
                                : <span>-</span>}
                            </div>
                          )
                        }
                      ]}
                      data={Array.isArray(logConsolidationPlanPreview.groups) ? logConsolidationPlanPreview.groups : []}
                      keyExtractor={(row: LogConsolidationPlanGroup) => `${row.jobIndex}`}
                      striped
                      hover
                      responsive
                      clientSidePagination={true}
                      pagination={true}
                      currentPage={logConsolidationJobsPage}
                      pageSize={logConsolidationJobsPageSize}
                      onPageChange={(page: number) => setLogConsolidationJobsPage(page)}
                      onPageSizeChange={(nextPageSize: number) => {
                        setLogConsolidationJobsPageSize(nextPageSize);
                        setLogConsolidationJobsPage(1);
                      }}
                      pageSizeOptions={[5, 10, 20, 50, 100]}
                      totalItems={Array.isArray(logConsolidationPlanPreview.groups) ? logConsolidationPlanPreview.groups.length : 0}
                      emptyMessage="Nenhum job planejado para esta consolidação."
                    />
                  </div>
                    </>
                  )}
                </>
              )}
            </AppModal>

            <AppModal
              show={showLogConsolidationObjectFieldsModal}
              title={logConsolidationObjectFieldsContext
                ? `Campos distintos - ${logConsolidationObjectFieldsContext.username} / ${logConsolidationObjectFieldsContext.code} / ${logConsolidationObjectFieldsContext.object}`
                : 'Campos distintos'}
              onClose={closeLogConsolidationObjectFieldsModal}
              closeDisabled={logConsolidationObjectFieldsLoading}
              size="lg"
              footer={(
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeLogConsolidationObjectFieldsModal}
                  disabled={logConsolidationObjectFieldsLoading}
                >
                  Fechar
                </button>
              )}
            >
              {logConsolidationObjectFieldsLoading ? (
                <div>
                  <div className="text-muted mb-2">{logConsolidationObjectFieldsLoadMessage || 'Carregando campos do object...'}</div>
                  <div className="progress" role="progressbar" aria-valuenow={logConsolidationObjectFieldsLoadProgress} aria-valuemin={0} aria-valuemax={100} style={{ height: '10px' }}>
                    <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${Math.max(2, logConsolidationObjectFieldsLoadProgress)}%` }} />
                  </div>
                  <div className="small text-muted mt-2">{Math.round(logConsolidationObjectFieldsLoadProgress)}%</div>
                </div>
              ) : logConsolidationObjectFieldsError ? (
                <div className="alert alert-danger mb-0">
                  Não foi possível carregar os campos ({logConsolidationObjectFieldsError}).
                </div>
              ) : logConsolidationObjectFieldsRows.length === 0 ? (
                <div className="alert alert-warning mb-0">Nenhum campo encontrado para este object.</div>
              ) : (
                <DataTable
                  columns={[
                    {
                      key: 'field',
                      label: 'Campo',
                      sortable: true,
                      render: (value: any, row: LogConsolidationObjectFieldDistinctRow) => {
                        const field = String(value || '-').trim() || '-';
                        return (
                          <div className="d-flex align-items-center gap-2">
                            <span>{field}</span>
                            {row.isGlobalAuthorization && (
                              <span className="badge text-bg-warning" title="Autorização global">*</span>
                            )}
                          </div>
                        );
                      }
                    },
                    {
                      key: 'distinctValuesCount',
                      label: 'Valores distintos',
                      sortable: true,
                      render: (value: any) => Number(value || 0).toLocaleString('pt-BR')
                    },
                    {
                      key: 'acoes',
                      label: 'Ações',
                      sortable: false,
                      render: (_value: any, row: LogConsolidationObjectFieldDistinctRow) => (
                        <ActionMenu
                          triggerSize="md"
                          actions={[
                            {
                              key: 'global-authorization',
                              label: 'Autorização global',
                              icon: Icons.Gear,
                              disabled: Boolean(row.isGlobalAuthorization),
                              onClick: () => {
                                markFieldAsGlobalAuthorization(row.field).catch(() => undefined);
                              }
                            },
                            {
                              key: 'clear-global-authorization',
                              label: 'Limpar regra',
                              icon: Icons.Trash,
                              variant: 'danger',
                              disabled: !Boolean(row.isGlobalAuthorization),
                              onClick: () => {
                                clearFieldGlobalAuthorization(row.field).catch(() => undefined);
                              }
                            }
                          ]}
                        />
                      )
                    }
                  ]}
                  data={logConsolidationObjectFieldsRows}
                  keyExtractor={(row: LogConsolidationObjectFieldDistinctRow) => `obj-field-${row.field}`}
                  striped
                  hover
                  responsive
                  clientSidePagination={true}
                  pagination={true}
                  currentPage={logConsolidationObjectFieldsPage}
                  pageSize={logConsolidationObjectFieldsPageSize}
                  onPageChange={(page: number) => setLogConsolidationObjectFieldsPage(page)}
                  onPageSizeChange={(nextPageSize: number) => {
                    setLogConsolidationObjectFieldsPageSize(nextPageSize);
                    setLogConsolidationObjectFieldsPage(1);
                  }}
                  totalItems={logConsolidationObjectFieldsRows.length}
                  pageSizeOptions={[10, 20, 50]}
                  emptyMessage="Nenhum campo encontrado para este object."
                />
              )}
            </AppModal>

            <AppModal
              show={showAdjustedStructureModal}
              title={`Estrutura de log ajustada${adjustedStructureTarget ? ` - Consolidação ${adjustedStructureTarget._id.slice(-6)}` : ''}`}
              onClose={closeAdjustedStructureModal}
              closeDisabled={adjustedStructureLoading}
              size="xl"
              footer={(
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAdjustedStructureModal}
                  disabled={adjustedStructureLoading}
                >
                  Fechar
                </button>
              )}
            >
              <DataTable
                columns={[
                  {
                    key: 'sourceGroupId',
                    label: 'Grupo origem',
                    sortable: true,
                    render: (value: any) => {
                      const sourceGroupId = String(value || '').trim();
                      if (!sourceGroupId) return '-';
                      return (
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => openAdjustedGroupOriginModal(sourceGroupId)}
                          title="Visualizar origens do agrupamento"
                        >
                          {sourceGroupId}
                        </button>
                      );
                    }
                  },
                  {
                    key: 'user',
                    label: 'USER',
                    sortable: true,
                    render: (value: any) => String(value || '-').trim() || '-'
                  },
                  {
                    key: 'code',
                    label: 'CODE',
                    sortable: true,
                    render: (value: any) => String(value || '-').trim() || '-'
                  },
                  {
                    key: 'object',
                    label: 'OBJECT',
                    sortable: true,
                    render: (value: any) => String(value || '-').trim() || '-'
                  },
                  {
                    key: 'field',
                    label: 'FIELD',
                    sortable: true,
                    render: (value: any) => String(value || '-').trim() || '-'
                  },
                  {
                    key: 'valueFrom',
                    label: 'VALUE FROM',
                    sortable: true,
                    render: (value: any) => String(value || '-').trim() || '-'
                  },
                  {
                    key: 'valueTo',
                    label: 'VALUE TO',
                    sortable: true,
                    render: (value: any) => String(value || '').trim()
                  }
                ]}
                data={adjustedStructureRows}
                keyExtractor={(row: ProfileAdjustedRecordRow) => row._id}
                loading={adjustedStructureLoading}
                filters={adjustedStructureColumnFilters}
                onFilter={(columnKey: string, value: string) => {
                  setAdjustedStructureColumnFilters((prev) => ({ ...prev, [columnKey]: value }));
                }}
                striped
                hover
                responsive
                pagination={true}
                currentPage={adjustedStructurePage}
                pageSize={adjustedStructurePageSize}
                totalItems={adjustedStructureTotal}
                onPageChange={(nextPage) => {
                  if (!adjustedStructureTarget?._id) return;
                  loadAdjustedStructureRows(adjustedStructureTarget._id, nextPage, adjustedStructurePageSize);
                }}
                onPageSizeChange={(nextPageSize) => {
                  if (!adjustedStructureTarget?._id) return;
                  loadAdjustedStructureRows(adjustedStructureTarget._id, 1, nextPageSize);
                }}
                pageSizeOptions={[50, 100, 250, 500]}
                emptyMessage="Nenhum registro estruturado encontrado para esta consolidação."
              />
            </AppModal>

            <AppModal
              show={showActivityModal}
              title={`Log do upload${activityUpload?.fileName ? ` - ${activityUpload.fileName}` : ''}`}
              onClose={closeActivityModal}
              closeDisabled={activityLoading}
              size="xl"
              footer={(
                <button type="button" className="btn btn-secondary" onClick={closeActivityModal} disabled={activityLoading}>
                  Fechar
                </button>
              )}
            >
              {activityLoading ? (
                <div className="text-muted">Carregando log do upload...</div>
              ) : activityItems.length === 0 ? (
                <div className="text-muted">Nenhum evento registrado para este upload.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-striped mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '180px' }}>Data/Hora</th>
                        <th style={{ width: '210px' }}>Usuário</th>
                        <th style={{ width: '110px' }}>Nível</th>
                        <th style={{ width: '180px' }}>Ação</th>
                        <th>Mensagem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityItems.map((item, index) => (
                        <tr key={`${item.action}-${item.createdAt || index}-${index}`}>
                          <td>
                            {item.createdAt
                              ? new Date(item.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' })
                              : '-'}
                          </td>
                          <td>
                            {item.actor?.fullName
                              || item.actor?.username
                              || item.actor?.userId
                              || '-'}
                          </td>
                          <td>{item.level || 'info'}</td>
                          <td>{item.action}</td>
                          <td>
                            <div>{item.message}</div>
                            {item.metadata && Object.keys(item.metadata).length > 0 && (
                              <pre className="small mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(item.metadata, null, 2)}
                              </pre>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </AppModal>

            <AppModal
              show={showAdjustedGroupOriginModal}
              title="Origens do agrupamento"
              onClose={() => {
                if (adjustedGroupOriginLoading) return;
                setShowAdjustedGroupOriginModal(false);
                setAdjustedGroupOriginItem(null);
              }}
              closeDisabled={adjustedGroupOriginLoading}
              size="xl"
              footer={(
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAdjustedGroupOriginModal(false);
                    setAdjustedGroupOriginItem(null);
                  }}
                  disabled={adjustedGroupOriginLoading}
                >
                  Fechar
                </button>
              )}
            >
              {adjustedGroupOriginLoading ? (
                <div className="text-muted">Carregando origens...</div>
              ) : !adjustedGroupOriginItem ? (
                <div className="text-muted">Nenhum detalhe de origem encontrado para este agrupamento.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  <div className="border rounded-3 p-3">
                    <div className="small text-muted mb-1">Grupo</div>
                    <div className="fw-semibold">
                      USER: {String(adjustedGroupOriginItem.user || '-')} | CODE: {String(adjustedGroupOriginItem.code || '-')} | OBJECT: {String(adjustedGroupOriginItem.object || '-')}
                    </div>
                  </div>

                  <div className="border rounded-3 p-3">
                    <div className="small text-muted mb-2">Campos/valores usados no agrupamento</div>
                    {Array.isArray(adjustedGroupOriginItem.groupedFieldValues) && adjustedGroupOriginItem.groupedFieldValues.length > 0 ? (
                      <div className="d-flex flex-column gap-1">
                        {adjustedGroupOriginItem.groupedFieldValues.map((item, index) => {
                          const values = Array.isArray(item?.values) ? item.values.map((v) => String(v || '').trim()).filter(Boolean) : [];
                          return (
                            <div key={`${String(item?.field || 'field')}-${index}`} className="small">
                              <span className="fw-semibold">{String(item?.field || '-')}:</span> {values.length > 0 ? values.join(' | ') : '-'}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="small text-muted">Sem campos/valores registrados.</div>
                    )}
                  </div>

                  <div className="border rounded-3 p-3">
                    <div className="small text-muted mb-2">Linhas de origem consideradas</div>
                    <DataTable
                      columns={[
                        {
                          key: 'sourceRowRef',
                          label: 'Origem',
                          sortable: true,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'sourceRowNumber',
                          label: 'Linha',
                          sortable: true,
                          render: (value: any) => Number(value || 0).toLocaleString()
                        },
                        {
                          key: 'uploadId',
                          label: 'Upload ID',
                          sortable: true,
                          render: (value: any) => String(value || '-').trim() || '-'
                        },
                        {
                          key: 'fieldValues',
                          label: 'Campos e valores da linha',
                          sortable: false,
                          render: (value: any) => {
                            const fieldValues = Array.isArray(value)
                              ? value as Array<{ field?: string; values?: string[] }>
                              : [];
                            if (fieldValues.length === 0) return '-';
                            return (
                              <div className="small d-flex flex-column gap-1">
                                {fieldValues.map((item, index) => {
                                  const field = String(item?.field || '').trim() || '-';
                                  const values = Array.isArray(item?.values)
                                    ? item.values.map((entry) => String(entry || '').trim()).filter(Boolean)
                                    : [];
                                  return (
                                    <div key={`${field}-${index}`}>
                                      <span className="fw-semibold">{field}:</span> {values.length > 0 ? values.join(' | ') : '-'}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                        }
                      ]}
                      data={Array.isArray(adjustedGroupOriginItem.sourceRows) ? adjustedGroupOriginItem.sourceRows : []}
                      keyExtractor={(row: any, index: number) => String(row?.sourceRowRef || `${index}`)}
                      loading={false}
                      striped
                      hover
                      responsive
                      pagination={false}
                      emptyMessage="Nenhuma linha de origem registrada."
                    />
                  </div>
                </div>
              )}
            </AppModal>

            <AppModal
              show={showConsolidationActivityModal}
              title={(
                `Log da consolidação${consolidationActivityTarget?.createdAt
                  ? ` - ${new Date(consolidationActivityTarget.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`
                  : ''}`
              )}
              onClose={closeConsolidationActivityModal}
              closeDisabled={consolidationActivityLoading}
              size="xl"
              footer={(
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeConsolidationActivityModal}
                  disabled={consolidationActivityLoading}
                >
                  Fechar
                </button>
              )}
            >
              {consolidationActivityLoading ? (
                <div className="text-muted">Carregando log da consolidação...</div>
              ) : consolidationActivityItems.length === 0 ? (
                <div className="text-muted">Nenhum evento registrado para esta consolidação.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-striped mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '180px' }}>Data/Hora</th>
                        <th style={{ width: '210px' }}>Usuário</th>
                        <th style={{ width: '110px' }}>Nível</th>
                        <th style={{ width: '180px' }}>Ação</th>
                        <th>Mensagem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consolidationActivityItems.map((item, index) => (
                        <tr key={`${item.action}-${item.createdAt || index}-${index}`}>
                          <td>
                            {item.createdAt
                              ? new Date(item.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' })
                              : '-'}
                          </td>
                          <td>
                            {item.actor?.fullName
                              || item.actor?.username
                              || item.actor?.userId
                              || '-'}
                          </td>
                          <td>{item.level || 'info'}</td>
                          <td>{item.action}</td>
                          <td>
                            <div>{item.message}</div>
                            {item.metadata && Object.keys(item.metadata).length > 0 && (
                              <pre className="small mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(item.metadata, null, 2)}
                              </pre>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </AppModal>
          </>
        )}

        <AppModal
          show={showDownloadModal}
          title="Download de arquivos de carga"
          onClose={() => { if (!downloadInProgress) setShowDownloadModal(false); }}
          closeDisabled={downloadInProgress}
          size="lg"
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDownloadModal(false)}
                disabled={downloadInProgress}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={executeDownloadFiles}
                disabled={downloadInProgress || downloadModalLoading}
              >
                {downloadInProgress ? 'Baixando...' : 'Realizar download'}
              </button>
            </>
          }
        >
          {downloadModalLoading ? (
            <div className="d-flex align-items-center gap-2 py-3">
              <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
              <span>Carregando informações dos arquivos...</span>
            </div>
          ) : (
            <div>
              <DataTable
                columns={[
                  {
                    key: 'fileType',
                    label: 'Arquivo',
                    sortable: false,
                    render: (_value: any, row: any) => (
                      <strong>{String(row.label || '').trim() || '-'}</strong>
                    )
                  },
                  {
                    key: 'rowCount',
                    label: 'Total de linhas',
                    sortable: false,
                    render: (value: any) => {
                      const numericValue = typeof value === 'number' ? value : Number(value || 0);
                      return Number.isFinite(numericValue)
                        ? numericValue.toLocaleString('pt-BR')
                        : '-';
                    }
                  },
                  {
                    key: 'partitionEnabled',
                    label: 'Particionar',
                    sortable: false,
                    render: (_value: any, row: any) => {
                      const isAuth = row.fileType === 'auth';
                      const checked = isAuth ? downloadAuthPartition : downloadOrgPartition;
                      const inputId = isAuth ? 'partition-auth-toggle' : 'partition-org-toggle';
                      const setChecked = isAuth ? setDownloadAuthPartition : setDownloadOrgPartition;
                      return (
                        <div className="form-check form-switch mb-0">
                          <input
                            id={inputId}
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                            disabled={downloadInProgress}
                          />
                          <label className="form-check-label" htmlFor={inputId}>
                            {checked ? 'Ativado' : 'Desativado'}
                          </label>
                        </div>
                      );
                    }
                  },
                  {
                    key: 'partCount',
                    label: 'Número de partes',
                    sortable: false,
                    render: (_value: any, row: any) => {
                      const isAuth = row.fileType === 'auth';
                      const partitionEnabled = isAuth ? downloadAuthPartition : downloadOrgPartition;
                      const partCount = isAuth ? downloadAuthParts : downloadOrgParts;
                      const inputId = isAuth ? 'auth-parts-input' : 'org-parts-input';
                      const setPartCount = isAuth ? setDownloadAuthParts : setDownloadOrgParts;

                      if (!partitionEnabled) {
                        return <span className="text-muted">Arquivo único</span>;
                      }

                      return (
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <input
                            id={inputId}
                            type="number"
                            className="form-control"
                            style={{ width: '90px' }}
                            min={2}
                            max={100}
                            value={partCount}
                            onChange={(e) => setPartCount(Math.max(2, Math.min(100, Number(e.target.value) || 2)))}
                            disabled={downloadInProgress}
                          />
                          <span className="text-muted small">sufixo _parte1, _parte2...</span>
                        </div>
                      );
                    }
                  }
                ]}
                data={[
                  {
                    fileType: 'auth',
                    label: 'Arquivo de autorizações',
                    rowCount: downloadModalAuthCount ?? 0,
                  },
                  {
                    fileType: 'orglevel',
                    label: 'Arquivo de níveis organizacionais',
                    rowCount: downloadModalOrgLevelCount ?? 0,
                  }
                ]}
                keyExtractor={(row: any) => String(row.fileType || '')}
                bordered
                responsive
                hover
                striped
                showColumnMenu={false}
              />

              <div className="alert alert-info small mt-3 mb-0">
                <strong>Nota:</strong> Ao particionar, cada arquivo manterá os grupos de perfil
                {' '}× objeto (autorizações) ou perfil × campo (níveis organizacionais) sem divisão
                {' '}no meio de um grupo, garantindo compatibilidade com o acelerador de carga SAP.
              </div>
            </div>
          )}
        </AppModal>

        <AlertConfirmModal
          show={dialogState.show}
          mode={dialogState.mode}
          title={dialogState.title}
          message={dialogState.message}
          extraContent={dialogState.showPersistGroupOriginsToggle ? (
            <>
              {dialogState.showPersistGroupOriginsToggle ? (
                <div className="form-check mb-0">
                  <input
                    id={dialogState.persistGroupOriginsToggleId || 'persist-group-origins'}
                    className="form-check-input"
                    type="checkbox"
                    checked={persistGroupOriginsChoice}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setPersistGroupOriginsChoice(checked);
                      persistGroupOriginsChoiceRef.current = checked;
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={dialogState.persistGroupOriginsToggleId || 'persist-group-origins'}
                  >
                    Persistir origens de agrupamento (aumenta tempo e uso de armazenamento)
                  </label>
                </div>
              ) : null}
            </>
          ) : undefined}
          confirmLabel={dialogState.confirmLabel}
          cancelLabel={dialogState.cancelLabel}
          confirmVariant={dialogState.confirmVariant}
          busy={dialogBusy}
          onClose={closeDialog}
          onConfirm={handleDialogConfirm}
        />
      </div>
    </>
  );
};

export default AnalisadorLogsSAP;
